<template>
    <div class="container-fluid">
        <div class="row">
            <div class="col-lg-12">
                <!-- 제목 · 탭 · 수집을 한 줄에 (조회기간은 일별 탭 안으로 옮겨서 자리가 났다) -->
                <div class="card mb-3">
                    <div class="card-body py-2 d-flex align-items-center flex-wrap">
                        <h4 class="mb-0 mr-4">판매장부</h4>

                        <ul class="nav nav-tabs sales-ledger-tabs" v-if="canSeeProfit">
                            <li class="nav-item" v-for="t in ledgerTabs" :key="t.value">
                                <a
                                    class="nav-link"
                                    :class="{ active: activeTab === t.value, disabled: showspinner }"
                                    href="javascript:void(0)"
                                    @click="showspinner || (activeTab = t.value)"
                                >{{ t.text }}</a>
                            </li>
                        </ul>

                        <div class="flex-grow-1"></div>

                        <!-- 수집은 어느 탭에서든 필요하므로 공통. 선택기간 재수집은 조회기간과 한 세트라 일별 탭에 있다 -->
                        <b-button
                            :variant="settlementCollectionStatus.isCollected ? 'success' : 'danger'"
                            @click="collectSettlementData(false)"
                            :disabled="showspinner"
                            size="sm"
                            class="mr-2"
                            :title="settlementCollectionStatus.tooltip"
                        >
                            <i class="fas fa-download mr-1"></i>
                            {{ settlementCollectionStatus.buttonText }}
                        </b-button>

                        <!-- 원가·이익 잠금 -->
                        <b-button
                            v-if="canSeeProfit"
                            size="sm"
                            variant="outline-secondary"
                            :title="ledgerLocked ? '잠금 비밀번호를 바꾸거나 해제합니다' : '직원에게 원가·이익이 보이지 않게 잠급니다'"
                            @click="showLockSetModal = true"
                        >
                            <i class="fas mr-1" :class="ledgerLocked ? 'fa-lock' : 'fa-unlock'"></i>
                            {{ ledgerLocked ? '잠금 설정' : '잠그기' }}
                        </b-button>
                        <b-button v-else size="sm" variant="outline-primary" @click="showLockModal = true">
                            <i class="fas fa-user-shield mr-1"></i>관리자 확인
                        </b-button>
                    </div>
                </div>
            </div>
            
            <div class="col-lg-12" v-if="showspinner">
                <div class="d-flex justify-content-center">
                    <div>
                        <img style="max-width:10%;" :src="require('../../../assets/images/small/loading.gif')">
                        {{ loadingtext }} (화면을 이탈하거나 취소시 데이터가 정상적으로 수집되지 않을 수 있습니다.)
                    </div>
                </div>
            </div>

                <!-- 비용관리 모달 (요약장부의 '비용 입력' 버튼) -->
            <b-modal
                v-model="showCostManager"
                title="비용관리"
                size="xl"
                hide-footer
                scrollable
            >
                <LedgerCostManager
                    :fixed-costs="fixedCosts"
                    :product-costs="productCosts"
                    :products="costProductOptions"
                    :saving="savingFixedCost"
                    :initial-date="costInitialDate"
                    @save="saveFixedCosts"
                    @add-cost="addProductCost"
                    @remove-cost="removeProductCost"
                />
            </b-modal>

            <!-- 관리자 확인 (직원도 보는 화면이라 무엇이 가려졌는지는 말하지 않는다) -->
            <b-modal
                v-model="showLockModal"
                title="관리자 확인"
                size="sm"
                centered
                hide-footer
                @hidden="lockInput = ''; lockError = ''"
            >
                <b-form-input
                    v-model="lockInput"
                    type="password"
                    placeholder="비밀번호"
                    autocomplete="off"
                    @keyup.enter="submitUnlock"
                />
                <div class="lock-modal__error" v-if="lockError">{{ lockError }}</div>
                <div class="text-right mt-3">
                    <b-button size="sm" variant="secondary" class="mr-2" @click="showLockModal = false">닫기</b-button>
                    <b-button size="sm" variant="primary" @click="submitUnlock">확인</b-button>
                </div>
            </b-modal>

            <!-- 잠금 설정 / 변경 / 해제 -->
            <b-modal
                v-model="showLockSetModal"
                :title="ledgerLocked ? '잠금 설정 변경' : '원가·이익 잠그기'"
                size="sm"
                centered
                hide-footer
            >
                <b-form-input
                    v-if="ledgerLocked"
                    v-model="lockCurrentPw"
                    type="password"
                    class="mb-2"
                    placeholder="현재 비밀번호"
                    autocomplete="off"
                />
                <b-form-input
                    v-model="lockNewPw"
                    type="password"
                    :placeholder="ledgerLocked ? '새 비밀번호 (4자 이상)' : '비밀번호 (4자 이상)'"
                    autocomplete="off"
                    @keyup.enter="submitLockSetting(false)"
                />
                <div class="lock-modal__error" v-if="lockError">{{ lockError }}</div>
                <div class="d-flex justify-content-between mt-3">
                    <b-button
                        v-if="ledgerLocked"
                        size="sm"
                        variant="outline-danger"
                        @click="submitLockSetting(true)"
                    >
                        잠금 풀기
                    </b-button>
                    <span v-else></span>
                    <div>
                        <b-button size="sm" variant="secondary" class="mr-2" @click="showLockSetModal = false">취소</b-button>
                        <b-button size="sm" variant="primary" @click="submitLockSetting(false)">
                            {{ ledgerLocked ? '변경' : '잠그기' }}
                        </b-button>
                    </div>
                </div>
            </b-modal>

            <!-- 매입가가 없어 손익에서 빠진 상품 안내 -->
            <div class="col-lg-12" v-if="!showspinner && missingPriceCount > 0">
                <div class="missing-price-alert d-flex align-items-center">
                    <i class="fas fa-exclamation-triangle mr-3"></i>
                    <div>
                        <div class="missing-price-alert__title">
                            매입가가 없는 상품 {{ missingPriceCount }}개
                            <template v-if="canSeeProfit">
                                — 이익 {{ Math.round(missingPriceAmount).toLocaleString() }}원이 장부에 안 잡히고 있어요
                            </template>
                            <small v-if="missingOptionCount" class="font-weight-normal">
                                (옵션 {{ missingOptionCount }}개 포함)
                            </small>
                        </div>
                        <div class="missing-price-alert__desc">
                            얼마에 사왔는지를 모르면 장부가 채워지지 않아요.
                            매입가를 넣어주세요.
                        </div>
                    </div>
                    <div class="flex-grow-1"></div>
                    <b-button size="sm" variant="warning" class="flex-shrink-0" @click="goFillPurchasePrice">
                        매입가 입력하러 가기 <i class="fas fa-arrow-right ml-1"></i>
                    </b-button>
                </div>
            </div>

            <!-- 요약장부 — 손익 · 비용 · 추이 · 자산을 한 화면에 -->
            <div class="col-lg-12" v-if="activeTab === 'summary' && !showspinner && canSeeProfit">
                <!-- 1. 손익 흐름 (연 단위 서버 집계 — 조회기간과 무관) -->
                <div class="card mb-4">
                    <PnlPivotTable
                        :monthly-summary="monthlySummary"
                        :fixed-costs="fixedCosts"
                        :product-costs="productCosts"
                        :year="ledgerYear"
                        :years="ledgerYears"
                        :loading="loadingSummary"
                        @year-change="changeLedgerYear"
                        @period-click="onPivotPeriodClick"
                        @open-costs="openCostsFor('')"
                    />
                </div>

                <!-- 2. 월별 추이 -->
                <div class="card mb-4">
                    <MonthlyTrendChart
                        :monthly-summary="monthlySummary"
                        :fixed-costs="fixedCosts"
                        @month-click="onMonthClick"
                    />
                </div>

                <!-- 3. 자본이 어디에 묶여 있나 (입고 기록이 있어야 재고를 계산할 수 있다) -->
                <div class="card mb-4" v-if="inboundHistory.length">
                    <AssetTrendChart
                        :daily-data="dailySummary"
                        :fixed-costs="fixedCosts"
                        :product-costs="productCosts"
                        :setting="ledgerSetting"
                        :coverage="inventoryCoverage"
                        :purchase-lots="purchaseLots"
                        :inbound="inboundHistory"
                        :schedules="settlementSchedules"
                        :recon="reconSummary"
                        @save-setting="saveLedgerSetting"
                        @open-recon="openReconInScm"
                    />
                </div>
            </div>

            <!-- 일별·상품별 조회 조건 (조회기간·조회·재수집은 한 세트라 여기 둔다) -->
            <div class="col-lg-12" v-if="!showspinner && (!canSeeProfit || activeTab === 'ledger')">
                <div class="card mb-3">
                    <div class="card-body py-2 d-flex align-items-center flex-wrap">
                        <b-button-group size="sm" class="mr-3">
                            <b-button
                                v-for="d in quickRanges"
                                :key="d.days"
                                variant="outline-secondary"
                                @click="selectRecentDays(d.days)"
                            >{{ d.text }}</b-button>
                        </b-button-group>
                        <b-dropdown text="월별조회" variant="outline-secondary" size="sm" class="mr-3">
                            <b-dropdown-item
                                v-for="m in monthOptions"
                                :key="m.value"
                                @click="selectMonth(m.year, m.month)"
                            >{{ m.label }}</b-dropdown-item>
                        </b-dropdown>
                        <b-form-input v-model="startDate" type="date" size="sm" style="width: 140px;" />
                        <span class="mx-2">~</span>
                        <b-form-input v-model="endDate" type="date" size="sm" style="width: 140px;" />
                        <b-button v-if="canSeeProfit" variant="primary" size="sm" class="ml-2" :disabled="showspinner" @click="search">
                            <i class="fas fa-search mr-1"></i>조회
                        </b-button>
                        <div class="flex-grow-1"></div>
                        <b-button
                            variant="warning"
                            size="sm"
                            :disabled="showspinner"
                            title="위 조회 기간의 기존 데이터를 덮어쓰고 다시 수집합니다 (오류 데이터 보정용)"
                            @click="collectSettlementData(true)"
                        >
                            <i class="fas fa-sync-alt mr-1"></i>선택 기간 재수집
                        </b-button>
                    </div>
                </div>
            </div>

            <!-- 상품DB에 없어 집계에서 빠진 판매 알림.
                 조용히 빠지면 쿠팡 판매분석 숫자와 안 맞는 원인이 되므로 이익 권한과 무관하게 항상 보여준다 -->
            <div class="col-lg-12" v-if="showlist && activeTab === 'ledger' && excludedSales.rowCount > 0">
                <div class="alert alert-warning py-2 px-3 mb-3 d-flex align-items-center">
                    <i class="fas fa-exclamation-triangle mr-2"></i>
                    <div>
                        이 기간에 <strong>상품DB에 없는 옵션 {{ excludedSales.optionCount }}개</strong>의 판매
                        <strong>{{ Math.round(excludedSales.amount).toLocaleString() }}원</strong>이 집계에서 빠졌습니다.
                        <small class="d-block mt-1">
                            쿠팡 판매분석과 매출이 다르게 보이는 원인입니다.
                            상품DB를 다시 수집하면 채워집니다. 판매를 중지했거나 삭제한 옵션은 다시 수집해도 채워지지 않습니다.
                        </small>
                    </div>
                    <div class="flex-grow-1"></div>
                    <b-button size="sm" variant="warning" class="flex-shrink-0 ml-2" @click="goCollectGrowthDB">
                        상품DB 수집하러 가기
                    </b-button>
                </div>
            </div>

            <div class="col-lg-12" v-if="showlist && activeTab === 'ledger' && canSeeProfit">
                <!-- 간단한 요약 정보 -->
                <div class="card mb-4">
                    <!-- 어느 기간의 합계인지 명시. 요약장부는 달력 월 기준이라 이 값과 다를 수 있다 -->
                    <div class="card-header py-2 d-flex flex-wrap align-items-center">
                        <strong class="mr-2">{{ startDate }} ~ {{ endDate }}</strong>
                        <small class="text-muted">합계 ({{ periodDayCount }}일)</small>
                        <div class="flex-grow-1"></div>
                        <small class="text-muted">
                            요약장부는 달력 기준(1일~말일)이라 조회 기간이 다르면 숫자도 다릅니다
                        </small>
                    </div>
                    <div class="card-body py-3">
                        <div class="row text-center">
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">총매출액</span>
                                    <strong class="text-primary" style="font-size: 20px;">{{ Math.round(summary.totalRevenue).toLocaleString() }}원</strong>
                                </div>
                            </div>
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">순매출액<small class="d-block">(쿠폰제외)</small></span>
                                    <strong class="text-primary" style="font-size: 20px;">{{ Math.round(summary.totalNetSales).toLocaleString() }}원</strong>
                                </div>
                            </div>
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">총이익(GP)</span>
                                    <strong :class="summary.totalProfit >= 0 ? 'text-success' : 'text-danger'" style="font-size: 20px;">{{ Math.round(summary.totalProfit).toLocaleString() }}원</strong>
                                </div>
                            </div>
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between" title="쿠팡이 잃어버린 재고에 대한 보상금입니다. 총이익(GP)에는 포함되고, 개당이익·개별마진에는 넣지 않습니다">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">재고손실보상<small class="d-block">(총이익 포함)</small></span>
                                    <strong class="text-success" style="font-size: 20px;">{{ Math.round(summary.totalCompensation || 0).toLocaleString() }}원</strong>
                                </div>
                            </div>
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between" title="(기여이익-매입원가)*1/11로 계산된 부가세입니다">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">부가세</span>
                                    <strong class="text-danger" style="font-size: 20px;">{{ Math.round(summary.totalVat).toLocaleString() }}원</strong>
                                </div>
                            </div>
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">총매입액</span>
                                    <strong class="text-danger" style="font-size: 20px;">{{ Math.round(summary.totalPurchase).toLocaleString() }}원</strong>
                                </div>
                            </div>
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">총광고비</span>
                                    <strong class="text-warning" style="font-size: 20px;">{{ Math.round(summary.totalAdCost).toLocaleString() }}원</strong>
                                </div>
                            </div>
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">총마진율</span>
                                    <strong :class="summary.totalMarginRate >= 30 ? 'text-success' : summary.totalMarginRate >= 15 ? 'text-warning' : 'text-danger'" style="font-size: 20px;">{{ Math.round(summary.totalMarginRate) }}%</strong>
                                </div>
                            </div>
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">ROAS</span>
                                    <strong :class="summary.totalROAS >= 300 ? 'text-success' : summary.totalROAS >= 150 ? 'text-warning' : 'text-danger'" style="font-size: 20px;">{{ Math.round(summary.totalROAS) }}%</strong>
                                </div>
                            </div>
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between" title="부가세 제외 매입가 기준 수익률 (마진 ÷ (매입가×10/11))">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">ROI</span>
                                    <strong :class="summary.totalROI >= 30 ? 'text-success' : summary.totalROI >= 10 ? 'text-warning' : 'text-danger'" style="font-size: 20px;">{{ Math.round(summary.totalROI) }}%</strong>
                                </div>
                            </div>
                            <div class="col-6 col-md-4 col-lg px-1 mb-3 mb-lg-0">
                                <div class="d-flex flex-column h-100 justify-content-between" title="현재 전체가용재고 금액 합계 (재고관리 화면 기준 스냅샷)">
                                    <span class="text-muted" style="font-size: 14px; word-break: keep-all;">재고금액</span>
                                    <strong class="text-dark" style="font-size: 20px;">{{ Math.round(summary.totalStockAmount).toLocaleString() }}원</strong>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 차트 영역 — 값은 아래 표가 쓰는 행(dailyRows·productRows)에서만 나온다(판 #725) -->
                <div class="row mb-3" v-if="salesLedgerData.length > 0">
                    <!-- 손익 워터폴(기간 합계) ↔ 일별 추이(시계열) 전환.
                         워터폴은 "얼마나"를, 일별 추이는 "언제부터"를 답한다 -->
                    <div class="col-md-6 mb-3 mb-md-0">
                        <div class="card h-100">
                            <div class="card-header py-2 d-flex justify-content-between align-items-start flex-wrap">
                                <div class="mr-2">
                                    <h6 class="card-title mb-0">{{ chartMode === 'waterfall' ? '손익 워터폴' : '일별 추이' }}</h6>
                                    <small class="text-muted">
                                        <template v-if="chartMode === 'waterfall'">조회기간 합계 · 빨강은 차감, 회색은 소계</template>
                                        <template v-else>순매출과 ROI는 단위가 달라 위아래로 나눠 그린다</template>
                                    </small>
                                </div>
                                <div class="btn-group btn-group-sm diag-sort" role="group">
                                    <button type="button" class="btn"
                                        :class="chartMode === 'waterfall' ? 'btn-primary' : 'btn-outline-secondary'"
                                        @click="chartMode = 'waterfall'">워터폴</button>
                                    <button type="button" class="btn"
                                        :class="chartMode === 'daily' ? 'btn-primary' : 'btn-outline-secondary'"
                                        @click="chartMode = 'daily'">일별 추이</button>
                                </div>
                            </div>
                            <div class="card-body py-2">
                                <ApexChart
                                    v-if="chartMode === 'waterfall'"
                                    ref="salesLedgerChart"
                                    element="sales-ledger-chart"
                                    :chartOption="waterfallOptions"
                                />
                                <!-- 보조축(오른쪽 두 번째 Y축)을 쓰지 않는다. 축이 둘이면 두 선이
                                     만나는 지점이 축 스케일 잡기에 따라 바뀌어 없는 상관관계가 보인다 -->
                                <template v-else>
                                    <ApexChart
                                        element="daily-sales-chart"
                                        :chartOption="dailySalesOptions"
                                    />
                                    <ApexChart
                                        element="daily-roi-chart"
                                        :chartOption="dailyRoiOptions"
                                    />
                                </template>
                            </div>
                        </div>
                    </div>

                    <!-- 상품 진단 TOP 10 — 지금 손봐야 할 상품과 각각 얼마짜리 문제인지 -->
                    <div class="col-md-6">
                        <div class="card h-100 d-flex flex-column">
                            <div class="card-header py-2 d-flex justify-content-between align-items-start flex-wrap">
                                <div class="mr-2">
                                    <h6 class="card-title mb-0">상품 진단 TOP 10</h6>
                                    <small class="text-muted">
                                        전체 {{ formatNumber(diagnostics.summary.total) }}개 중 {{ diagnostics.top.length }}개 ·
                                        이 {{ diagnostics.top.length }}개에 {{ formatMoneyShort(diagnostics.summary.lockedTop) }}원 묶임
                                        (전체 재고의 {{ diagnostics.summary.lockedRatio }}%)
                                        <span v-if="diagnostics.summary.note" class="text-warning">· {{ diagnostics.summary.note }}</span>
                                    </small>
                                </div>
                                <div class="btn-group btn-group-sm diag-sort" role="group">
                                    <button type="button" class="btn"
                                        :class="diagSort === 'stock' ? 'btn-primary' : 'btn-outline-secondary'"
                                        @click="diagSort = 'stock'">묶인 돈</button>
                                    <button type="button" class="btn"
                                        :class="diagSort === 'sales' ? 'btn-primary' : 'btn-outline-secondary'"
                                        @click="diagSort = 'sales'">매출</button>
                                    <button type="button" class="btn"
                                        :class="diagSort === 'loss' ? 'btn-primary' : 'btn-outline-secondary'"
                                        @click="diagSort = 'loss'">손실</button>
                                    <button type="button" class="btn"
                                        :class="diagSort === 'roi' ? 'btn-primary' : 'btn-outline-secondary'"
                                        @click="diagSort = 'roi'">ROI</button>
                                </div>
                            </div>
                            <!-- 카드 높이는 옆 칸(워터폴)이 정하고 리스트가 헤더를 뺀 나머지를 채운다 -->
                            <div class="card-body py-2 diag-list flex-grow-1">
                                <div class="diag-row diag-row--head">
                                    <div></div>
                                    <div>상품</div>
                                    <div>기간 매출</div>
                                    <div>묶인 재고</div>
                                    <div class="text-right">ROI · 수익</div>
                                </div>
                                <div v-for="(p, i) in diagnostics.top" :key="p.vendorItemId" class="diag-row" :title="p.name">
                                    <div class="diag-row__rank">{{ i + 1 }}</div>
                                    <div class="diag-row__name">{{ p.name }}</div>
                                    <div class="diag-row__metric">
                                        <div class="diag-row__bar"><i :style="{ width: p.salesPct + '%', background: '#2a78d6' }"></i></div>
                                        <span class="diag-row__val">{{ formatMoneyShort(p.saleAmount) }}원</span>
                                    </div>
                                    <div class="diag-row__metric">
                                        <div class="diag-row__bar"><i :style="{ width: p.stockPct + '%', background: '#eb6834' }"></i></div>
                                        <span class="diag-row__val">{{ formatMoneyShort(p.stockAmount) }} · {{ p.stockDays }}일</span>
                                    </div>
                                    <!-- 색은 그 상품의 ROI만 따라간다. 순위를 따라가면 정렬을 바꿀 때
                                         색이 재배치돼서 같은 상품을 눈으로 쫓을 수 없다 -->
                                    <div class="diag-row__roi"
                                        :class="p.roi >= 30 ? 'is-good' : p.roi >= 10 ? 'is-mid' : 'is-bad'">
                                        {{ p.roi.toFixed(1) }}%
                                        <!-- 1만 미만이면 formatMoneyShort가 접미사를 안 붙여 '4,121'이 돼서 단위를 알 수 없다 -->
                                        <em>{{ p.grossProfit >= 0 ? '+' : '−' }}{{ formatMoneyShort(Math.abs(p.grossProfit)) }}원</em>
                                    </div>
                                </div>
                                <div v-if="!diagnostics.top.length" class="text-center text-muted py-5">
                                    매입가가 등록된 상품이 없어 진단할 수 없습니다.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>

                <!-- 테이블 헤더 with Switch -->
                <div class="card mb-3">
                    <!-- 좁은 화면(390)에선 단추 줄을 아래로 내린다 — 한 줄에 몰면 글자가 한 자씩 꺾였다(판 #452 화면 판) -->
                    <div class="card-header d-flex flex-wrap justify-content-between align-items-center py-2">
                        <h6 class="card-title mb-0 text-nowrap mr-2">{{ isProductView ? '상품별' : '일별' }} 판매장부</h6>
                        <div class="d-flex flex-wrap align-items-center">
                            <!-- 엑셀 다운로드 버튼 -->
                            <b-button
                                variant="success"
                                size="sm"
                                @click="isProductView ? downloadProductTableExcel() : downloadDailyTableExcel()"
                                class="mr-2 my-1 text-nowrap"
                                :disabled="showspinner"
                            >
                                <i class="fas fa-file-excel mr-1"></i>
                                엑셀 다운로드
                            </b-button>
                            <!-- 전체 데이터(날짜별·상품별) 다운로드 버튼 -->
                            <b-button
                                variant="primary"
                                size="sm"
                                @click="downloadFullTableExcel()"
                                class="mr-3 my-1 text-nowrap"
                                :disabled="showspinner"
                            >
                                <i class="fas fa-file-excel mr-1"></i>
                                전체 데이터
                            </b-button>
                            <!-- 비용 입력(요약장부와 같은 비용관리 · 조회 기간 끝날의 달로 연다 · 판 #452) -->
                            <b-button
                                variant="outline-secondary"
                                size="sm"
                                @click="openCostsFor(endDate)"
                                class="mr-3 my-1 text-nowrap"
                            >
                                <i class="fas fa-receipt mr-1"></i>
                                비용 입력
                            </b-button>
                            <small class="mr-2 text-nowrap">상품별 보기</small>
                            <b-form-checkbox
                                v-model="isProductView"
                                switch
                                size="sm"
                                @change="toggleView"
                            ></b-form-checkbox>
                        </div>
                    </div>
                    <!-- 리스크 필터 — 누르면 상품별 보기에서 해당 상품만 남긴다. 다시 누르면 해제 -->
                    <div v-if="productViewData.length" class="card-body py-2">
                        <span
                            v-for="btn in ledgerRiskButtons"
                            :key="btn.key"
                            :title="btn.half && !ledgerRiskHalfReady ? '조회 기간이 14일 이상일 때 앞뒤 절반을 비교할 수 있습니다' : btn.tip"
                            class="d-inline-block"
                        >
                            <b-button
                                :variant="ledgerRiskFilter === btn.key ? btn.color : 'outline-' + btn.color"
                                size="sm"
                                class="mb-1 mr-1"
                                :disabled="btn.half && !ledgerRiskHalfReady"
                                @click="toggleLedgerRisk(btn.key)"
                            >
                                {{ btn.label }}<b-badge :variant="ledgerRiskFilter === btn.key ? 'light' : btn.color" class="ml-1">{{ ledgerRiskCounts[btn.key] || 0 }}</b-badge>
                            </b-button>
                        </span>
                    </div>
                </div>

                <!-- 일별 데이터 테이블 -->
                <CDataTable
                    storage-key="growthSalesLedger.1"
                    v-if="!isProductView"
                    key="daily-table"
                    id="daily"
                    :headers="dailyHeaders"
                    :items="dailyRows"
                    v-model="tableSelectedItem"
                    :show-select="false"
                    itemKey="date"
                    :items-per-page="31"
                    :pagination="true"
                    :expandable="false"
                    :fixedColumns="'date'"
                    :headerTooltips="headerTooltips"
                    style="cursor: pointer;"
                >
                    <!-- 일별 테이블 슬롯들 -->
                    <template v-slot:date="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            {{ item.date }}
                        </div>
                    </template>
                    <template v-slot:일별판매량="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            {{ formatNumber(item.일별판매량) }}개
                        </div>
                    </template>
                    <template v-slot:일별총매출액="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-revenue">
                            <span class="font-weight-bold text-primary">
                                {{ formatNumber(item.일별총매출액) }}원
                            </span>
                        </div>
                    </template>
                    <template v-slot:일별쿠팡지원할인="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별쿠팡지원할인) }}원
                        </div>
                    </template>
                    <template v-slot:일별즉시할인쿠폰="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별즉시할인쿠폰) }}원
                        </div>
                    </template>
                    <template v-slot:일별다운로드쿠폰="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별다운로드쿠폰) }}원
                        </div>
                    </template>
                    <template v-slot:일별순매출액="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-revenue">
                            <span class="font-weight-bold text-info">
                                {{ formatNumber(item.일별순매출액) }}원
                            </span>
                        </div>
                    </template>
                    <template v-slot:일별수수료="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별수수료) }}원
                        </div>
                    </template>
                    <template v-slot:일별배송비="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별배송비) }}원
                        </div>
                    </template>
                    <template v-slot:일별입출고비="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별입출고비) }}원
                        </div>
                    </template>
                    <template v-slot:일별보관비="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별보관비) }}원
                        </div>
                    </template>
                    <template v-slot:일별반품회수재입고비="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별반품회수재입고비) }}원
                        </div>
                    </template>
                    <template v-slot:일별반출비="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별반출비) }}원
                        </div>
                    </template>
                    <template v-slot:일별반출배송비="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별반출배송비) }}원
                        </div>
                    </template>
                    <template v-slot:일별바코드비="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별바코드비) }}원
                        </div>
                    </template>
                    <template v-slot:일별재고손실보상="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-revenue">
                            <span class="text-success">{{ formatNumber(item.일별재고손실보상) }}원</span>
                            <a v-if="canSeeRecon" href="javascript:void(0)" class="ml-1 small" title="공급관리에서 확인 안 된 재고 보기" @click.stop="openReconInScm">확인 안 된 재고 ›</a>
                        </div>
                    </template>
                    <template v-slot:일별밀크런비용="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별밀크런비용) }}원
                        </div>
                    </template>
                    <template v-slot:일별광고비="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별광고비) }}원
                        </div>
                    </template>
                    <template v-slot:일별외부마케팅비="{item}">
                        <div
                            class="bg-cost"
                            :title="costTooltipOf(costsOfDate(item.date))"
                            style="cursor: help;"
                        >{{ formatNumber(costSumOf(costsOfDate(item.date))) }}원</div>
                    </template>
                    <template v-slot:일별판매비용합계="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별판매비용합계) }}원
                        </div>
                    </template>
                    <template v-slot:일별기여이익="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            <span :class="item.일별기여이익 >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(item.일별기여이익) }}원
                            </span>
                        </div>
                    </template>
                    <template v-slot:일별매입원가="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별매입원가) }}원
                        </div>
                    </template>
                    <template v-slot:일별부가세="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.일별부가세) }}원
                        </div>
                    </template>
                    <template v-slot:일별총수익="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            <span :class="item.일별총수익 >= 0 ? 'font-weight-bold text-success' : 'font-weight-bold text-danger'">
                                {{ formatNumber(item.일별총수익) }}원
                            </span>
                            <!-- 날짜 칸은 슬롯이 안 켜져 있어(isSlot 없음) 총이익 칸에 단다(#112) -->
                            <b-badge v-if="item.비용미확정 > 0" variant="warning" class="ml-1" :title="item.비용미확정 + '개 상품이 배송 완료 전이라 배송비·입출고비가 아직 다 안 잡혔습니다'">비용 미확정</b-badge>
                        </div>
                    </template>
                    <template v-slot:일별자연판매량="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            {{ formatNumber(item.일별자연판매량) }}개
                        </div>
                    </template>
                    <template v-slot:일별광고판매량="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            {{ formatNumber(item.일별광고판매량) }}개
                        </div>
                    </template>
                    <template v-slot:일별반품수량="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            <span class="text-secondary">{{ formatNumber(item.일별반품수량 || 0) }}개</span>
                        </div>
                    </template>
                    <template v-slot:일별광고총수익="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            <span :class="item.일별광고총수익 >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(Math.round(item.일별광고총수익 || 0)) }}원
                            </span>
                        </div>
                    </template>
                    <template v-slot:일별광고순수익="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            <span :class="(item.일별광고순수익 || 0) >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(Math.round(item.일별광고순수익 || 0)) }}원
                            </span>
                        </div>
                    </template>
                    <template v-slot:일별ROAS="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            {{ (item.일별ROAS && item.일별ROAS > 0) ? (item.일별ROAS * 100).toFixed(1) + '%' : '-' }}
                        </div>
                    </template>
                    <template v-slot:일별ROI="{item}">
                        <div @click="showDateDetail(item)" style="cursor: pointer;">
                            <span v-if="item.일별ROI && item.일별ROI !== 0" :class="item.일별ROI >= 30 ? 'text-success font-weight-bold' : item.일별ROI >= 10 ? 'text-warning' : 'text-danger'">
                                {{ item.일별ROI.toFixed(1) }}%
                            </span>
                            <span v-else>-</span>
                        </div>
                    </template>
                    <template v-slot:memo="{item}">
                        <MemoCell :content="getMemo(item)" @save="(c) => saveMemo(item, c)" />
                    </template>
                </CDataTable>

                <!-- 상품별 데이터 테이블 -->
                <CDataTable
                    storage-key="growthSalesLedger.2"
                    v-if="isProductView"
                    key="product-table"
                    id="product"
                    :headers="productHeaders"
                    :items="productTableRows"
                    v-model="tableSelectedItem"
                    :show-select="false"
                    itemKey="vendorItemId"
                    :items-per-page="50"
                    :pagination="true"
                    :expandable="false"
                    :fixedColumns="'상품명'"
                    :headerTooltips="headerTooltips"
                    style="cursor: pointer;"
                >
                    <!-- 상품별 테이블 슬롯들 -->
                    <template v-slot:썸네일="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            <img :src="item.썸네일" class="img-fluid rounded avatar-40" alt="image" />
                        </div>
                    </template>
                    <template v-slot:상품명="{item}">
                        <div @click="showProductDetail(item)" style="max-width: 200px; cursor: pointer;">
                            <div class="text-truncate font-weight-bold" :title="item.판매상품명">
                                {{ item.판매상품명 }}
                            </div>
                            <div class="d-flex align-items-center" style="gap: 4px;">
                                <span class="text-truncate text-muted small" :title="item.옵션명">
                                    {{ item.옵션명 }}
                                </span>
                                <span v-if="item.upBundlingSize > 1" class="bundle-badge">x{{ item.upBundlingSize }}</span>
                            </div>
                        </div>
                    </template>
                    <template v-slot:skuGrade="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            <b-badge :variant="getSkuGradeBadgeVariant(item.skuGrade)">
                                {{ getSkuGradeLabel(item.skuGrade) }}
                            </b-badge>
                        </div>
                    </template>
                    <template v-slot:판매가="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            {{ formatNumber(calcSalePrice(item)) }}원
                        </div>
                    </template>
                    <template v-slot:coupangStock="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            {{ formatNumber(item.coupangStock || 0) }}개
                        </div>
                    </template>
                    <template v-slot:purchasePrice="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            {{ item.purchasePrice ? formatNumber(item.purchasePrice) + '원' : '-' }}
                        </div>
                    </template>
                    <template v-slot:saleQuantity="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            {{ formatNumber(item.saleQuantity) }}개
                        </div>
                    </template>
                    <template v-slot:saleAmount="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-revenue">
                            {{ formatNumber(item.saleAmount) }}원
                        </div>
                    </template>
                    <template v-slot:coupangDiscount="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.coupangDiscount) }}원
                        </div>
                    </template>
                    <template v-slot:instantCoupon="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.instantCoupon) }}원
                        </div>
                    </template>
                    <template v-slot:downloadCoupon="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.downloadCoupon) }}원
                        </div>
                    </template>
                    <template v-slot:netSales="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-revenue">
                            {{ formatNumber(item.netSales) }}원
                        </div>
                    </template>
                    <template v-slot:commissionTotal="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.commissionTotal) }}원
                        </div>
                    </template>
                    <template v-slot:shippingTotal="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.shippingTotal) }}원
                        </div>
                    </template>
                    <template v-slot:warehouseTotal="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.warehouseTotal) }}원
                        </div>
                    </template>
                    <template v-slot:storageFee="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.storageFee) }}원
                        </div>
                    </template>
                    <template v-slot:creturnFee="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.creturnFee) }}원
                        </div>
                    </template>
                    <template v-slot:vreturnFee="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.vreturnFee) }}원
                        </div>
                    </template>
                    <template v-slot:vreturnShippingFee="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.vreturnShippingFee) }}원
                        </div>
                    </template>
                    <template v-slot:barcodeFee="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.barcodeFee) }}원
                        </div>
                    </template>
                    <template v-slot:inventoryCompensation="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-revenue">
                            <span class="text-success">{{ formatNumber(item.inventoryCompensation) }}원</span>
                            <a v-if="canSeeRecon" href="javascript:void(0)" class="ml-1 small" title="공급관리에서 확인 안 된 재고 보기" @click.stop="openReconInScm">확인 안 된 재고 ›</a>
                        </div>
                    </template>
                    <template v-slot:milkrunCostTotal="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.milkrunCostTotal) }}원
                        </div>
                    </template>
                    <template v-slot:adTotal="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.adTotal) }}원
                        </div>
                    </template>
                    <template v-slot:externalMarketing="{item}">
                        <div
                            class="bg-cost"
                            :title="costTooltipOf(costsOfProduct(item.vendorItemId))"
                            style="cursor: help;"
                        >{{ formatNumber(costSumOf(costsOfProduct(item.vendorItemId))) }}원</div>
                    </template>
                    <template v-slot:sellingCosts="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ formatNumber(item.sellingCosts) }}원
                        </div>
                    </template>
                    <template v-slot:contribution="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            <span :class="item.contribution >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(item.contribution) }}원
                            </span>
                        </div>
                    </template>
                    <template v-slot:cogs="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ item.cogs !== null ? formatNumber(item.cogs) + '원' : '-' }}
                        </div>
                    </template>
                    <template v-slot:vat="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;" class="bg-cost">
                            {{ item.vat !== null ? formatNumber(item.vat) + '원' : '-' }}
                        </div>
                    </template>
                    <template v-slot:grossProfit="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            <span v-if="item.grossProfit !== null" :class="item.grossProfit >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(Math.round(item.grossProfit)) }}원
                            </span>
                            <span v-else>-</span>
                        </div>
                    </template>
                    <template v-slot:profitPerUnit="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            {{ item.profitPerUnit !== null ? formatNumber(Math.round(item.profitPerUnit)) + '원' : '-' }}
                            <b-badge v-if="item.costPending || item.costPendingDays" variant="warning" class="ml-1" title="배송 완료 전이라 배송비·입출고비가 아직 다 안 잡혔습니다. 며칠 뒤 다시 수집하면 확정됩니다">비용 미확정</b-badge>
                        </div>
                    </template>
                    <template v-slot:grossMargin="{item}">
                        <span v-if="item.grossMargin !== null && item.grossMargin !== undefined" :class="item.grossMargin >= 30 ? 'text-success font-weight-bold' : item.grossMargin >= 10 ? 'text-warning' : 'text-danger'">
                            {{ item.grossMargin.toFixed(1) }}%
                        </span>
                        <span v-else>-</span>
                    </template>
                    <template v-slot:naturalSales="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            {{ formatNumber(item.naturalSales || 0) }}개
                        </div>
                    </template>
                    <template v-slot:saleQuantityFromAd="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            {{ formatNumber(item.saleQuantityFromAd || 0) }}개
                        </div>
                    </template>
                    <template v-slot:returnQuantity="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            <span class="text-secondary">{{ formatNumber(item.returnQuantity || 0) }}개</span>
                        </div>
                    </template>
                    <template v-slot:adTotalProfit="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            <span :class="item.adTotalProfit >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(Math.round(item.adTotalProfit || 0)) }}원
                            </span>
                        </div>
                    </template>
                    <template v-slot:adNetProfit="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            <span :class="(item.adNetProfit || 0) >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(Math.round(item.adNetProfit || 0)) }}원
                            </span>
                        </div>
                    </template>
                    <template v-slot:roas="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            {{ (item.roas !== null && item.roas !== undefined) ? (item.roas * 100).toFixed(1) + '%' : '-' }}
                        </div>
                    </template>
                    <template v-slot:adROI="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            {{ (item.adROI !== null && item.adROI !== undefined) ? (item.adROI * 100).toFixed(1) + '%' : '-' }}
                        </div>
                    </template>
                    <template v-slot:roi="{item}">
                        <div @click="showProductDetail(item)" style="cursor: pointer;">
                            <span v-if="item.roi !== null && item.roi !== undefined" :class="item.roi >= 30 ? 'text-success font-weight-bold' : item.roi >= 10 ? 'text-warning' : 'text-danger'">
                                {{ item.roi.toFixed(1) }}%
                            </span>
                            <span v-else>-</span>
                        </div>
                    </template>
                    <template v-slot:memo="{item}">
                        <MemoCell :content="getMemo(item)" @save="(c) => saveMemo(item, c)" />
                    </template>
                </CDataTable>

                <!-- 상세 정보 모달 -->
                <b-modal
                    v-model="showModal"
                    :title="modalTitle"
                    size="xl"
                    hide-footer
                    scrollable
                    modal-class="sales-ledger-modal"
                    @shown="forceModalStyle"
                >
                    <!-- 모달 상단 차트 영역 -->
                    <div class="mb-3">
                        <!-- 상품별 상세 모달: 파레토 차트 -->
                        <div v-if="!isProductView" class="card">
                            <div class="card-header py-2">
                                <h6 class="card-title mb-0">상품별 파레토 차트</h6>
                            </div>
                            <div class="card-body py-2 modal-chart-container">
                                <ApexChart
                                    v-if="showModalChart"
                                    ref="modalChart"
                                    element="modal-chart"
                                    :chartOption="modalChartOptions"
                                />
                                <div v-else class="text-center py-3">
                                    <div class="spinner-border text-primary" role="status">
                                        <span class="sr-only">차트 로딩 중...</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 일자별 상세 모달: 일자별 지표 -->
                        <div v-else class="card">
                            <div class="card-header py-2 d-flex justify-content-between align-items-center">
                                <h6 class="card-title mb-0" style="cursor: pointer;" @click="modalChartCollapsed = !modalChartCollapsed">
                                    <i class="fas mr-1" :class="modalChartCollapsed ? 'fa-chevron-right' : 'fa-chevron-down'"></i>
                                    일자별 지표
                                </h6>
                                <div class="d-flex align-items-center" v-show="!modalChartCollapsed">
                                    <span class="mr-2 small" :class="dailyChartMode === 'amount' ? 'font-weight-bold' : 'text-muted'">금액</span>
                                    <b-form-checkbox
                                        v-model="dailyChartMode"
                                        switch
                                        true-value="quantity"
                                        false-value="amount"
                                        @change="updateDailyChart"
                                        class="mb-0"
                                    />
                                    <span class="ml-2 small" :class="dailyChartMode === 'quantity' ? 'font-weight-bold' : 'text-muted'">수량</span>
                                </div>
                            </div>
                            <div class="card-body py-2 modal-chart-container" v-show="!modalChartCollapsed">
                                <ApexChart
                                    v-if="showModalChart"
                                    ref="modalChart"
                                    element="modal-chart"
                                    :chartOption="modalChartOptions"
                                />
                                <div v-else class="text-center py-3">
                                    <div class="spinner-border text-primary" role="status">
                                        <span class="sr-only">차트 로딩 중...</span>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- 특정 상품 날짜별 모달: 재고 정보 (그래프 아래·테이블 위, 컴팩트) -->
                    <div v-if="isProductView && modalCoverage" class="d-flex flex-wrap align-items-center mb-2" style="gap: 6px; font-size: 12px;">
                        <span class="border rounded px-2 py-1">
                            매입가 <b-badge variant="dark" class="ml-1">{{ modalCoverage.purchasePrice ? formatNumber(modalCoverage.purchasePrice) + '원' : '-' }}</b-badge>
                        </span>
                        <span class="border rounded px-2 py-1">
                            쿠팡재고 <b-badge variant="primary" class="ml-1">{{ formatNumber(modalCoverage.coupangStock || 0) }}개</b-badge>
                            <span class="text-muted ml-1">{{ formatNumber(modalCoverage.coupangStockDays || 0) }}일 · {{ formatNumber(modalCoverage.coupangStockAmount || 0) }}원</span>
                        </span>
                        <span class="border rounded px-2 py-1">
                            전체가용재고 <b-badge variant="success" class="ml-1">{{ formatNumber(modalCoverage.totalStock || 0) }}개</b-badge>
                            <span class="text-muted ml-1">{{ formatNumber(modalCoverage.totalStockDays || 0) }}일 · {{ formatNumber(modalCoverage.totalStockAmount || 0) }}원</span>
                        </span>
                    </div>

                    <!-- 모달 테이블 엑셀 다운로드 버튼 -->
                    <div class="mb-3 text-right">
                        <b-button
                            variant="success"
                            size="sm"
                            @click="downloadModalTableExcel()"
                            :disabled="showspinner"
                        >
                            <i class="fas fa-file-excel mr-1"></i>
                            엑셀 다운로드
                        </b-button>
                    </div>

                    <CDataTable
                        storage-key="growthSalesLedger.3"
                        :headers="modalHeaders"
                        :items="modalData"
                        :show-select="false"
                        :items-per-page="20"
                        :pagination="true"
                        itemKey="uniqueKey"
                        :fixedColumns="modalFixedColumns"
                        :headerTooltips="headerTooltips"
                        class="table-sm"
                    >
                        <!-- 모달 테이블 슬롯들 -->
                        <template v-slot:썸네일="{item}">
                            <img :src="item.썸네일" class="img-fluid rounded avatar-40" alt="image" />
                        </template>
                        <template v-slot:상품명="{item}">
                            <div style="max-width: 200px;">
                                <div class="text-truncate font-weight-bold" :title="item.판매상품명">
                                    {{ item.판매상품명 }}
                                </div>
                                <div class="d-flex align-items-center" style="gap: 4px;">
                                    <span class="text-truncate text-muted small" :title="item.옵션명">
                                        {{ item.옵션명 }}
                                    </span>
                                    <span v-if="item.upBundlingSize > 1" class="bundle-badge">x{{ item.upBundlingSize }}</span>
                                </div>
                            </div>
                        </template>
                        <template v-slot:skuGrade="{item}">
                            <b-badge :variant="getSkuGradeBadgeVariant(item.skuGrade)">
                                {{ getSkuGradeLabel(item.skuGrade) }}
                            </b-badge>
                        </template>
                        <template v-slot:판매가="{item}">
                            {{ formatNumber(calcSalePrice(item)) }}원
                        </template>
                        <template v-slot:coupangStock="{item}">
                            {{ formatNumber(item.coupangStock || 0) }}개
                        </template>
                        <template v-slot:purchasePrice="{item}">
                            {{ item.purchasePrice ? formatNumber(item.purchasePrice) + '원' : '-' }}
                        </template>
                        <template v-slot:saleQuantity="{item}">
                            {{ formatNumber(item.saleQuantity) }}개
                        </template>
                        <template v-slot:saleAmount="{item}">
                            <div class="bg-revenue">{{ formatNumber(item.saleAmount) }}원</div>
                        </template>
                        <template v-slot:coupangDiscount="{item}">
                            <div class="bg-cost">{{ formatNumber(item.coupangDiscount) }}원</div>
                        </template>
                        <template v-slot:instantCoupon="{item}">
                            <div class="bg-cost">{{ formatNumber(item.instantCoupon) }}원</div>
                        </template>
                        <template v-slot:downloadCoupon="{item}">
                            <div class="bg-cost">{{ formatNumber(item.downloadCoupon) }}원</div>
                        </template>
                        <template v-slot:netSales="{item}">
                            <div class="bg-revenue">{{ formatNumber(item.netSales) }}원</div>
                        </template>
                        <template v-slot:commissionTotal="{item}">
                            <div class="bg-cost">{{ formatNumber(item.commissionTotal) }}원</div>
                        </template>
                        <template v-slot:shippingTotal="{item}">
                            <div class="bg-cost">{{ formatNumber(item.shippingTotal) }}원</div>
                        </template>
                        <template v-slot:warehouseTotal="{item}">
                            <div class="bg-cost">{{ formatNumber(item.warehouseTotal) }}원</div>
                        </template>
                        <template v-slot:storageFee="{item}">
                            <div class="bg-cost">{{ formatNumber(item.storageFee) }}원</div>
                        </template>
                        <template v-slot:creturnFee="{item}">
                            <div class="bg-cost">{{ formatNumber(item.creturnFee) }}원</div>
                        </template>
                        <template v-slot:vreturnFee="{item}">
                            <div class="bg-cost">{{ formatNumber(item.vreturnFee) }}원</div>
                        </template>
                        <template v-slot:vreturnShippingFee="{item}">
                            <div class="bg-cost">{{ formatNumber(item.vreturnShippingFee) }}원</div>
                        </template>
                        <template v-slot:barcodeFee="{item}">
                            <div class="bg-cost">{{ formatNumber(item.barcodeFee) }}원</div>
                        </template>
                        <template v-slot:inventoryCompensation="{item}">
                            <div class="bg-revenue"><span class="text-success">{{ formatNumber(item.inventoryCompensation) }}원</span><a v-if="canSeeRecon" href="javascript:void(0)" class="ml-1 small" title="공급관리에서 확인 안 된 재고 보기" @click.stop="openReconInScm">확인 안 된 재고 ›</a></div>
                        </template>
                        <template v-slot:milkrunCostTotal="{item}">
                            <div class="bg-cost">{{ formatNumber(item.milkrunCostTotal) }}원</div>
                        </template>
                        <template v-slot:adTotal="{item}">
                            <div class="bg-cost">{{ formatNumber(item.adTotal) }}원</div>
                        </template>
                        <template v-slot:sellingCosts="{item}">
                            <div class="bg-cost">{{ formatNumber(item.sellingCosts) }}원</div>
                        </template>
                        <template v-slot:contribution="{item}">
                            <span :class="item.contribution >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(item.contribution) }}원
                            </span>
                        </template>
                        <template v-slot:cogs="{item}">
                            <div class="bg-cost">{{ item.cogs !== null ? formatNumber(item.cogs) + '원' : '-' }}</div>
                        </template>
                        <template v-slot:vat="{item}">
                            <div class="bg-cost">{{ item.vat !== null ? formatNumber(item.vat) + '원' : '-' }}</div>
                        </template>
                        <template v-slot:grossProfit="{item}">
                            <span v-if="item.grossProfit !== null" :class="item.grossProfit >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(Math.round(item.grossProfit)) }}원
                            </span>
                            <span v-else>-</span>
                        </template>
                        <template v-slot:profitPerUnit="{item}">
                            {{ item.profitPerUnit !== null ? formatNumber(Math.round(item.profitPerUnit)) + '원' : '-' }}
                            <b-badge v-if="item.costPending || item.costPendingDays" variant="warning" class="ml-1" title="배송 완료 전이라 배송비·입출고비가 아직 다 안 잡혔습니다. 며칠 뒤 다시 수집하면 확정됩니다">비용 미확정</b-badge>
                        </template>
                        <template v-slot:grossMargin="{item}">
                            <span v-if="item.grossMargin !== null && item.grossMargin !== undefined" :class="item.grossMargin >= 30 ? 'text-success font-weight-bold' : item.grossMargin >= 10 ? 'text-warning' : 'text-danger'">
                                {{ item.grossMargin.toFixed(1) }}%
                            </span>
                            <span v-else>-</span>
                        </template>
                        <template v-slot:naturalSales="{item}">
                            {{ formatNumber(item.naturalSales || 0) }}개
                        </template>
                        <template v-slot:saleQuantityFromAd="{item}">
                            {{ formatNumber(item.saleQuantityFromAd || 0) }}개
                        </template>
                        <template v-slot:returnQuantity="{item}">
                            <span class="text-secondary">{{ formatNumber(item.returnQuantity || 0) }}개</span>
                        </template>
                        <template v-slot:adTotalProfit="{item}">
                            <span :class="item.adTotalProfit >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(Math.round(item.adTotalProfit || 0)) }}원
                            </span>
                        </template>
                        <template v-slot:adNetProfit="{item}">
                            <span :class="(item.adNetProfit || 0) >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(Math.round(item.adNetProfit || 0)) }}원
                            </span>
                        </template>
                        <template v-slot:roas="{item}">
                            {{ (item.roas !== null && item.roas !== undefined) ? (item.roas * 100).toFixed(1) + '%' : '-' }}
                        </template>
                        <template v-slot:adROI="{item}">
                            {{ (item.adROI !== null && item.adROI !== undefined) ? (item.adROI * 100).toFixed(1) + '%' : '-' }}
                        </template>
                        <template v-slot:roi="{item}">
                            <span v-if="item.roi !== null && item.roi !== undefined" :class="item.roi >= 30 ? 'text-success font-weight-bold' : item.roi >= 10 ? 'text-warning' : 'text-danger'">
                                {{ item.roi.toFixed(1) }}%
                            </span>
                            <span v-else>-</span>
                        </template>
                        <!-- 일별 테이블 슬롯들 (모달에서 사용) -->
                        <template v-slot:일별판매량="{item}">
                            {{ formatNumber(item.일별판매량) }}개
                        </template>
                        <template v-slot:일별총매출액="{item}">
                            {{ formatNumber(item.일별총매출액) }}원
                        </template>
                        <template v-slot:일별쿠팡지원할인="{item}">
                            {{ formatNumber(item.일별쿠팡지원할인) }}원
                        </template>
                        <template v-slot:일별즉시할인쿠폰="{item}">
                            {{ formatNumber(item.일별즉시할인쿠폰) }}원
                        </template>
                        <template v-slot:일별다운로드쿠폰="{item}">
                            {{ formatNumber(item.일별다운로드쿠폰) }}원
                        </template>
                        <template v-slot:일별순매출액="{item}">
                            {{ formatNumber(item.일별순매출액) }}원
                        </template>
                        <template v-slot:일별수수료="{item}">
                            {{ formatNumber(item.일별수수료) }}원
                        </template>
                        <template v-slot:일별배송비="{item}">
                            {{ formatNumber(item.일별배송비) }}원
                        </template>
                        <template v-slot:일별입출고비="{item}">
                            {{ formatNumber(item.일별입출고비) }}원
                        </template>
                        <template v-slot:일별광고비="{item}">
                            {{ formatNumber(item.일별광고비) }}원
                        </template>
                        <template v-slot:일별판매비용합계="{item}">
                            {{ formatNumber(item.일별판매비용합계) }}원
                        </template>
                        <template v-slot:일별기여이익="{item}">
                            <span :class="item.일별기여이익 >= 0 ? 'text-success font-weight-bold' : 'text-danger font-weight-bold'">
                                {{ formatNumber(item.일별기여이익) }}원
                            </span>
                        </template>
                        <template v-slot:일별매입원가="{item}">
                            {{ formatNumber(item.일별매입원가) }}원
                        </template>
                        <template v-slot:일별부가세="{item}">
                            {{ formatNumber(item.일별부가세) }}원
                        </template>
                        <template v-slot:일별총수익="{item}">
                            <span :class="item.일별총수익 >= 0 ? 'font-weight-bold text-success' : 'font-weight-bold text-danger'">
                                {{ formatNumber(item.일별총수익) }}원
                            </span>
                        </template>
                        <template v-slot:일별자연판매량="{item}">
                            {{ formatNumber(item.일별자연판매량) }}개
                        </template>
                        <template v-slot:일별광고판매량="{item}">
                            {{ formatNumber(item.일별광고판매량) }}개
                        </template>
                        <template v-slot:일별반품수량="{item}">
                            <span class="text-secondary">{{ formatNumber(item.일별반품수량 || 0) }}개</span>
                        </template>
                        <template v-slot:일별ROAS="{item}">
                            {{ (item.일별ROAS && item.일별ROAS > 0) ? (item.일별ROAS * 100).toFixed(1) + '%' : '-' }}
                        </template>
                        <template v-slot:일별ROI="{item}">
                            <span v-if="item.일별ROI && item.일별ROI !== 0" :class="item.일별ROI >= 30 ? 'text-success font-weight-bold' : item.일별ROI >= 10 ? 'text-warning' : 'text-danger'">
                                {{ item.일별ROI.toFixed(1) }}%
                            </span>
                            <span v-else>-</span>
                        </template>
                        <template v-slot:memo="{item}">
                            <MemoCell :content="getMemo(item)" @save="(c) => saveMemo(item, c)" />
                        </template>
                    </CDataTable>
                </b-modal>
            </div>
        </div>
    </div>
</template>

<script>
import axios from 'axios'
import CDataTable from '@/components/common/CDataTable'
import PnlPivotTable from '@/components/growth/PnlPivotTable'
import LedgerCostManager from '@/components/growth/LedgerCostManager'
import { fifoLotByDate, 밀크런ByDate, 매입가ByDate } from '@/utils/lotCost';
import { ledgerProfitPerUnit } from '@/utils/adDomain';
import { buildWaterfall, buildDiagnostics, formatMoneyShort } from '@/utils/ledgerCharts';
import AssetTrendChart from '@/components/growth/AssetTrendChart'
import MonthlyTrendChart from '@/components/growth/MonthlyTrendChart'
import ledgerCache from '@/services/ledgerCache'
import MemoCell from '@/components/common/MemoCell'
import ApexChart from '@/components/charts/ApexChart'
import * as XLSX from 'xlsx'
import keepAliveViewMixin from '@/mixins/keepAliveViewMixin'

// 이 화면이 store 에 담은 상품DB 목록과, 그걸 받기 직전의 원본 변경시각(stamp).
// store 목록이 이것과 같고 서버 stamp 도 그대로일 때만 다시 받지 않는다
const growthDbLoaded = { list: null, stamp: null };

export default {
    name: 'GrowthSalesLedger',
    mixins: [keepAliveViewMixin],
    components: {
        CDataTable,
        PnlPivotTable,
        LedgerCostManager,
        AssetTrendChart,
        MonthlyTrendChart,
        MemoCell,
        ApexChart
    },
    data() {
        return {
            title: '판매장부',
            desc1: '기간별 상품 판매 수익성 분석',
            desc2: '18개 핵심 지표로 상품별 수익성을 한눈에 확인하세요',

            showlist: false,
            showspinner: false,
            loadingtext: '데이터 로딩 중...',

            // 정산현황수집 관련 (growthScm과 동일한 방식)
            todaySettlementNeeded: false, // Toast 메시지 표시 여부와 동일한 상태

            // 재수집 윈도우: 최근 N일은 DB에 있어도 다시 수집 (쿠팡에서 배송비/입출고비가 확정되기까지의 기간)
            // 운영하면서 늦게 확정되는 케이스가 보이면 숫자만 늘리면 됨
            recollectDays: 5,

            // 조회 기간
            startDate: '',
            endDate: '',
            // 실제로 조회된 기간 — 리스크 판정 전용 (조회 후 입력칸만 바꿔도 판정이 어긋나지 않게)
            queriedStartDate: '',
            queriedEndDate: '',

            // 테이블 데이터
            salesLedgerData: [],
            productViewData: [], // 상품별 보기용 데이터

            // 상품DB에 없어서 집계에서 빠진 판매 (조용히 사라지면 쿠팡 숫자와 안 맞는 원인이 된다)
            excludedSales: { optionCount: 0, rowCount: 0, amount: 0 },

            // ===== 손익장부 (요약장부 / 비용관리 / 자산현황) =====
            activeTab: 'ledger',
            ledgerTabs: [
                { value: 'summary', text: '요약장부' },
                { value: 'ledger', text: '일별 · 상품별' }
            ],
            showCostManager: false,   // 요약장부 안의 비용관리 접기/펼치기
            costInitialDate: '',      // 비용관리를 열 때 맞출 날짜(일별 «비용 입력» = endDate · 요약장부 = 빈 값)
            monthlySummary: [],    // 서버 월별 집계 (연 단위, 조회기간과 무관)
            dailySummary: [],      // 서버 일별 집계 (자산현황 차트용)
            missingPriceCount: 0,  // 매입가가 없어 손익에서 빠진 상품 수
            missingPriceAmount: 0,
            missingPriceList: [],  // [{ vendorItemId, quantity, contribution }] 금액 큰 순
            ledgerYear: String(new Date().getFullYear()),
            ledgerYears: [],
            loadingSummary: false,
            quickRanges: [
                { days: 7, text: '최근 7일' },
                { days: 30, text: '최근 30일' },
                { days: 90, text: '최근 90일' }
            ],
            fixedCosts: [],        // 월 스냅샷 고정비 [{month, items:[]}]
            productCosts: [],      // 상품 직접비(외부마케팅비) 건별
            ledgerSetting: null,   // { baseCashDate, baseCashAmount }
            inventoryCoverage: [], // 상품별 재고 스냅샷 (재고 역산 기준점)
            reconSummary: null,      // 재고 대사 요약 — 「확인 안 된 재고」 칸 (공급관리와 같은 서버 계산)
            inboundHistory: [],    // 입고이력 (재고 역산)
            settlementSchedules: [], // 실제 정산 일정 (입금 시점)

            // ===== 판매장부 잠금 =====
            // 직원과 화면을 같이 쓸 때 원가·이익을 가린다. 회사 단위 비번 하나.
            // 잠겨 있으면 매입가 알림과 수집 버튼만 남기고 나머지를 감춘다
            ledgerLocked: false,     // 회사에 잠금이 걸려 있나
            ledgerUnlocked: false,   // 이 브라우저에서 풀었나
            showLockModal: false,
            showLockSetModal: false,
            lockInput: '',
            lockNewPw: '',
            lockCurrentPw: '',
            lockError: '',
            savingFixedCost: false,
            tableSelectedItem: [],

            // 보기 모드 및 모달
            isProductView: false, // 상품별 보기 switch
            ledgerRiskFilter: null, // 선택된 리스크 필터 키 (null = 해제)
            // 리스크 필터 버튼 — 빨강(적자) 6개, 노랑(검토) 3개. half = 앞뒤 절반 비교라 14일 이상만
            ledgerRiskButtons: [
                { key: 'lossByAd', label: '광고로 적자', color: 'danger', tip: '광고로 판 만큼의 이익이 광고비보다 적은 상품입니다' },
                { key: 'lossByReturn', label: '반품으로 적자', color: 'danger', tip: '광고비와 반품 회수비만 없었으면 흑자인 상품입니다' },
                { key: 'lossByStorage', label: '보관비로 적자', color: 'danger', tip: '광고비·반품 회수비·보관비만 없었으면 흑자인 상품입니다' },
                { key: 'lossStructural', label: '팔수록 적자', color: 'danger', tip: '광고·반품·보관비를 빼도 적자라 팔 때마다 손해인 상품입니다' },
                { key: 'storageOnly', label: '안 팔리고 보관비만', color: 'danger', tip: '기간 중 팔린 게 없는데 보관비가 나간 상품입니다' },
                { key: 'noPurchasePrice', label: '매입가 없음', color: 'danger', tip: '매입가가 입력되지 않아 이익을 계산할 수 없는 상품입니다' },
                { key: 'adLossAfterReturn', label: '반품 반영 시 광고적자', color: 'warning', tip: '반품률을 감안하면 광고로 판 만큼 광고비를 못 건지는 상품입니다' },
                { key: 'roiDecline', label: 'ROI 하락', color: 'warning', tip: '조회 기간 뒤쪽 절반의 ROI 가 앞쪽보다 10% 넘게 떨어진 상품입니다', half: true },
                { key: 'roasDecline', label: 'ROAS 하락', color: 'warning', tip: '조회 기간 뒤쪽 절반의 ROAS 가 앞쪽보다 10% 넘게 떨어진 상품입니다', half: true }
            ],
            dailyChartMode: 'amount', // 일별 차트 모드 (amount: 금액, quantity: 수량)
            showModal: false,
            modalTitle: '',
            modalData: [],
            modalHeaders: [],
            modalChartCollapsed: false, // 특정 상품 날짜별 모달 상단 그래프 접기 상태
            modalCoverage: null,        // 특정 상품 날짜별 모달 상단 재고 패널 데이터
            // 재고 스냅샷 (growthScm 계산값) — vendorItemId 기준 맵
            inventoryCoverageMap: {},
            ledgerExtrasPromise: null,   // loadLedgerExtras 진행 중 프라미스 (재고 스냅샷 재활용용)
            // 상품DB 로딩 프라미스 — mounted 로딩 중 조회를 눌러도 기다리게 하는 용도
            growthDbPromise: null,
            // 판매장부 메모 — 회사 단위 공유 메모
            memos: {
                date: {},          // { 'YYYY-MM-DD': '...' }
                product: {},       // { vendorItemId: '...' }
                date_product: {}   // { 'YYYY-MM-DD|vendorItemId': '...' }
            },
            showModalChart: false,
            modalChartOptions: {},
            showSummaryChart: false,
            summaryChartOptions: {},

            // 요약 데이터
            summary: {
                totalRevenue: 0,
                totalNetSales: 0,
                totalProfit: 0,
                totalVat: 0,
                totalAdCost: 0,
                totalPurchase: 0,
                totalFees: 0,
                totalSellingCosts: 0,
                totalCompensation: 0, // 재고손실보상 (총이익 포함·개당이익 제외)
                totalMarginRate: 0,
                totalROAS: 0,
                totalROI: 0,
                totalStockAmount: 0 // 전체가용재고 금액 합계 (현재 스냅샷)
            },

            // 차트 옵션
            diagSort: 'stock', // 상품 진단 리스트 정렬 (stock: 묶인 돈 / sales: 매출 / loss: 손실 / roi: ROI)
            // 워터폴은 기간 합계라 "언제부터 나빠졌나"에 답할 수 없다. 시계열로 전환할 수 있게 둔다
            chartMode: 'waterfall', // waterfall | daily

            // 헤더 툴팁 정의
            headerTooltips: {
                // 공통 컬럼
                '날짜': '매출 인식일 기준입니다',
                'date': '매출 인식일 기준입니다',
                '썸네일': '상품 이미지입니다',
                '상품명\n옵션명': '상품명과 옵션명입니다',
                '노출ID': '쿠팡 상품 노출 ID입니다',
                'productId': '쿠팡 상품 노출 ID입니다',
                '옵션ID': '쿠팡 옵션 ID입니다',
                'vendorItemId': '쿠팡 옵션 ID입니다',
                '상품등급': '상품의 등급 정보입니다',
                'skuGrade': '상품의 등급 정보입니다',

                // 일별 컬럼
                '판매량': '해당 기간 총 판매 수량입니다',
                '판매가': '순매출(쿠폰 제외) ÷ 판매량으로 계산한 개당 판매가입니다',
                '일별판매량': '해당 일자 총 판매 수량입니다',
                '총판매액': '정가 기준 총 매출액입니다',
                '일별총매출액': '해당 일자 정가 기준 총 매출액입니다',
                '쿠팡지원할인': '쿠팡에서 지원하는 할인 금액입니다',
                '일별쿠팡지원할인': '해당 일자 쿠팡에서 지원하는 할인 금액입니다',
                '즉시할인쿠폰': '즉시 할인 쿠폰 사용 금액입니다',
                '일별즉시할인쿠폰': '해당 일자 즉시 할인 쿠폰 사용 금액입니다',
                '다운로드쿠폰': '다운로드 쿠폰 사용 금액입니다',
                '일별다운로드쿠폰': '해당 일자 다운로드 쿠폰 사용 금액입니다',
                '순매출': '총판매액에서 모든 할인을 차감한 실제 매출액입니다',
                '일별순매출액': '해당 일자 총판매액에서 모든 할인을 차감한 실제 매출액입니다',
                '수수료': '판매 수수료 (부가세 포함)입니다',
                '일별수수료': '해당 일자 판매 수수료 (부가세 포함)입니다',
                '배송비': '배송비 (부가세 포함)입니다',
                '일별배송비': '해당 일자 배송비 (부가세 포함)입니다',
                '입출고비': '입출고비 (부가세 포함)입니다',
                '일별입출고비': '해당 일자 입출고비 (부가세 포함)입니다',
                '보관비': '로켓그로스 보관비입니다',
                '일별보관비': '해당 일자 로켓그로스 보관비입니다',
                '반품회수재입고비': '반품 회수 및 재입고 비용입니다',
                '일별반품회수재입고비': '해당 일자 반품 회수 및 재입고 비용입니다',
                '반출비': '반출 처리 비용입니다',
                '일별반출비': '해당 일자 반출 처리 비용입니다',
                '반출배송비': '반출 배송 비용입니다',
                '일별반출배송비': '해당 일자 반출 배송 비용입니다',
                '바코드비': '바코드 라벨링 비용입니다',
                '일별바코드비': '해당 일자 바코드 라벨링 비용입니다',
                '재고손실보상': '재고 손실에 대한 보상금입니다 (수익)',
                '일별재고손실보상': '해당 일자 재고 손실에 대한 보상금입니다 (수익)',
                '광고비': '광고비 (부가세 포함)입니다',
                '일별광고비': '해당 일자 광고비 (부가세 포함)입니다',
                '밀크런비용': '밀크런 입고 시 부피비례 배분된 비용입니다',
                '일별밀크런비용': '해당 일자 밀크런 입고 비용입니다',
                '기타비용': '비용관리에서 이 상품·날짜로 등록한 지출입니다 (체험단, 인플루언서, 촬영 등). 마우스를 올리면 내역이 보입니다',
                '일별외부마케팅비': '비용관리에서 이 날짜로 등록한 지출입니다. 마우스를 올리면 내역이 보입니다',
                'externalMarketing': '비용관리에서 이 상품으로 등록한 지출입니다. 마우스를 올리면 내역이 보입니다',
                '판매비용합계': '수수료 + 배송비 + 입출고비 + 보관비 + 반품회수재입고비 + 반출비 + 반출배송비 + 바코드비 + 밀크런비용 + 광고비 - 재고손실보상',
                '일별판매비용합계': '해당 일자 수수료 + 배송비 + 입출고비 + 보관비 + 반품회수재입고비 + 반출비 + 반출배송비 + 바코드비 + 밀크런비용 + 광고비 - 재고손실보상',
                '기여이익': '순매출에서 판매비용을 차감한 금액입니다',
                '일별기여이익': '해당 일자 순매출에서 판매비용을 차감한 금액입니다',
                '매입원가': '상품 매입에 소요된 비용입니다',
                '일별매입원가': '해당 일자 상품 매입에 소요된 비용입니다',
                '부가세': '(기여이익-매입원가)*1/11로 계산된 부가세입니다',
                '일별부가세': '해당 일자 (기여이익-매입원가)*1/11로 계산된 부가세입니다',
                '총이익(GP)': '기여이익에서 매입원가와 부가세를 차감한 총 이익입니다',
                '일별총수익': '해당 일자 기여이익에서 매입원가와 부가세를 차감한 총 이익입니다',
                '자연판매량': '광고를 통하지 않은 자연 판매 수량입니다 (정상 판매만)',
                '일별자연판매량': '해당 일자 광고를 통하지 않은 자연 판매 수량입니다 (정상 판매만)',
                '반품수량': '취소/반품된 수량입니다',
                '일별반품수량': '해당 일자 취소/반품된 수량입니다',
                '광고판매량': '광고를 통한 판매 수량입니다',
                '일별광고판매량': '해당 일자 광고를 통한 판매 수량입니다',
                '광고수익': '광고를 통한 총 수익 금액입니다',
                '광고매출': '광고판매량 × 개당매출액 (광고로 발생한 실제 매출)',
                '광고순수익': '광고로 발생한 이익 - 광고비 (광고판매량 × 개당이익[광고제외] - 광고비)',
                '일별광고총수익': '해당 일자 광고판매량 × 개당매출액 (광고로 발생한 실제 매출)',
                '일별광고순수익': '해당 일자 광고로 발생한 이익 - 광고비',
                'ROAS': '광고 대비 매출 비율 (Return On Ad Spend)입니다',
                '일별ROAS': '해당 일자 광고 대비 매출 비율 (Return On Ad Spend)입니다',
                'ROI': '매입(투자) 대비 수익률 (Return On Investment)입니다. 분모(매입원가)는 부가세 제외 기준입니다 (매입가×10/11).',
                '일별ROI': '해당 일자 매입 대비 수익률 (Return On Investment)입니다. 분모(매입원가)는 부가세 제외 기준입니다 (매입가×10/11).',

                // 상품별 컬럼
                '쿠팡재고': '쿠팡 물류센터 발주가능 재고 수량입니다 (재고관리 화면 기준, 현재 스냅샷)',
                'coupangStock': '쿠팡 물류센터 발주가능 재고 수량입니다 (재고관리 화면 기준, 현재 스냅샷)',
                '매입가': '가장 최근 매입가(개당)입니다 (상품DB 기준)',
                'purchasePrice': '가장 최근 매입가(개당)입니다 (상품DB 기준)',
                'saleQuantity': '순수 판매 수량입니다 (광고판매량 + 자연판매량 - 반품수량)',
                'saleAmount': '정가 기준 총 매출액입니다',
                'coupangDiscount': '쿠팡에서 지원하는 할인 금액입니다',
                'instantCoupon': '즉시 할인 쿠폰 사용 금액입니다',
                'downloadCoupon': '다운로드 쿠폰 사용 금액입니다',
                'netSales': '총판매액에서 모든 할인을 차감한 실제 매출액입니다',
                'commissionTotal': '판매 수수료 (부가세 포함)입니다',
                'shippingTotal': '배송비 (부가세 포함)입니다',
                'warehouseTotal': '입출고비 (부가세 포함)입니다',
                'storageFee': '로켓그로스 보관비입니다',
                'creturnFee': '반품 회수 및 재입고 비용입니다',
                'vreturnFee': '반출 처리 비용입니다',
                'vreturnShippingFee': '반출 배송 비용입니다',
                'barcodeFee': '바코드 라벨링 비용입니다',
                'inventoryCompensation': '재고 손실에 대한 보상금입니다 (수익 — 총이익에는 넣고 개당이익에는 넣지 않습니다)',
                'adTotal': '광고비 (부가세 포함)입니다',
                'milkrunCostTotal': '밀크런 입고 시 부피비례 배분된 비용입니다',
                'sellingCosts': '수수료 + 배송비 + 입출고비 + 보관비 + 반품회수재입고비 + 반출비 + 반출배송비 + 바코드비 + 밀크런비용 + 광고비 - 재고손실보상',
                'contribution': '순매출에서 판매비용을 차감한 금액입니다',
                'cogs': '상품 매입에 소요된 비용입니다',
                'vat': '(기여이익-매입원가)*1/11로 계산된 부가세입니다',
                'grossProfit': '기여이익에서 매입원가와 부가세를 차감한 총 이익입니다',
                'profitPerUnit': '(총이익 + 광고비 − 재고손실보상)을 판매량으로 나눈 개당 이익입니다 (광고비·재고손실보상 제외)',
                'grossMargin': '총이익을 순매출로 나눈 마진율(%)입니다',
                'naturalSales': '광고를 통하지 않은 자연 판매 수량입니다 (정상 판매만)',
                'saleQuantityFromAd': '광고를 통한 판매 수량입니다 (정상 판매만)',
                'returnQuantity': '취소/반품된 수량입니다',
                'adTotalProfit': '광고판매량 × 개당매출액 (광고로 발생한 실제 매출)',
                'adNetProfit': '광고로 발생한 이익 - 광고비 (광고판매량 × 개당이익[광고제외] - 광고비)',
                'roas': '광고 대비 매출 비율 (Return On Ad Spend)입니다',
                'adROI': '광고비 대비 광고매출 비율입니다',
                'roi': '매입(투자) 대비 수익률 (Return On Investment)입니다. 분모(매입원가)는 부가세 제외 기준입니다 (매입가×10/11).',
                '개당이익': '(총이익 + 광고비 − 재고손실보상)을 판매량으로 나눈 개당 이익입니다 (광고비·재고손실보상 제외)',
                '마진율': '총이익을 순매출로 나눈 마진율(%)입니다',
                '광고ROI': '광고비 대비 광고매출 비율입니다'
            },

            // 일별 테이블 헤더 (수치 합계로 표시)
            dailyHeaders: [
                { text: '날짜', value: 'date', align: 'center', width: 120 },
                { text: '판매량', value: '일별판매량', align: 'right', width: 100, isSlot: true },
                { text: '총판매액', value: '일별총매출액', align: 'right', width: 140, isSlot: true },
                { text: '쿠팡지원할인', value: '일별쿠팡지원할인', align: 'right', width: 120, isSlot: true },
                { text: '즉시할인쿠폰', value: '일별즉시할인쿠폰', align: 'right', width: 120, isSlot: true },
                { text: '다운로드쿠폰', value: '일별다운로드쿠폰', align: 'right', width: 120, isSlot: true },
                { text: '순매출', value: '일별순매출액', align: 'right', width: 140, isSlot: true },
                { text: '수수료', value: '일별수수료', align: 'right', width: 100, isSlot: true },
                { text: '배송비(VAT 포함)', value: '일별배송비', align: 'right', width: 130, isSlot: true },
                { text: '입출고비(VAT 포함)', value: '일별입출고비', align: 'right', width: 130, isSlot: true },
                { text: '보관비', value: '일별보관비', align: 'right', width: 100, isSlot: true },
                { text: '반품회수재입고비', value: '일별반품회수재입고비', align: 'right', width: 130, isSlot: true },
                { text: '반출비', value: '일별반출비', align: 'right', width: 100, isSlot: true },
                { text: '반출배송비', value: '일별반출배송비', align: 'right', width: 110, isSlot: true },
                { text: '바코드비', value: '일별바코드비', align: 'right', width: 100, isSlot: true },
                { text: '재고손실보상', value: '일별재고손실보상', align: 'right', width: 110, isSlot: true },
                { text: '밀크런비용', value: '일별밀크런비용', align: 'right', width: 110, isSlot: true },
                { text: '광고비', value: '일별광고비', align: 'right', width: 100, isSlot: true },
                { text: '기타비용', value: '일별외부마케팅비', align: 'right', width: 130, isSlot: true },
                { text: '판매비용합계', value: '일별판매비용합계', align: 'right', width: 120, isSlot: true },
                { text: '기여이익', value: '일별기여이익', align: 'right', width: 120, isSlot: true },
                { text: '매입원가', value: '일별매입원가', align: 'right', width: 120, isSlot: true },
                { text: '부가세', value: '일별부가세', align: 'right', width: 100, isSlot: true },
                { text: '총이익(GP)', value: '일별총수익', align: 'right', width: 120, isSlot: true },
                { text: '자연판매량', value: '일별자연판매량', align: 'right', width: 100, isSlot: true },
                { text: '광고판매량', value: '일별광고판매량', align: 'right', width: 100, isSlot: true },
                { text: '반품수량', value: '일별반품수량', align: 'right', width: 100, isSlot: true },
                { text: '광고매출', value: '일별광고총수익', align: 'right', width: 120, isSlot: true },
                { text: '광고순수익', value: '일별광고순수익', align: 'right', width: 120, isSlot: true },
                { text: 'ROAS', value: '일별ROAS', align: 'right', width: 100, isSlot: true },
                { text: 'ROI', value: '일별ROI', align: 'right', width: 100, isSlot: true },
                { text: '메모', value: 'memo', align: 'left', width: 220, isSlot: true }
            ],

            // 상품별 테이블 헤더 (상품 정보 포함)
            productHeaders: [
                { text: '썸네일', value: '썸네일', align: 'center', width: 80, isSlot: true },
                { text: '상품명\n옵션명', value: '상품명', align: 'left', width: 250, isSlot: true },
                { text: '노출ID', value: 'productId', align: 'center', width: 120 },
                { text: '옵션ID', value: 'vendorItemId', align: 'center', width: 140 },
                { text: '상품등급', value: 'skuGrade', align: 'center', width: 100, isSlot: true },
                { text: '판매가', value: '판매가', align: 'right', width: 110, isSlot: true },
                { text: '쿠팡재고', value: 'coupangStock', align: 'right', width: 100, isSlot: true },
                { text: '매입가', value: 'purchasePrice', align: 'right', width: 110, isSlot: true },
                { text: '판매량', value: 'saleQuantity', align: 'right', width: 100, isSlot: true },
                { text: '총판매액', value: 'saleAmount', align: 'right', width: 140, isSlot: true },
                { text: '쿠팡지원할인', value: 'coupangDiscount', align: 'right', width: 120, isSlot: true },
                { text: '즉시할인쿠폰', value: 'instantCoupon', align: 'right', width: 120, isSlot: true },
                { text: '다운로드쿠폰', value: 'downloadCoupon', align: 'right', width: 120, isSlot: true },
                { text: '순매출', value: 'netSales', align: 'right', width: 140, isSlot: true },
                { text: '수수료', value: 'commissionTotal', align: 'right', width: 100, isSlot: true },
                { text: '배송비(VAT 포함)', value: 'shippingTotal', align: 'right', width: 130, isSlot: true },
                { text: '입출고비(VAT 포함)', value: 'warehouseTotal', align: 'right', width: 130, isSlot: true },
                { text: '보관비', value: 'storageFee', align: 'right', width: 100, isSlot: true },
                { text: '반품회수재입고비', value: 'creturnFee', align: 'right', width: 130, isSlot: true },
                { text: '반출비', value: 'vreturnFee', align: 'right', width: 100, isSlot: true },
                { text: '반출배송비', value: 'vreturnShippingFee', align: 'right', width: 110, isSlot: true },
                { text: '바코드비', value: 'barcodeFee', align: 'right', width: 100, isSlot: true },
                { text: '재고손실보상', value: 'inventoryCompensation', align: 'right', width: 110, isSlot: true },
                { text: '밀크런비용', value: 'milkrunCostTotal', align: 'right', width: 110, isSlot: true },
                { text: '광고비', value: 'adTotal', align: 'right', width: 100, isSlot: true },
                { text: '기타비용', value: 'externalMarketing', align: 'right', width: 130, isSlot: true },
                { text: '판매비용합계', value: 'sellingCosts', align: 'right', width: 120, isSlot: true },
                { text: '기여이익', value: 'contribution', align: 'right', width: 120, isSlot: true },
                { text: '매입원가', value: 'cogs', align: 'right', width: 120, isSlot: true },
                { text: '부가세', value: 'vat', align: 'right', width: 100, isSlot: true },
                { text: '총이익(GP)', value: 'grossProfit', align: 'right', width: 120, isSlot: true },
                { text: '개당이익', value: 'profitPerUnit', align: 'right', width: 100, isSlot: true },
                { text: '마진율', value: 'grossMargin', align: 'right', width: 100, isSlot: true },
                { text: '자연판매량', value: 'naturalSales', align: 'right', width: 100, isSlot: true },
                { text: '광고판매량', value: 'saleQuantityFromAd', align: 'right', width: 100, isSlot: true },
                { text: '반품수량', value: 'returnQuantity', align: 'right', width: 100, isSlot: true },
                { text: '광고매출', value: 'adTotalProfit', align: 'right', width: 120, isSlot: true },
                { text: '광고순수익', value: 'adNetProfit', align: 'right', width: 120, isSlot: true },
                { text: 'ROAS', value: 'roas', align: 'right', width: 100, isSlot: true },
                { text: '광고ROI', value: 'adROI', align: 'right', width: 100, isSlot: true },
                { text: 'ROI', value: 'roi', align: 'right', width: 100, isSlot: true },
                { text: '메모', value: 'memo', align: 'left', width: 220, isSlot: true }
            ]
        }
    },
    computed: {
        // ===== 판매장부 위 차트 (판 #725 · #406 살려 새로 짓기) =====
        // 값은 표가 쓰는 행에서만 — 기타비용을 넣거나 지우면 표와 같이 다시 그려진다

        // 손익 워터폴 (조회기간 합계)
        waterfallOptions() {
            const { bars, etcDetail, 순매출액 } = buildWaterfall(this.dailyRows);
            if (!bars.length) return {};

            const colorOf = (bar) => {
                if (bar.kind === 'start' || bar.kind === 'subtotal') return '#b3b1a8';
                if (bar.kind === 'end') return bar.to >= 0 ? '#2a78d6' : '#d03b3b';
                return bar.delta < 0 ? '#d03b3b' : '#2a78d6';
            };
            const won = (v) => Math.round(v).toLocaleString() + '원';
            const etcText = Object.keys(etcDetail).map(k => k + ' ' + won(etcDetail[k])).join('<br>')
                + '<br>(표 반올림 차이 포함)';

            return {
                chart: { type: 'rangeBar', height: 440, toolbar: { show: false }, background: 'transparent' },
                series: [{
                    name: '금액',
                    data: bars.map(bar => ({
                        x: bar.label,
                        y: [Math.min(bar.from, bar.to), Math.max(bar.from, bar.to)],
                        fillColor: colorOf(bar)
                    }))
                }],
                plotOptions: {
                    bar: {
                        horizontal: true,
                        barHeight: '62%',
                        borderRadius: 4,
                        rangeBarGroupRows: true,
                        // 이걸 끄지 않으면 막대가 좁은 항목(보관비·반품회수 등)의 라벨이 통째로 사라진다
                        dataLabels: { hideOverflowingLabels: false }
                    }
                },
                fill: { opacity: 1 },
                dataLabels: {
                    enabled: true,
                    // 시작점(총매출액)과 소계는 증감이 아니라 누적액 그대로 보여준다
                    formatter: (val, opt) => {
                        const bar = bars[opt.dataPointIndex];
                        if (!bar) return '';
                        if (bar.delta === null) return formatMoneyShort(bar.to);
                        return (bar.delta < 0 ? '−' : '+') + formatMoneyShort(Math.abs(bar.delta));
                    },
                    textAnchor: 'middle',
                    // 짧은 막대는 라벨이 막대 밖으로 나가므로 흰 칩을 깔아 항상 읽히게 한다.
                    // Apex는 style.colors = 칩 배경, background.foreColor = 글자색이다 (이름과 반대)
                    style: { fontSize: '11px', fontWeight: 600, colors: ['#ffffff'] },
                    background: {
                        enabled: true, foreColor: '#212529', borderWidth: 1, borderColor: '#e9ecef',
                        borderRadius: 3, padding: 4, opacity: 0.96, dropShadow: { enabled: false }
                    },
                    dropShadow: { enabled: false }
                },
                grid: { borderColor: '#e9ecef' },
                // 래퍼(ApexChart.vue)가 categories 없으면 [1]을 강제 주입하므로 빈 배열을 넘긴다
                xaxis: { categories: [], labels: { formatter: (v) => formatMoneyShort(v) } },
                yaxis: { labels: { style: { fontSize: '12px' }, maxWidth: 150 } },
                // 폰(390)은 금액 눈금 다섯이 «600만800만1,000만» 으로 붙는다 — 셋으로
                responsive: [{ breakpoint: 576, options: { xaxis: { tickAmount: 3 } } }],
                legend: { show: false },
                tooltip: {
                    custom: ({ dataPointIndex }) => {
                        const bar = bars[dataPointIndex];
                        if (!bar) return '';
                        let body;
                        if (bar.delta === null) {
                            body = '<div>' + won(bar.to) + '</div>';
                        } else {
                            const ratio = 순매출액 > 0
                                ? ' · 순매출의 ' + (Math.abs(bar.delta) / 순매출액 * 100).toFixed(1) + '%'
                                : '';
                            body = '<div>' + (bar.delta < 0 ? '−' : '+') + won(Math.abs(bar.delta)) + '</div>'
                                + '<div class="text-muted">잔액 ' + won(bar.to) + ratio + '</div>';
                        }
                        if (bar.label === '기타 판매비용') body += '<div class="text-muted mt-1">' + etcText + '</div>';
                        if (bar.label === '매입가 미등록 상품') body += '<div class="text-muted mt-1">매입가가 없어 이익을 모르는 상품의 기여이익 — 총수익에서 빠진다</div>';
                        return '<div class="px-2 py-2" style="max-width:260px">'
                            + '<div class="font-weight-bold mb-1">' + bar.label + '</div>' + body + '</div>';
                    }
                }
            };
        },

        // 일별 추이 (워터폴의 짝 — 워터폴은 기간 합계라 시간 축이 없다)
        // 순매출(원)과 ROI(%)는 단위가 달라 한 축에 겹치면 ROI 선이 바닥에 눌린다.
        // 보조축 대신 위아래 두 칸으로 나누고 x축만 맞춘다.
        dailyTrendRows() {
            return (this.dailyRows || []).slice().sort((a, b) => String(a.date).localeCompare(String(b.date)));
        },
        dailyTrendLabels() {
            return this.dailyTrendRows.map(d => String(d.date).slice(5).replace('-', '/'));
        },
        dailySalesOptions() {
            const rows = this.dailyTrendRows;
            if (!rows.length) return {};
            return {
                chart: { type: 'bar', height: 195, toolbar: { show: false }, background: 'transparent', animations: { enabled: false } },
                series: [{ name: '순매출', data: rows.map(d => Math.round(d.일별순매출액 || 0)) }],
                colors: ['#2a78d6'],
                plotOptions: { bar: { columnWidth: '58%', borderRadius: 3 } },
                fill: { opacity: 1 },
                dataLabels: { enabled: false },
                stroke: { width: 2, colors: ['#ffffff'] },
                grid: { borderColor: '#e9ecef' },
                legend: { show: false },
                xaxis: { categories: this.dailyTrendLabels, tickAmount: 10, labels: { show: false } },
                yaxis: { tickAmount: 3, labels: { formatter: (v) => formatMoneyShort(v) } },
                tooltip: { y: { formatter: (v) => Math.round(v).toLocaleString() + '원' } }
            };
        },
        dailyRoiOptions() {
            const rows = this.dailyTrendRows;
            if (!rows.length) return {};
            // ROI 는 표(dailyRows)의 총수익·매입원가로 — 기타비용이 얹힌 날도 표와 같게
            const roiOf = (d) => (d.일별매입원가 > 0
                ? Math.round((d.일별총수익 / (d.일별매입원가 * 10 / 11)) * 100 * 10) / 10
                : 0);
            return {
                chart: { type: 'line', height: 235, toolbar: { show: false }, background: 'transparent', animations: { enabled: false } },
                series: [{ name: 'ROI', data: rows.map(roiOf) }],
                colors: ['#2a78d6'],
                stroke: { curve: 'straight', width: 2 },
                markers: { size: 0, hover: { size: 6 } },
                dataLabels: { enabled: false },
                grid: { borderColor: '#e9ecef' },
                legend: { show: false },
                xaxis: { categories: this.dailyTrendLabels, tickAmount: 10, labels: { show: true, style: { fontSize: '11px' } } },
                // 0부터 그린다 — 축을 잘라내면 하락폭이 실제보다 커 보이고 10% 기준선도 화면 밖으로 나간다
                yaxis: { min: 0, tickAmount: 4, labels: { formatter: (v) => Math.round(v) + '%' } },
                annotations: {
                    // 기준선은 데이터가 아니라 배경 눈금이라 중립색. 임계값은 요약 카드와 같은 30/10
                    yaxis: [
                        { y: 30, borderColor: '#c3c2b7', strokeDashArray: 5,
                            label: { text: '목표 30%', position: 'left', textAnchor: 'start', style: { background: 'transparent', color: '#898781', fontSize: '11px' } } },
                        { y: 10, borderColor: '#c3c2b7', strokeDashArray: 5,
                            label: { text: '위험 10%', position: 'left', textAnchor: 'start', style: { background: 'transparent', color: '#898781', fontSize: '11px' } } }
                    ]
                },
                tooltip: { y: { formatter: (v) => v.toFixed(1) + '%' } }
            };
        },

        // 상품 진단 TOP 10 (기타비용 얹힌 상품별 표 행 + 재고 스냅샷)
        diagnostics() {
            return buildDiagnostics(this.productRows, (vid) => this.getCoverage(vid), this.diagSort);
        },
        // 재고 대사(확인 안 된 재고·연결)를 볼 수 있나 — 판단은 서버(보는 사람 = 로그인한 본인 기준).
        // 화면은 서버가 요약을 줬는가만 본다
        canSeeRecon() {
            return !!this.reconSummary;
        },
        // 비용 입력에서 고를 상품 — 아직 판매가 없는 신상품도 넣을 수 있게 상품DB 전체
        costProductOptions() {
            return this.buildCostProductOptions();
        },

        // 상품별 매입 lot — 자산현황이 입고ID 로 lot 단가를 붙이는 데 쓴다 (정본 §5 Phase 3-14).
        // 수량이 붙은 lot 이 하나도 없는 상품은 아예 안 담는다.
        // 결과가 빈 배열이면 자산현황은 지금까지 쓰던 coverage.purchasePrice 대표값 그대로 간다.
        purchaseLots() {
            const out = [];
            (this.$store.state.growthdb || []).forEach(g => {
                if (!g || g.vendorItemId == null || !Array.isArray(g.매입정보)) return;
                if (!g.매입정보.some(r => r && r.입고ID && (parseFloat(r.수량) > 0))) return;
                out.push({ vendorItemId: g.vendorItemId, 매입정보: g.매입정보 });
            });
            return out;
        },

        // 번들 옵션 vid → 부모 vid + 묶음수량.
        // 번들은 GrowthDB 에 최상위 행이 없고 부모의 upBundlingProducts[] 안에만 있어서
        // 재고 스냅샷(InventoryCoverage)에도 자기 행이 없다 → getCoverage 가 여기로 부모를 찾는다.
        // 호출부가 상품 수만큼 도는 자리라(generateProductViewData 등) 매번 훑지 않고 한 번만 만든다.
        //
        // 반품재판매 옵션(returnProducts[] — RETURN_BEST 등)도 같은 처지라 묶음수량 1 로 함께 넣는다.
        // 손익 계산(:2898 productInfo 매칭)도 옵션은 본체 매입가를 그대로 쓴다.
        bundleParentOf() {
            const map = {};
            (this.$store.state.growthdb || []).forEach(p => {
                if (!p || p.vendorItemId == null) return;
                if (Array.isArray(p.returnProducts)) {
                    p.returnProducts.forEach(rp => {
                        if (rp && rp.vendorItemId != null) {
                            map[String(rp.vendorItemId)] = { parentVid: String(p.vendorItemId), size: 1 };
                        }
                    });
                }
                if (!Array.isArray(p.upBundlingProducts)) return;
                p.upBundlingProducts.forEach(b => {
                    if (b && b.vendorItemId != null) {
                        map[String(b.vendorItemId)] = {
                            parentVid: String(p.vendorItemId),
                            size: b.upBundlingSize || 1
                        };
                    }
                });
            });
            return map;
        },

        // 원가·이익을 보여줄 수 있나 (잠금이 없거나, 이 브라우저에서 풀었으면)
        canSeeProfit() {
            return !this.ledgerLocked || this.ledgerUnlocked;
        },

        // 매입가 없는 건 중 옵션이 몇 개인지 (상품 수는 본체 기준이라 따로 알려준다)
        missingOptionCount() {
            return (this.missingPriceList || []).reduce((s, m) => s + (Number(m.optionCount) || 0), 0);
        },

        // 조회 기간 일수 (상단 집계가 며칠치인지 표시)
        periodDayCount() {
            if (!this.startDate || !this.endDate) return 0;
            const s = new Date(this.startDate + 'T00:00:00');
            const e = new Date(this.endDate + 'T00:00:00');
            return Math.max(1, Math.round((e - s) / 86400000) + 1);
        },

        // 실제로 조회된 기간 일수 (리스크 판정 전용)
        queriedDayCount() {
            if (!this.queriedStartDate || !this.queriedEndDate) return 0;
            const s = new Date(this.queriedStartDate + 'T00:00:00');
            const e = new Date(this.queriedEndDate + 'T00:00:00');
            return Math.max(1, Math.round((e - s) / 86400000) + 1);
        },

        // ===== 기타비용을 얹은 표시용 데이터 =====
        // 이 두 computed 는 요약장부(getLedgerMonthlySummary)가 아니라 정산현황 원본
        // (/api/getSettlementData)으로 프론트가 직접 계산한 값을 다룬다. 서버가 기타비용을
        // 반영해 주는 쪽은 요약장부뿐이라, 여기서는 예전처럼 프론트가 얹어야 한다.
        // (여기를 서버값으로 바꾸면 일별·상품별 표에서 기타비용이 통째로 사라진다)
        //
        // calculateProductMetricsV3는 쿠팡 정산 데이터만 계산한다. 기타비용은 나중에 붙는 값이라
        // 원본 계산을 건드리지 않고 여기서 덧씌운다. 비용을 넣거나 지우면 자동으로 다시 계산된다.
        //
        // 부가세는 별도 컬럼을 만들지 않고 기존 '부가세' 컬럼에 공제분을 반영한다.
        // (수수료·배송비처럼 다른 컬럼도 부가세 포함 단일 컬럼이라 일관되게)
        //   부가세 = (쿠팡 기여이익 − 매입원가)/11 − 기타비용 중 '부가세 포함' 유형/11
        dailyRows() {
            if (!this.productCosts.length) return this.salesLedgerData;
            return this.salesLedgerData.map(row => {
                const list = this.costsOfDate(row.date);
                if (!list.length) return row;
                const cost = this.costSumOf(list);
                const refund = this.vatRefundOf(list);
                const contribution = this.num(row['일별기여이익']) - cost;
                const vat = this.num(row['일별부가세']) - refund;
                return {
                    ...row,
                    일별외부마케팅비: cost,
                    일별판매비용합계: this.num(row['일별판매비용합계']) + cost,
                    일별기여이익: contribution,
                    일별부가세: vat,
                    일별총수익: contribution - this.num(row['일별매입원가']) - vat
                };
            });
        },

        productRows() {
            if (!this.productCosts.length) return this.productViewData;
            return this.productViewData.map(row => {
                const list = this.costsOfProduct(row.vendorItemId);
                if (!list.length) return row;
                const cost = this.costSumOf(list);
                const refund = this.vatRefundOf(list);
                const contribution = this.num(row.contribution) - cost;
                const vat = this.num(row.vat) - refund;
                const netSales = this.num(row.netSales);
                // 매입가를 모르면 총이익도 모른다 — 0 원가로 계산하면 이익이 부풀어 보인다
                if (row.cogs === null || row.cogs === undefined) {
                    return {
                        ...row,
                        externalMarketing: cost,
                        sellingCosts: this.num(row.sellingCosts) + cost,
                        contribution
                    };
                }
                // 원래 총이익에서 비용만 빼고 공제분만 더한다. contribution − cogs − vat 로 다시 풀면
                // 매입가 없는 날의 기여이익까지 이익에 섞인다(그 날은 원가가 합산에 없다)
                const grossProfit = this.num(row.grossProfit) - cost + refund;
                return {
                    ...row,
                    externalMarketing: cost,
                    sellingCosts: this.num(row.sellingCosts) + cost,
                    contribution,
                    vat,
                    grossProfit,
                    grossMargin: netSales ? (grossProfit / netSales) * 100 : 0
                };
            });
        },

        // 상품별 표에 실제로 뿌리는 행 — 리스크 필터가 켜져 있으면 해당 상품만
        productTableRows() {
            if (!this.ledgerRiskFilter) return this.productRows;
            const map = this.ledgerRiskMap;
            return this.productRows.filter(row => (map[row.vendorItemId] || []).includes(this.ledgerRiskFilter));
        },

        // 조회 기간을 앞뒤 절반으로 나눌 수 있는지 (ROI/ROAS 하락 판정)
        ledgerRiskHalfReady() {
            return this.queriedDayCount >= 14;
        },

        // 상품(vendorItemId) → 리스크 키 배열. 기타비용을 얹기 전(productViewData) 기준
        ledgerRiskMap() {
            const map = {};
            (this.productViewData || []).forEach(item => {
                map[item.vendorItemId] = this.ledgerRiskFlags(item);
            });
            return map;
        },

        // 버튼 뱃지 — 화면 필터와 무관하게 조회된 전체 상품 기준
        ledgerRiskCounts() {
            const counts = {};
            Object.values(this.ledgerRiskMap).forEach(keys => {
                keys.forEach(k => { counts[k] = (counts[k] || 0) + 1; });
            });
            return counts;
        },

        // 전에 쓴 비용 분류 (자동완성용) — 고정비·직접비 양쪽에서 모은다
        knownCostCategories() {
            const set = [];
            this.productCosts.forEach(c => {
                const v = (c.category || '').trim();
                if (v && !set.includes(v)) set.push(v);
            });
            this.fixedCosts.forEach(f => (f.items || []).forEach(it => {
                const v = (it.category || '').trim();
                if (v && !set.includes(v)) set.push(v);
            }));
            return set.sort();
        },

        // 모달의 고정 컬럼 결정
        modalFixedColumns() {
            if (this.isProductView) {
                // 상품별 보기에서 일별 상세 모달: 날짜 고정
                return 'date';
            } else {
                // 일별 보기에서 상품별 상세 모달: 상품명 고정
                return '상품명';
            }
        },

        // 오늘자 정산현황 수집 상태 (growthScm의 todayCollectionStatus와 동일)
        settlementCollectionStatus() {
            const today = this.getTodayString();
            const isCollected = !this.todaySettlementNeeded;

            return {
                isCollected: isCollected,
                buttonText: isCollected ? '정산현황수집완료' : '정산현황수집',
                tooltip: isCollected
                    ? `오늘(${today}) 정산현황이 수집되었습니다.`
                    : `오늘(${today}) 정산현황을 수집해주세요!`
            };
        },

        // 월별조회 드롭다운 목록 (최근 12개월, 한국시간 기준)
        monthOptions() {
            const todayStr = this.getTodayString();
            const base = new Date(todayStr + 'T00:00:00');
            const list = [];
            for (let i = 0; i < 12; i++) {
                const d = new Date(base.getFullYear(), base.getMonth() - i, 1);
                const y = d.getFullYear();
                const mo = d.getMonth() + 1;
                list.push({
                    value: `${y}-${String(mo).padStart(2, '0')}`,
                    label: `${y}년 ${mo}월`,
                    year: y,
                    month: mo
                });
            }
            return list;
        }
    },
    async mounted() {
        // 기본 조회 기간 설정 (어제까지 30일, 오늘 데이터 제외) - 한국시간 기준
        const todayStr = this.getTodayString(); // 한국시간 기준 오늘
        const dates = this.generateDatesFromUpdateDate(todayStr); // 어제부터 30일 전까지

        this.startDate = dates[0]; // 30일 전
        this.endDate = dates[dates.length - 1]; // 어제

        console.log(`판매장부 기본 조회 기간 (한국시간): ${this.startDate} ~ ${this.endDate}`);

        // 메시지 이벤트 핸들러 등록 (growthScm 방식 참고)
        // 확장프로그램 자동수집은 알람으로 아무 때나 도므로, 아래 초기 로딩(await)보다
        // 먼저 등록해야 로딩 중에 도착한 수집 완료 신호를 놓치지 않는다.
        this.messageEventHandler = async (event) => {
            // 정산현황수집 응답 처리 (Extension에서 직접 서버 저장 후 완료 시그널)
            if (event.data.msg && event.data.msg === 'settlementData') {
                // 에러 메시지 체크 (res가 에러 메시지 문자열인 경우)
                if (typeof event.data.res === 'string' &&
                    event.data.res !== 'success' &&
                    event.data.res !== 'settlementData') {
                    alert(event.data.res);
                    this.showspinner = false;
                    return;
                }

                console.log('정산현황 수집+저장 완료:', event.data.count, '건');
                this.loadingtext = '정산현황 수집 완료!';

                // 정산현황 수집 완료 후 상태 업데이트
                this.todaySettlementNeeded = false;

                // 서버에서 데이터 reload
                setTimeout(() => this.afterCollect(), 1000);
            }

            // 진행 신호가 오는 동안은 수집이 살아있다는 뜻 — 타임아웃 타이머를 다시 건다.
            // (안 걸면 15분 넘는 수집에서 진행 표시가 중간에 멈춘다)
            if (event.data.res === 'growthdbratio' || event.data.type === 'autoCollectStatus') {
                this.touchOwnCollect();
            }
            // 진행률 업데이트 (Extension에서 전송하는 수집/저장 진행 상태)
            if (this.ownCollectRunning && event.data.res && event.data.res === 'growthdbratio' && event.data.ratio !== undefined) {
                this.showspinner = true;
                if (event.data.ratio === 100) {
                    this.loadingtext = event.data.text || '정산현황 수집 완료';
                } else {
                    this.loadingtext = (event.data.text || '정산현황 수집 중') + ' ' + event.data.ratio.toFixed(0) + '%';
                }
            }
            // [수집로직 일원화] 수집 완료 시 데이터 재조회
            if (event.data.type === 'manualCollectComplete' && event.data.itemKey === 'rocketGrowthSalesLedger') {
                this.endOwnCollection();
                this.showspinner = false;
                this.showlist = true;
                if (event.data.success === false) {
                    alert(event.data.error || '판매장부 수집에 실패했습니다.');
                    return;
                }
                this.todaySettlementNeeded = false;
                this.afterCollect();
            }
            // [수집로직 일원화] 진행 상태 표시
            // 진행 메시지는 itemKey 가 없다 — 내가 건 수집일 때만 반영한다
            if (this.ownCollectRunning && event.data.type === 'autoCollectStatus') {
                this.showspinner = true;
                this.loadingtext = event.data.message;
            }
        };

        // 리스너 부착은 activated가 담당한다 (keep-alive로 캐시된 뒤 백그라운드에서
        // 다른 화면의 수집 메시지를 받지 않게 하려면, 보이는 동안만 듣고 있어야 한다)
        this.bindExtensionMessages();

        // 초기 로딩 — 상품DB를 확보할 때까지 조회/수집 버튼 잠금
        // (상품DB 없이 조회하면 vendorItemId 매칭이 전부 실패해서 모든 값이 0으로 나옴)
        this.showspinner = true;
        this.loadingtext = '판매장부 초기 데이터 불러오는 중...';
        try {
            // 잠금 상태부터 확인한다(30ms). 잠겨 있으면 화면에 안 나올 데이터를
            // 900KB나 받아올 이유가 없어서, 뒤이어 뭘 부를지가 이 답에 달렸다
            await this.loadLedgerLock();

            // 초기 정산현황 상태 확인 — 수집 버튼 색/문구용이라 잠겨도 필요하다
            await this.checkInitialSettlementStatus();

            if (this.canSeeProfit) {
                this.loadMemos();

                // 손익장부 부가 데이터 (고정비·직접비·기준현금·재고스냅샷·입고이력)
                // 프라미스를 보관해 뒀다가 아래 재고 스냅샷 단계에서 기다린다 (같은 응답을 재활용)
                this.ledgerExtrasPromise = this.loadLedgerExtras();

                // 요약장부 — 연 단위라 조회기간과 무관하게 따로 불러온다
                this.loadLedgerYears().then(() => this.loadMonthlySummary(this.ledgerYear));
            } else {
                // 잠긴 화면에 남는 건 매입가 안내뿐이고, 그 목록이 여기서 온다
                this.loadMonthlySummary(this.ledgerYear);
            }

            // growthDB 로드 — 프라미스를 보관해서 search()가 기다릴 수 있게 함
            this.growthDbPromise = this.ensureGrowthDB();
            const dbStatus = await this.growthDbPromise;
            if (dbStatus === 'empty') return; // 상품DB 수집 페이지로 이동됨

            // 재고 스냅샷 로드 (growthScm이 저장한 재고커버리지 → vendorItemId 맵)
            if (this.canSeeProfit) await this.loadInventoryCoverage();
        } catch (e) {
            console.error('판매장부 초기 로딩 실패', e);
        } finally {
            // 어느 단계에서 실패하든 화면이 잠긴 채로 남지 않게 한다
            this.showspinner = false;
        }

        // 테이블 클릭 이벤트 설정
        this.setupTableClickEvents();

        // 상품DB 준비 완료 → 바로 조회 (사용자가 빈 화면에서 조회를 누를 필요 없음).
        // 잠겨 있으면 표가 안 보이므로 한 달치를 받아올 이유도 없다 — 풀 때 그때 받는다
        if (this.canSeeProfit) await this.search();
    },

    watch: {
        // 비용을 넣거나 지우면 상단 집계 카드도 다시 계산한다 (표는 computed라 자동 반영됨)
        productCosts() {
            if (this.salesLedgerData.length) this.calculateSummary();
        },

        // 데이터가 변경되면 클릭 이벤트 재설정
        salesLedgerData() {
            this.$nextTick(() => {
                this.setupTableClickEvents();
            });
        },
        productViewData() {
            this.$nextTick(() => {
                this.setupTableClickEvents();
            });
        },
        isProductView() {
            this.$nextTick(() => {
                this.setupTableClickEvents();
            });
        }
    },
    methods: {
        // 장부 원본(정산현황·상품DB·비용)이 마지막으로 바뀐 시각.
        // 한 계정을 여럿이 보는데 그중 한 명만 수집을 돌리면 나머지는 옛 캐시를 계속 봤다.
        // 실패하면 null 을 돌려주고, 그때는 캐시를 쓰지도 담지도 않는다 (낡은 숫자보다 한 번 더 받는 게 낫다)
        async fetchLedgerStamp() {
            try {
                const res = await axios.post('/api/growth/getLedgerStamp', {});
                if (res.data?.res !== 'success') return null;
                return res.data.stamp || '';
            } catch (e) {
                console.error('[판매장부] 원본 변경시각 확인 실패', e);
                return null;
            }
        },

        // 로켓그로스 상품DB를 store에 확보 (mounted / search 공용)
        // 반환: 'ok' = 사용 가능 / 'empty' = 수집 필요(상품DB 페이지로 이동) / 'error' = 조회 실패
        async ensureGrowthDB() {
            // store 에 목록이 있다는 것만으로 쓰면, 다른 PC·탭에서 새로 수집한 상품DB 가 안 보여
            // 새 옵션 매출이 「상품DB에 없는 옵션」으로 빠졌다. 받은 뒤 원본이 바뀌었으면 다시 받는다
            const cached = this.$store.state.growthdb;
            const stamp = await this.fetchLedgerStamp();
            if (cached && cached.length > 0 && stamp !== null &&
                growthDbLoaded.list === cached && growthDbLoaded.stamp === stamp) return 'ok';
            try {
                const result = await axios.post('/api/getGrowthDBV2');
                const growthdb = result.data?.growthdb;
                if (!growthdb || growthdb.length === 0) {
                    alert('로켓그로스 판매장부를 이용하기전에 로켓그로스 상품DB를 먼저 수집해주세요')
                    this.$router.push({ name: 'growth.growthdb' })
                    return 'empty';
                }
                this.$store.commit('growthdbupdate', growthdb);
                growthDbLoaded.list = this.$store.state.growthdb;
                growthDbLoaded.stamp = stamp;
                return 'ok';
            } catch (e) {
                console.error('로켓그로스 상품DB 불러오기 실패', e);
                // 다시 받기만 실패했으면 들고 있던 목록으로 계속 본다
                if (cached && cached.length > 0) return 'ok';
                return 'error';
            }
        },
        // ===== 손익장부: 비용 / 기준현금 / 재고 역산 데이터 =====
        // 조회 기간과 무관하게 전체를 받는다 — 요약장부는 기간 비교가 목적이고,
        // 자산현황은 스냅샷에서 과거로 되감아야 해서 구간을 자를 수 없다
        // 「확인 안 된 재고」 — 공급관리(SCM)와 같은 서버 계산의 요약만 받는다
        async loadReconSummary() {
            try {
                const res = await axios.post('/api/growth/getInventoryRecon', { summaryOnly: true });
                this.reconSummary = res.data?.res === 'success' ? (res.data.summary || null) : null;
                // forbidden = 볼 권한 없음(오류 아님)
                if (res.data?.res !== 'success' && res.data?.res !== 'forbidden') console.error('[판매장부] 확인 안 된 재고 조회 실패', res.data?.alert);
            } catch (e) {
                console.error('[판매장부] 확인 안 된 재고 조회 실패', e);
            }
        },
        // 「확인 안 된 재고」 칸을 누르면 공급관리(SCM)의 미확인 목록으로
        openReconInScm() {
            this.$router.push({ name: 'growth.growthscmV0', query: { recon: 'all' } }).catch(e => {
                console.error('[판매장부] 공급관리 이동 실패', e);
            });
        },
        async loadLedgerExtras() {
            try {
                const [fixedRes, costRes, settingRes, coverageRes, inboundRes, scheduleRes] = await Promise.all([
                    axios.post('/api/growth/getFixedCosts', {}),
                    axios.post('/api/growth/getSalesLedgerCosts', {}),
                    axios.post('/api/growth/getLedgerSetting', {}),
                    axios.post('/api/getInventoryCoverage', {}),
                    axios.post('/api/growth/getInboundHistory', {}),
                    axios.post('/api/growth/getSettlementSchedules', {})
                ]);

                this.fixedCosts = fixedRes.data?.res === 'success' ? (fixedRes.data.fixedCosts || []) : [];
                this.productCosts = costRes.data?.res === 'success' ? (costRes.data.costs || []) : [];
                this.ledgerSetting = settingRes.data?.res === 'success' ? settingRes.data.setting : null;
                this.inventoryCoverage = coverageRes.data?.success ? (coverageRes.data.coverage || []) : [];
                // 같은 응답으로 vendorItemId 맵까지 만든다 (예전엔 같은 600KB를 한 번 더 받았다)
                this.buildInventoryCoverageMap(this.inventoryCoverage);
                this.inboundHistory = inboundRes.data?.res === 'success' ? (inboundRes.data.inbound || []) : [];
                this.settlementSchedules = scheduleRes.data?.res === 'success' ? (scheduleRes.data.schedules || []) : [];
                // 확인 안 된 재고는 따로 받는다 — 큰 회사는 몇 초 걸려 위 묶음을 붙잡지 않게 기다리지 않는다
                this.loadReconSummary();

                console.log('[손익장부] 고정비', this.fixedCosts.length,
                    '/ 상품직접비', this.productCosts.length,
                    '/ 재고스냅샷', this.inventoryCoverage.length,
                    '/ 입고이력', this.inboundHistory.length,
                    '/ 정산일정', this.settlementSchedules.length);
            } catch (e) {
                console.error('[손익장부] 부가 데이터 로딩 실패', e);
            }
        },

        // 수집이 끝난 뒤 화면 전체를 최신으로 맞춘다.
        // 수집 완료 신호가 두 경로(settlementData / manualCollectComplete)로 오는데
        // 한쪽만 갱신하면 입고이력·요약장부가 낡은 채로 남는다
        async afterCollect() {
            // 수집으로 원본이 바뀌었으니 받아둔 정산현황을 버린다.
            // 바뀐 건 이 계정 것뿐이라 이 계정 것만 버린다 (clearAll 이면 옆 계정 것까지 날아간다)
            ledgerCache.clearScope(this.$store.state.user?._id);
            try {
                await this.search();
                await this.loadLedgerExtras();          // 입고이력·재고·비용
                await this.loadLedgerYears();
                await this.loadMonthlySummary(this.ledgerYear, { force: true });
                this.lastLoadedAt = Date.now();
            } catch (e) {
                console.error('[판매장부] 수집 후 갱신 실패', e);
            }
        },

        // 탭을 오래 비워뒀다가 돌아왔을 때 (keepAliveViewMixin이 호출).
        // mixin 기본 구현은 search()만 다시 부르는데, 그 사이 수집이 돌았을 수 있으므로
        // 요약장부와 비용·재고 데이터도 함께 최신으로 맞춘다
        async refreshIfStale() {
            try {
                if (!this.canSeeProfit) {
                    await this.loadMonthlySummary(this.ledgerYear);   // 매입가 안내만
                    this.lastLoadedAt = Date.now();
                    return;
                }
                await this.search();
                this.loadLedgerExtras();
                await this.loadMonthlySummary(this.ledgerYear);
                this.lastLoadedAt = Date.now();
            } catch (e) {
                console.error('[판매장부] 탭 복귀 재조회 실패', e);
            }
        },

        // ===== 판매장부 잠금 =====
        // 해제 상태는 서버 세션이 갖고 있다. 화면은 그 답만 따른다 —
        // 브라우저에 두면 직원이 값을 고쳐 열 수 있고, 잠긴 세션에는 서버가 손익 숫자를 아예 안 준다.
        async loadLedgerLock() {
            try {
                const res = await axios.post('/api/growth/getLedgerLock', {});
                if (res.data?.res !== 'success') return;
                this.ledgerLocked = !!res.data.locked;
                this.ledgerUnlocked = !!res.data.unlocked;

                if (this.ledgerLocked && !this.ledgerUnlocked) {
                    // 잠긴 동안은 예전에 받아둔 숫자도 남아 있으면 안 된다.
                    // 잠금은 계정별이므로 이 계정 것만 버린다
                    await ledgerCache.clearScope(this.$store.state.user?._id);
                    this.monthlySummary = [];
                    this.dailySummary = [];
                }
            } catch (e) {
                console.error('[판매장부] 잠금 상태 확인 실패', e);
            }
        },

        async submitUnlock() {
            this.lockError = '';
            try {
                const res = await axios.post('/api/growth/unlockLedger', { pw: this.lockInput });
                if (res.data?.res !== 'success' || !res.data.ok) {
                    this.lockError = '비밀번호가 맞지 않습니다.';
                    return;
                }
                this.ledgerUnlocked = true;
                this.showLockModal = false;
                this.lockInput = '';
                // 잠겨 있어 건너뛴 데이터를 지금 채운다
                this.loadMemos();
                this.ledgerExtrasPromise = this.loadLedgerExtras();
                await this.loadLedgerYears();
                await this.loadMonthlySummary(this.ledgerYear, { force: true });
                await this.loadInventoryCoverage();
                await this.search();
            } catch (e) {
                console.error('[판매장부] 잠금 해제 실패', e);
                this.lockError = '확인 중 오류가 발생했습니다.';
            }
        },

        async submitLockSetting(remove = false) {
            this.lockError = '';
            try {
                const res = await axios.post('/api/growth/setLedgerLock', {
                    currentPw: this.lockCurrentPw,
                    newPw: this.lockNewPw,
                    remove
                });
                if (res.data?.res !== 'success') {
                    this.lockError = res.data?.alert || '설정에 실패했습니다.';
                    return;
                }
                this.ledgerLocked = !!res.data.locked;
                // 방금 설정한 사람은 계속 볼 수 있다 (서버가 그 세션을 풀어둔다)
                this.ledgerUnlocked = true;
                this.showLockSetModal = false;
                this.lockNewPw = '';
                this.lockCurrentPw = '';
            } catch (e) {
                console.error('[판매장부] 잠금 설정 실패', e);
                this.lockError = '설정 중 오류가 발생했습니다.';
            }
        },

        // ===== 요약장부: 연 단위 월별 집계 =====
        // 원본 정산현황은 큰 회사 기준 올해만 11만 건(50MB)이라 그대로 못 내리고,
        // 서버가 DB 안에서 집계해 월 12행 + 일 365행으로 줄여 준다.
        //
        // 집계가 1~3초 걸리는데(상품 수에 비례) 화면을 오갈 때마다 다시 부르면 답답하다.
        // 결과를 IndexedDB에 담아 그동안은 즉시 띄운다.
        // 수집이 끝나면 afterCollect 가 캐시를 비우므로 낡은 값이 남지 않는다.
        async loadMonthlySummary(year, { force = false } = {}) {
            const y = String(year || this.ledgerYear);
            // 캐시는 계정(_id) 단위로 나눈다. companyid 는 같은 문자열을 쓰는 다른 계정이 실제로 있다
            const scope = this.$store.state.user?._id || '';
            const cacheKey = 'summary-' + y;
            // 남이 수집을 돌렸으면 내 캐시는 이미 낡았다. 서버가 준 변경시각으로 판단한다
            const stamp = await this.fetchLedgerStamp();

            const apply = data => {
                this.monthlySummary = data.months || [];
                this.dailySummary = data.days || [];
                this.missingPriceCount = data.missingPurchasePriceCount || 0;
                this.missingPriceAmount = data.missingPurchasePriceAmount || 0;
                this.missingPriceList = data.missingPurchasePrice || [];
            };

            // 잠겨 있어도 호출은 한다 — 매입가 안내가 이 응답에서 오기 때문이다.
            // 대신 서버가 숫자를 빼고 주므로 캐시에는 담지 않는다
            if (!force && this.canSeeProfit && stamp !== null) {
                const cached = await ledgerCache.getMonth(scope, cacheKey, stamp);
                if (cached && Array.isArray(cached.months)) {
                    apply(cached);
                    console.log('[요약장부]', y, '캐시 사용');
                    return;
                }
            }

            this.loadingSummary = true;
            try {
                const res = await axios.post('/api/growth/getLedgerMonthlySummary', { year: y });
                // 잠긴 응답에는 손익이 빠져 있다. 매입가 안내만 받아 쓰고 캐시에는 담지 않는다
                if (res.data?.res === 'locked') {
                    this.ledgerLocked = true;
                    this.ledgerUnlocked = false;
                    apply(res.data);
                    return;
                }
                if (res.data?.res !== 'success') {
                    console.error('getLedgerMonthlySummary failed', res.data);
                    apply({});
                    return;
                }
                apply(res.data);
                if (stamp !== null) {
                    await ledgerCache.putMonth(scope, cacheKey, {
                        months: this.monthlySummary,
                        days: this.dailySummary,
                        missingPurchasePrice: this.missingPriceList,
                        missingPurchasePriceCount: this.missingPriceCount,
                        missingPurchasePriceAmount: this.missingPriceAmount
                    }, stamp);
                }
                console.log('[요약장부]', y, this.monthlySummary.length, '개월 /',
                    this.dailySummary.length, '일 / 매입가없음', this.missingPriceCount, '개');
            } catch (e) {
                console.error('[요약장부] 조회 실패', e);
                apply({});
            } finally {
                this.loadingSummary = false;
            }
        },

        // 매입가 없는 상품만 필터된 상태로 상품DB 화면으로.
        //
        // 단순히 '매입정보 없음'으로 거르면 안 팔린 상품까지 전부 나와서 일이 커진다.
        // 실제로 판매가 있었는데 매입가가 없는 것만, 그것도 금액 큰 순으로 넘긴다.
        // 목록이 길어 URL에 못 담으므로 sessionStorage로 전달한다
        goFillPurchasePrice() {
            try {
                sessionStorage.setItem('ledgerMissingPrice', JSON.stringify({
                    year: this.ledgerYear,
                    items: this.missingPriceList
                }));
            } catch (e) {
                console.warn('[손익장부] 매입가 목록 전달 실패 — 전체 미입력 상품을 보여줍니다', e.message);
            }
            this.$router.push({ name: 'growth.growthdb', query: { filter: 'noPurchasePrice' } });
        },

        // 상품DB에 없어 집계에서 빠진 판매 안내 → 상품관리(상품DB) 화면으로.
        // 지금까지 안내 문구만 있고 이동 수단이 없어서 사용자가 알림을 눌러도 아무 일도 일어나지 않았다.
        goCollectGrowthDB() {
            this.$router.push({ name: 'growth.growthdb' });
        },

        async loadLedgerYears() {
            try {
                const res = await axios.post('/api/growth/getLedgerYears', {});
                if (res.data?.res !== 'success') return;
                this.ledgerYears = res.data.years || [];
                // 올해 데이터가 없으면 가장 최근 해로
                if (this.ledgerYears.length && this.ledgerYears.indexOf(this.ledgerYear) === -1) {
                    this.ledgerYear = this.ledgerYears[0];
                }
            } catch (e) {
                console.error('[요약장부] 연도 목록 실패', e);
            }
        },

        changeLedgerYear(year) {
            this.ledgerYear = String(year);
            this.loadMonthlySummary(this.ledgerYear);
        },

        // 월별 추이 차트에서 월 클릭 → 그 달로 조회
        onMonthClick(month) {
            this.onPivotPeriodClick({ periodKey: month, unit: 'month' });
        },

        // 최근 N일 바로가기
        // 첫 진입 기본값과 같은 기준: 한국시간 어제까지 N일 (오늘 데이터 제외)
        selectRecentDays(days) {
            const end = new Date(this.getTodayString()); // KST 오늘 (UTC 자정)
            end.setUTCDate(end.getUTCDate() - 1); // 어제
            const start = new Date(end);
            start.setUTCDate(start.getUTCDate() - (days - 1));
            const fmt = d => d.toISOString().split('T')[0];
            this.startDate = fmt(start);
            this.endDate = fmt(end);
            this.search();
        },

        // 비용 입력에서 고를 상품 목록 (조회된 상품 기준)
        // 아직 판매가 없는 신상품도 넣을 수 있도록 상품DB 전체를 후보로 준다
        buildCostProductOptions() {
            const db = this.$store.state.growthdb || [];
            return db
                .filter(p => p.vendorItemId != null)
                .map(p => ({
                    value: String(p.vendorItemId),
                    text: (p.판매상품명 || p.상품명 || p.vendorItemId) + (p.옵션명 ? ' / ' + p.옵션명 : '')
                }));
        },

        // 이 상품의 조회기간 내 직접비 목록
        costsOfProduct(vendorItemId) {
            const vid = String(vendorItemId);
            return this.productCosts.filter(c =>
                String(c.vendorItemId) === vid &&
                (!this.startDate || c.date >= this.startDate) &&
                (!this.endDate || c.date <= this.endDate)
            );
        },

        // 이 날짜의 직접비 목록
        costsOfDate(date) {
            return this.productCosts.filter(c => c.date === date);
        },

        num(v) {
            const n = Number(v);
            return isNaN(n) ? 0 : n;
        },

        costSumOf(list) {
            return (list || []).reduce((s, c) => s + (Number(c.amount) || 0), 0);
        },

        // '부가세 포함'으로 등록한 비용만 매입세액으로 되돌린다.
        // 원천징수(3.3%)·세금없음은 부가세가 없으므로 공제도 없다
        vatRefundOf(list) {
            return (list || []).reduce(
                (s, c) => s + (c.tax === 'vat' ? Math.round((Number(c.amount) || 0) / 11) : 0), 0);
        },

        // 표에서는 합계만 보여주고, 내역은 툴팁으로 (입력·수정은 비용관리 한 곳에서)
        costTooltipOf(list) {
            if (!list || !list.length) return '등록된 비용이 없습니다 (비용관리에서 입력)';
            return list
                .map(c => `${c.date} · ${c.category || '분류없음'} ${Number(c.amount).toLocaleString()}원`)
                .join('\n');
        },

        // 상품 직접비 추가
        async addProductCost(payload) {
            try {
                const res = await axios.post('/api/growth/saveSalesLedgerCost', payload);
                if (res.data?.res !== 'success') {
                    console.error('saveSalesLedgerCost failed', res.data);
                    alert('비용 저장에 실패했습니다.');
                    return;
                }
                this.productCosts.push({ ...payload, _id: res.data._id });
                // 요약장부 숫자는 서버가 기타비용까지 반영해 내려준다. 비용을 넣었으면 다시 받아야 한다
                await this.loadMonthlySummary(this.ledgerYear, { force: true });
            } catch (e) {
                console.error('[손익장부] 직접비 저장 오류', e);
                alert('비용 저장 중 오류가 발생했습니다.');
            }
        },

        // 상품 직접비 삭제 (금액 0으로 보내면 서버가 지운다)
        async removeProductCost(entry) {
            try {
                const res = await axios.post('/api/growth/saveSalesLedgerCost', { _id: entry._id, amount: 0 });
                if (res.data?.res !== 'success') {
                    console.error('deleteSalesLedgerCost failed', res.data);
                    alert('비용 삭제에 실패했습니다.');
                    return;
                }
                const idx = this.productCosts.findIndex(c => String(c._id) === String(entry._id));
                if (idx > -1) this.productCosts.splice(idx, 1);
                // 지운 비용도 서버 집계에서 빠져야 하므로 요약장부를 다시 받는다
                await this.loadMonthlySummary(this.ledgerYear, { force: true });
            } catch (e) {
                console.error('[손익장부] 직접비 삭제 오류', e);
                alert('비용 삭제 중 오류가 발생했습니다.');
            }
        },

        // 고정비 저장 — 해당 월을 통째로 덮어쓴다
        async saveFixedCosts(payload) {
            this.savingFixedCost = true;
            try {
                const res = await axios.post('/api/growth/saveFixedCosts', payload);
                if (res.data?.res !== 'success') {
                    console.error('saveFixedCosts failed', res.data);
                    alert('비용 저장에 실패했습니다.');
                    return;
                }
                // 로컬 갱신 (재조회 없이 요약장부까지 즉시 반영)
                const idx = this.fixedCosts.findIndex(f => f.month === payload.month);
                if (!payload.items || payload.items.length === 0) {
                    if (idx > -1) this.fixedCosts.splice(idx, 1);
                } else if (idx > -1) {
                    this.$set(this.fixedCosts, idx, { ...this.fixedCosts[idx], items: payload.items });
                } else {
                    this.fixedCosts.push({ month: payload.month, items: payload.items });
                    this.fixedCosts.sort((a, b) => (a.month > b.month ? 1 : -1));
                }
            } catch (e) {
                console.error('[손익장부] 고정비 저장 오류', e);
                alert('비용 저장 중 오류가 발생했습니다.');
            } finally {
                this.savingFixedCost = false;
            }
        },

        // 기준현금 저장
        async saveLedgerSetting(payload) {
            try {
                const res = await axios.post('/api/growth/saveLedgerSetting', payload);
                if (res.data?.res !== 'success') {
                    console.error('saveLedgerSetting failed', res.data);
                    alert('기준현금 저장에 실패했습니다.');
                    return;
                }
                this.ledgerSetting = { ...payload };
            } catch (e) {
                console.error('[손익장부] 기준현금 저장 오류', e);
                alert('기준현금 저장 중 오류가 발생했습니다.');
            }
        },

        // 요약장부에서 기간 셀 클릭 → 그 기간으로 조회 후 일별 탭으로
        onPivotPeriodClick({ periodKey, unit }) {
            const range = this.periodToRange(periodKey, unit);
            if (!range) return;
            this.startDate = range.start;
            this.endDate = range.end;
            this.activeTab = 'ledger';
            this.search();
        },

        // 기간 키 → 날짜 범위
        periodToRange(key, unit) {
            const pad = n => String(n).padStart(2, '0');
            const lastDay = (y, m) => new Date(y, m, 0).getDate();
            if (unit === 'year') {
                return { start: key + '-01-01', end: key + '-12-31' };
            }
            const y = parseInt(key.slice(0, 4), 10);
            if (unit === 'half') {
                const h = key.endsWith('H1') ? 1 : 2;
                const sm = h === 1 ? 1 : 7;
                const em = h === 1 ? 6 : 12;
                return { start: `${y}-${pad(sm)}-01`, end: `${y}-${pad(em)}-${lastDay(y, em)}` };
            }
            if (unit === 'quarter') {
                const q = parseInt(key.slice(6), 10);
                const sm = (q - 1) * 3 + 1;
                const em = sm + 2;
                return { start: `${y}-${pad(sm)}-01`, end: `${y}-${pad(em)}-${lastDay(y, em)}` };
            }
            const m = parseInt(key.slice(5, 7), 10);
            return { start: `${key}-01`, end: `${key}-${pad(lastDay(y, m))}` };
        },

        // ===== 판매장부 메모 =====
        async loadMemos() {
            try {
                const res = await axios.post('/api/growth/getSalesLedgerMemos')
                if (res.data?.res !== 'success') return
                const next = { date: {}, product: {}, date_product: {} }
                for (const m of (res.data.memos || [])) {
                    if (m.scope === 'date' && m.date) {
                        next.date[m.date] = m.content
                    } else if (m.scope === 'product' && m.vendorItemId) {
                        next.product[String(m.vendorItemId)] = m.content
                    } else if (m.scope === 'date_product' && m.date && m.vendorItemId) {
                        next.date_product[`${m.date}|${m.vendorItemId}`] = m.content
                    }
                }
                this.memos = next
            } catch (e) {
                console.error('loadMemos failed', e)
            }
        },
        // item에서 어떤 scope인지 자동 판별 (헤더 컬럼에 따라 date/vendorItemId 존재 여부 다름)
        memoScopeFor(item) {
            const hasDate = !!item.date
            const hasVendorItemId = item.vendorItemId != null && item.vendorItemId !== ''
            if (hasDate && hasVendorItemId) return 'date_product'
            if (hasVendorItemId) return 'product'
            if (hasDate) return 'date'
            return null
        },
        getMemo(item) {
            const scope = this.memoScopeFor(item)
            if (!scope) return ''
            if (scope === 'date') return this.memos.date[item.date] || ''
            if (scope === 'product') return this.memos.product[String(item.vendorItemId)] || ''
            return this.memos.date_product[`${item.date}|${item.vendorItemId}`] || ''
        },
        async saveMemo(item, content) {
            const scope = this.memoScopeFor(item)
            if (!scope) return
            const payload = {
                scope,
                date: (scope === 'date' || scope === 'date_product') ? item.date : null,
                vendorItemId: (scope === 'product' || scope === 'date_product') ? String(item.vendorItemId) : null,
                content
            }
            try {
                const res = await axios.post('/api/growth/saveSalesLedgerMemo', payload)
                if (res.data?.res !== 'success') {
                    console.error('saveMemo failed', res.data)
                    return
                }
                // 로컬 상태 갱신 (reactive)
                const trimmed = (content || '').trim()
                if (scope === 'date') {
                    if (trimmed) this.$set(this.memos.date, item.date, trimmed)
                    else this.$delete(this.memos.date, item.date)
                } else if (scope === 'product') {
                    const k = String(item.vendorItemId)
                    if (trimmed) this.$set(this.memos.product, k, trimmed)
                    else this.$delete(this.memos.product, k)
                } else {
                    const k = `${item.date}|${item.vendorItemId}`
                    if (trimmed) this.$set(this.memos.date_product, k, trimmed)
                    else this.$delete(this.memos.date_product, k)
                }
            } catch (e) {
                console.error('saveMemo error', e)
            }
        },
        // 월별조회: 선택한 월의 1일~말일로 세팅 후 조회 (말일이 오늘을 넘으면 오늘로 자름)
        selectMonth(year, month) {
            const mm = String(month).padStart(2, '0');
            const first = `${year}-${mm}-01`;
            const lastDay = new Date(year, month, 0).getDate();
            let last = `${year}-${mm}-${String(lastDay).padStart(2, '0')}`;

            // 종료일이 오늘(한국시간)을 넘으면 오늘로 캡핑
            const todayStr = this.getTodayString();
            if (last > todayStr) {
                last = todayStr;
            }

            this.startDate = first;
            this.endDate = last;
            this.search();
        },
        // 조회 실행 (정산현황 데이터 기반으로 수정)
        async search() {
            if (!this.startDate || !this.endDate) {
                alert('조회 기간을 선택해주세요.');
                return;
            }

            if (this.startDate > this.endDate) {
                alert('시작일이 종료일보다 늦을 수 없습니다.');
                return;
            }

            this.showlist = false;
            this.showspinner = true;
            this.loadingtext = '판매장부 데이터 조회 중...';

            try {
                console.log('=== 판매장부 조회 시작 ===');
                console.log('조회 기간:', this.startDate, '~', this.endDate);

                // 1. growthdb 데이터 확인 — mounted에서 로딩 중이면 그 프라미스를 기다린다.
                //    (상품DB 없이 계산하면 vendorItemId 매칭이 전부 실패해서 조용히 0건이 됨)
                this.loadingtext = '로켓그로스 상품DB 확인 중...';
                //    조회할 때마다 다시 확인한다 — 화면을 연 뒤 다른 곳에서 상품DB 가 바뀌었을 수 있다
                await this.growthDbPromise;
                this.growthDbPromise = this.ensureGrowthDB();
                const dbStatus = await this.growthDbPromise;
                if (dbStatus !== 'ok') {
                    alert('로켓그로스 상품DB를 불러오지 못했습니다. 잠시 후 다시 조회해주세요.');
                    return;
                }

                // 2. 정산현황 데이터 조회 (adCost가 이미 포함되어 있음)
                //    요약장부에서 월을 이리저리 눌러보면 같은 달을 반복해서 내려받게 된다.
                //    한 달치가 수천 건이라(압축 미들웨어도 없음) 받아둔 건 IndexedDB에서 재사용한다
                this.loadingtext = '정산현황 데이터 조회 중...';
                const scope = this.$store.state.user?._id || '';
                const rangeKey = `range-${this.startDate}~${this.endDate}`;
                const stamp = await this.fetchLedgerStamp();

                let settlementData = stamp === null ? null : await ledgerCache.getMonth(scope, rangeKey, stamp);
                if (Array.isArray(settlementData)) {
                    console.log('[판매장부] 정산현황 캐시 사용', settlementData.length, '건');
                } else {
                    const settlementResult = await axios.post('/api/getSettlementData', {
                        startDate: this.startDate,
                        endDate: this.endDate
                    });
                    if (!settlementResult.data.success) {
                        alert(settlementResult.data.message || '정산현황 데이터 조회에 실패했습니다.');
                        return;
                    }
                    settlementData = settlementResult.data.data || [];
                    console.log('[판매장부] 정산현황 서버 조회', settlementData.length, '건');
                    if (stamp !== null) await ledgerCache.putMonth(scope, rangeKey, settlementData, stamp);
                }

                // 3. lot FIFO 원가 전용 — **조회기간 앞 판매수량**
                //    FIFO 는 앞에서부터 깎는 계산이라 기간 앞 판매를 모르면 lot 이 덜 깎인 채로
                //    시작해 더 싼(앞) lot 단가가 붙는다. 서버(buildLedgerSummary)는 이미 앞 판매를
                //    전부 넣고 있어서, 이걸 안 받으면 같은 상품 매입원가가 대시보드와 갈린다
                //    (실측 최근90일 rubycoco 총이익 203만원 차 / ROI 4%p).
                //    lot 보유 상품만 내려오므로 큰 회사도 최대 3만 행이다. 실패해도 조회는 계속한다
                //    — 앞 판매가 없으면 예전과 같은 값이 나올 뿐이고, 여기서 멈추면 장부가 통째로 안 뜬다.
                const priorKey = `prior-${this.startDate}`;
                let priorSales = stamp === null ? null : await ledgerCache.getMonth(scope, priorKey, stamp);
                if (!Array.isArray(priorSales)) {
                    try {
                        const priorResult = await axios.post('/api/growth/getPriorSalesQty', {
                            startDate: this.startDate
                        });
                        priorSales = priorResult.data.success ? (priorResult.data.data || []) : [];
                        console.log('[판매장부] 기간 앞 판매수량', priorSales.length, '행');
                        if (stamp !== null) await ledgerCache.putMonth(scope, priorKey, priorSales, stamp);
                    } catch (e) {
                        console.error('[판매장부] 기간 앞 판매수량 조회 실패 - lot 단가가 대시보드와 갈릴 수 있음', e);
                        priorSales = [];
                    }
                }

                // 날짜 조회 시에는 버튼 상태를 변경하지 않음 (mounted와 수집 완료 시에만 변경)
                this.queriedStartDate = this.startDate;
                this.queriedEndDate = this.endDate;
                this.processSalesLedgerDataV3(settlementData, priorSales);
                this.showlist = true;
            } catch (error) {
                console.error('판매장부 조회 오류:', error);
                alert('데이터 조회 중 오류가 발생했습니다.');
            } finally {
                this.showspinner = false;
            }
        },

        // 초기 정산현황 상태 확인 (mounted에서 호출)
        async checkInitialSettlementStatus() {
            try {
                const today = this.getTodayString();
                const todayStr = today;
                const yesterdayStr = this.generateDatesFromUpdateDate(todayStr)[29]; // 마지막 날짜가 어제

                console.log('=== 초기 정산현황 상태 확인 ===');
                console.log('오늘 날짜:', today);
                console.log('어제 날짜:', yesterdayStr);

                // 어제자 정산현황 데이터만 확인 (실제 수집은 어제까지 하므로)
                const result = await axios.post('/api/getSettlementData', {
                    startDate: yesterdayStr,
                    endDate: yesterdayStr
                });

                if (result.data.success) {
                    const yesterdayData = result.data.data || [];
                    if (yesterdayData.length > 0) {
                        console.log('어제자 정산현황 존재 - 수집 완료');
                        this.todaySettlementNeeded = false;
                    } else {
                        console.log('어제자 정산현황 없음 - 수집 필요');
                        this.todaySettlementNeeded = true;
                    }
                } else {
                    console.log('정산현황 확인 실패 - 수집 필요로 설정');
                    this.todaySettlementNeeded = true;
                }

                console.log('초기 todaySettlementNeeded:', this.todaySettlementNeeded);
                console.log('===============================');
            } catch (error) {
                console.error('초기 정산현황 상태 확인 오류:', error);
                this.todaySettlementNeeded = true; // 오류 시 수집 필요로 설정
            }
        },

        // 오늘자 정산현황 수집 상태 확인 (growthScm과 동일한 로직)
        checkTodaySettlementStatus(settlementData) {
            const today = this.getTodayString();
            const todayStr = today;
            const yesterdayStr = this.generateDatesFromUpdateDate(todayStr)[29]; // 마지막 날짜가 어제

            console.log('=== 오늘자 정산현황 확인 ===');
            console.log('오늘 날짜:', today);
            console.log('어제 날짜:', yesterdayStr);

            // 정산현황 데이터에서 어제자 데이터 확인 (실제 수집은 어제까지 하므로)
            // settlementData의 날짜 필드는 revenueDate입니다
            const yesterdaySettlement = settlementData.find(item => item.revenueDate === yesterdayStr);

            if (yesterdaySettlement) {
                console.log('어제자 정산현황 발견:', yesterdaySettlement);
                this.todaySettlementNeeded = false; // 수집 완료
            } else {
                console.log('어제자 정산현황 없음 - 수집 필요');
                this.todaySettlementNeeded = true; // 수집 필요

                // Toast 메시지 표시
                this.$bvToast.toast('오늘자 정산현황을 수집해주세요!', {
                    title: '데이터 수집 필요',
                    variant: 'warning',
                    solid: true,
                    autoHideDelay: 3000
                });
            }

            console.log('todaySettlementNeeded:', this.todaySettlementNeeded);
            console.log('===========================');
        },

        // 판매장부 데이터 처리 V3 (일별 집계 방식, adCost 포함)
        // priorSales = 조회기간 앞 판매수량(lot FIFO 전용). 안 넘어오면 예전 동작 그대로다
        processSalesLedgerDataV3(settlementData, priorSales) {
            console.log('=== processSalesLedgerDataV3 시작 ===');
            console.log('정산현황 데이터:', settlementData.length, '개');

            const growthDB = this.$store.state.growthdb || [];
            console.log('growthDB:', growthDB.length, '개');

            // 상품DB가 비어 있으면 vendorItemId 매칭이 전부 실패해서 모든 값이 0이 된다.
            // 조용히 0을 보여주지 않고 중단 (데이터 없음과 상품DB 없음을 구분)
            if (growthDB.length === 0) {
                console.error('processSalesLedgerDataV3: 상품DB가 비어 있어 계산 중단');
                this.salesLedgerData = [];
                alert('로켓그로스 상품DB가 없어 조회할 수 없습니다. 상품DB를 먼저 수집해주세요.');
                return;
            }

            // growthDB의 vendorItemId만 추출 (returnProducts도 포함, 타입 변환 포함)
            const validVendorItemIds = new Set();
            growthDB.forEach(product => {
                // 숫자와 문자열 둘 다 추가
                validVendorItemIds.add(product.vendorItemId);
                validVendorItemIds.add(String(product.vendorItemId));

                // returnProducts의 vendorItemId도 추가
                if (product.returnProducts && Array.isArray(product.returnProducts)) {
                    product.returnProducts.forEach(rp => {
                        if (rp.vendorItemId) {
                            validVendorItemIds.add(rp.vendorItemId);
                            validVendorItemIds.add(String(rp.vendorItemId));
                        }
                    });
                }

                if (product.upBundlingProducts && Array.isArray(product.upBundlingProducts)) {
                    product.upBundlingProducts.forEach(bundle => {
                        if (bundle.vendorItemId) {
                            validVendorItemIds.add(bundle.vendorItemId);
                            validVendorItemIds.add(String(bundle.vendorItemId));
                        }
                    });
                }

            });

            console.log('유효한 vendorItemId 개수:', validVendorItemIds.size);
            console.log('조회 기간:', this.startDate, '~', this.endDate);

            // 샘플 데이터 확인
            if (settlementData.length > 0) {
                console.log('정산 데이터 샘플:', settlementData[0]);
                console.log('첫 번째 정산 데이터의 vendorItemId:', settlementData[0].vendorItemId);
                console.log('첫 번째 정산 데이터의 revenueDate:', settlementData[0].revenueDate);
                console.log('vendorItemId 매칭 여부:', validVendorItemIds.has(settlementData[0].vendorItemId));
                console.log('날짜 범위 체크:', settlementData[0].revenueDate >= this.startDate && settlementData[0].revenueDate <= this.endDate);
            }

            // 유효한 정산 데이터만 필터링
            const validSettlementData = settlementData.filter(item =>
                validVendorItemIds.has(item.vendorItemId) &&
                item.revenueDate >= this.startDate &&
                item.revenueDate <= this.endDate
            );

            console.log('유효한 정산 데이터:', validSettlementData.length, '개');

            // 상품DB에 없어서 위 필터에서 빠진 판매를 따로 집계해 화면에 알린다.
            // 그냥 버리면 화면 총매출액이 쿠팡(비즈니스인사이트)보다 조용히 작아지고,
            // 사용자는 대조해보기 전까지 누락 사실 자체를 알 수 없다.
            // 매출은 수량이 양수인 행만 합산하므로(반품행 금액은 집계에 안 들어감) 여기서도 동일 기준을 쓴다.
            // 수량 0 행(광고 노출만 있었거나 보관비만 붙은 행)은 판매가 아니라서 제외한다.
            // 이 행들의 옵션은 애초에 상품DB(판매중 상품)에 안 잡히므로, 세면 아무리 재수집해도 안 사라지는 알림이 된다.
            const excludedOptionIds = new Set();
            let excludedRowCount = 0;
            let excludedAmount = 0;
            settlementData.forEach(item => {
                if (validVendorItemIds.has(item.vendorItemId)) return;
                if (item.revenueDate < this.startDate || item.revenueDate > this.endDate) return;
                if ((item.saleQuantity || 0) <= 0) return;
                excludedOptionIds.add(String(item.vendorItemId));
                excludedRowCount++;
                excludedAmount += item.saleAmount || 0;
            });
            this.excludedSales = {
                optionCount: excludedOptionIds.size,
                rowCount: excludedRowCount,
                amount: excludedAmount
            };
            if (excludedRowCount > 0) {
                console.warn('상품DB에 없어 집계에서 제외된 판매:', this.excludedSales);
            }

            // 정산 데이터는 있는데 전부 걸러진 경우 — 원인을 구분해서 알린다 (조용히 0을 보여주지 않음)
            if (settlementData.length > 0 && validSettlementData.length === 0) {
                const 상품DB매칭 = settlementData.filter(i => validVendorItemIds.has(i.vendorItemId)).length;
                const 기간포함 = settlementData.filter(i => i.revenueDate >= this.startDate && i.revenueDate <= this.endDate).length;
                console.error('판매장부 0건 진단:', {
                    정산데이터: settlementData.length,
                    상품DB매칭: 상품DB매칭,
                    조회기간포함: 기간포함,
                    조회기간: `${this.startDate} ~ ${this.endDate}`,
                    샘플: settlementData[0]
                });
            }

            // 일별로 데이터 그룹화
            const dailyGrouped = {};

            // 먼저 vendorItemId + revenueDate별로 그룹화 (정상 판매와 반품을 합치기 위해)
            const vendorDateGrouped = {};
            validSettlementData.forEach(item => {
                const key = `${item.vendorItemId}_${item.revenueDate}`;
                if (!vendorDateGrouped[key]) {
                    vendorDateGrouped[key] = [];
                }
                vendorDateGrouped[key].push(item);
            });

            // ⚠ FIFO 원가는 위 (vid, 날짜) 묶음으로 계산하면 안 된다.
            //
            // getFifoUnitByDate 주석이 "settlementData 는 **조회 기간으로 자르기 전 전체**를 넘긴다.
            // 2월만 보더라도 1월 판매를 알아야 lot 이 얼마나 깎였는지 알 수 있다" 고 적어놨는데,
            // 정작 호출부가 그 날 하루치만 넘기고 있었다. 그러면 날마다 lot 큐를 처음부터 다시 까서
            // 사실상 첫 lot 단가가 계속 붙는다.
            //
            // 그래서 **상품별 판매내역**을 따로 만들어 넘긴다. vid 로만 거르고 날짜로는 안 자른다.
            // 실측: lot 을 타는 25개사 중 6개사에서 단가가 갈렸고 매입원가 합계 810만원 차
            // (rubycoco 67개 상품 중 36개, −2.92%). lot 이 2026-08 부터 쌓이기 시작해 앞으로 커진다.
            //
            // ⚠ 그리고 settlementData 는 **조회기간만** 담고 있다(/api/getSettlementData 가
            //   startDate~endDate 로 받아온다). 기간 앞 판매가 없으면 lot 이 덜 깎인 채로 시작해
            //   더 싼(앞) lot 단가가 붙는다 — 서버 buildLedgerSummary 는 앞 판매를 전부 넣으므로
            //   그대로 두면 화면과 대시보드가 갈린다(최근90일 rubycoco 총이익 203만원 / ROI 4%p).
            //   ⇒ /api/growth/getPriorSalesQty 로 받은 앞 판매를 여기에 보태 입력을 같게 맞춘다.
            const fifoSalesByVid = {};
            settlementData.forEach(item => {
                if (!validVendorItemIds.has(item.vendorItemId)) return;
                const key = String(item.vendorItemId);
                if (!fifoSalesByVid[key]) fifoSalesByVid[key] = [];
                fifoSalesByVid[key].push(item);
            });
            // 앞 판매는 lot 을 깎는 역할이다. 날짜별 단가로 나오므로 조회기간 밖 날짜는 그냥 안 쓰인다.
            // saleQuantity 에 순수수량이 들어 있고 returnQuantity 는 없다(getFifoUnitByDate 의
            // net = q − rq 가 같은 값을 낸다). 서버 prior 집계와 같은 식으로 접어서 내려온다.
            (priorSales || []).forEach(item => {
                if (!validVendorItemIds.has(item.vendorItemId)) return;
                const key = String(item.vendorItemId);
                if (!fifoSalesByVid[key]) fifoSalesByVid[key] = [];
                fifoSalesByVid[key].push(item);
            });

            // 상품별 날짜→FIFO 단가. 아래 루프에서 상품당 한 번만 채운다(값이 null 이어도 기억한다)
            const fifoUnitByVid = {};

            // 그룹화된 데이터를 일별로 처리
            Object.values(vendorDateGrouped).forEach(items => {
                const firstItem = items[0];
                const date = firstItem.revenueDate;

                if (!dailyGrouped[date]) {
                    dailyGrouped[date] = {
                        date: date,
                        products: [],
                        // 정산대기 계산용 — 정산주기는 도중에 바뀔 수 있으므로(주정산↔월정산)
                        // 고정 상수가 아니라 그날 레코드에 찍힌 값을 그대로 올린다.
                        // 재수집 전 과거 데이터엔 없어서 빈 값일 수 있다
                        settlementType: firstItem.settlementType || '',
                        settlementCycleEnd: firstItem.settlementCycleEnd || '',
                        일별판매량: 0,
                        일별총매출액: 0,
                        일별쿠팡지원할인: 0,
                        일별즉시할인쿠폰: 0,
                        일별다운로드쿠폰: 0,
                        일별순매출액: 0,
                        일별수수료: 0,
                        일별배송비: 0,
                        일별입출고비: 0,
                        일별보관비: 0,
                        일별반품회수재입고비: 0,
                        일별반출비: 0,
                        일별반출배송비: 0,
                        일별바코드비: 0,
                        일별재고손실보상: 0,
                        일별밀크런비용: 0,
                        비용미확정: 0,   // 배송 미완료로 비용이 덜 잡힌 상품 수(#112)
                        일별광고비: 0,
                        일별판매비용합계: 0,
                        일별기여이익: 0,
                        일별매입원가: 0,
                        일별부가세: 0,
                        일별총수익: 0,
                        일별자연판매량: 0,
                        일별광고판매량: 0,
                        일별반품수량: 0,
                        일별광고총수익: 0, // 광고매출 (광고판매량 × 개당매출액)
                        일별광고이익: 0,   // 광고이익 (광고판매량 × 개당이익[광고제외]) — 광고순수익 계산용
                        일별광고순수익: 0,
                        일별ROAS: 0,
                        일별ROI: 0
                    };
                }

                // 상품 정보 찾기 (타입 변환 고려)
                const productInfo = growthDB.find(p =>
                    p.vendorItemId == firstItem.vendorItemId ||
                    String(p.vendorItemId) === String(firstItem.vendorItemId)
                ) || growthDB.find(p =>
                    p.returnProducts && p.returnProducts.some(rp =>
                        rp.vendorItemId == firstItem.vendorItemId ||
                        String(rp.vendorItemId) === String(firstItem.vendorItemId)
                    )
                ) || growthDB.find(p =>
                    p.upBundlingProducts && p.upBundlingProducts.some(bundle =>
                        bundle.vendorItemId == firstItem.vendorItemId ||
                        String(bundle.vendorItemId) === String(firstItem.vendorItemId)
                    )
                );
                if (productInfo) {
                    // FIFO 단가는 **상품당 한 번** 계산해서 날짜별로 꺼내 쓴다.
                    //
                    // 예전엔 (vid, 날짜) 묶음마다 큐를 새로 걸었는데, 같은 상품의 전체 판매내역을
                    // 날 수만큼 다시 도는 것이라 느리기만 하고 **결과는 전부 같은 값**이었다
                    // (기간 단가 하나를 내는 함수였다). 이제 날짜별 단가를 한 번에 받는다.
                    const vidKey = String(firstItem.vendorItemId);
                    if (!(vidKey in fifoUnitByVid)) {
                        fifoUnitByVid[vidKey] = this.getFifoUnitByDate(
                            productInfo,
                            fifoSalesByVid[vidKey] || items
                        );
                    }
                    const 단가맵 = fifoUnitByVid[vidKey];

                    const productMetrics = this.calculateProductMetricsV3({
                        vendorItemId: firstItem.vendorItemId,
                        settlementData: items, // 같은 vendorItemId + revenueDate의 모든 데이터 (정상 + 반품)
                        // 그 날의 FIFO 단가. lot 이 없는 상품은 null → 날짜별 대표값으로 돌아간다
                        fifo단가: 단가맵 && 단가맵[date] ? 단가맵[date].매입가 : null,
                        // 그 날 판매가 속한 lot 의 밀크런 원/개(#112). lot 이 없으면 null → 판매일 기준 최근 입고값
                        fifo밀크런: 단가맵 && 단가맵[date] ? 단가맵[date].밀크런 : null,
                        productInfo: productInfo
                    });

                    if (productMetrics) {
                        dailyGrouped[date].products.push(productMetrics);

                        // 일별 합계 계산
                        dailyGrouped[date].일별판매량 += productMetrics.saleQuantity || 0;
                        if (productMetrics.costPending) dailyGrouped[date].비용미확정 += 1;
                        dailyGrouped[date].일별총매출액 += productMetrics.saleAmount || 0;
                        dailyGrouped[date].일별쿠팡지원할인 += productMetrics.coupangDiscount || 0;
                        dailyGrouped[date].일별즉시할인쿠폰 += productMetrics.instantCoupon || 0;
                        dailyGrouped[date].일별다운로드쿠폰 += productMetrics.downloadCoupon || 0;
                        dailyGrouped[date].일별순매출액 += productMetrics.netSales || 0;
                        dailyGrouped[date].일별수수료 += productMetrics.commissionTotal || 0;
                        dailyGrouped[date].일별배송비 += productMetrics.shippingTotal || 0;
                        dailyGrouped[date].일별입출고비 += productMetrics.warehouseTotal || 0;
                        dailyGrouped[date].일별보관비 += productMetrics.storageFee || 0;
                        dailyGrouped[date].일별반품회수재입고비 += productMetrics.creturnFee || 0;
                        dailyGrouped[date].일별반출비 += productMetrics.vreturnFee || 0;
                        dailyGrouped[date].일별반출배송비 += productMetrics.vreturnShippingFee || 0;
                        dailyGrouped[date].일별바코드비 += productMetrics.barcodeFee || 0;
                        dailyGrouped[date].일별재고손실보상 += productMetrics.inventoryCompensation || 0;
                        dailyGrouped[date].일별밀크런비용 += productMetrics.milkrunCostTotal || 0;
                        dailyGrouped[date].일별광고비 += productMetrics.adTotal || 0;
                        dailyGrouped[date].일별판매비용합계 += productMetrics.sellingCosts || 0;
                        dailyGrouped[date].일별기여이익 += productMetrics.contribution || 0;
                        dailyGrouped[date].일별매입원가 += productMetrics.cogs || 0;
                        dailyGrouped[date].일별부가세 += productMetrics.vat || 0;
                        dailyGrouped[date].일별총수익 += productMetrics.grossProfit || 0;
                        dailyGrouped[date].일별자연판매량 += productMetrics.naturalSales || 0;
                        dailyGrouped[date].일별광고판매량 += productMetrics.saleQuantityFromAd || 0;
                        dailyGrouped[date].일별반품수량 = (dailyGrouped[date].일별반품수량 || 0) + (productMetrics.returnQuantity || 0);

                        // 광고매출(진짜 매출) = 광고판매량 × 개당매출액(단가)
                        const unitPrice = productMetrics.saleQuantity > 0 ? (productMetrics.saleAmount || 0) / productMetrics.saleQuantity : 0;
                        dailyGrouped[date].일별광고총수익 = (dailyGrouped[date].일별광고총수익 || 0) + ((productMetrics.saleQuantityFromAd || 0) * unitPrice);
                        // 광고이익 = 광고판매량 × 개당이익(광고제외) — 광고순수익 계산용
                        dailyGrouped[date].일별광고이익 = (dailyGrouped[date].일별광고이익 || 0) + ((productMetrics.saleQuantityFromAd || 0) * (productMetrics.profitPerUnit || 0));
                    }
                }
            });

            // 일별 데이터를 배열로 변환하고 날짜순 정렬
            this.salesLedgerData = Object.values(dailyGrouped).map(day => {
                // 일별 ROAS 계산 (광고판매량 * 평균판매가 / 광고비)
                if (day.일별광고비 > 0 && day.일별광고판매량 > 0) {
                    const avgSalePrice = day.일별총매출액 / day.일별판매량;
                    day.일별ROAS = (day.일별광고판매량 * avgSalePrice) / day.일별광고비;
                } else {
                    day.일별ROAS = 0;
                }

                // 일별 ROI 계산 (총이익 / 매입원가 * 100)
                if (day.일별매입원가 > 0) {
                    day.일별ROI = Math.round(((day.일별총수익 / (day.일별매입원가 * 10 / 11)) * 100) * 10) / 10; // 소수점 1자리 반올림 (분모는 부가세 제외 매입원가)
                } else {
                    day.일별ROI = 0;
                }

                // 모든 수치 반올림 처리
                day.일별판매량 = Math.round(day.일별판매량);
                day.일별총매출액 = Math.round(day.일별총매출액);
                day.일별쿠팡지원할인 = Math.round(day.일별쿠팡지원할인);
                day.일별즉시할인쿠폰 = Math.round(day.일별즉시할인쿠폰);
                day.일별다운로드쿠폰 = Math.round(day.일별다운로드쿠폰);
                day.일별순매출액 = Math.round(day.일별순매출액);
                day.일별수수료 = Math.round(day.일별수수료);
                day.일별배송비 = Math.round(day.일별배송비);
                day.일별입출고비 = Math.round(day.일별입출고비);
                day.일별보관비 = Math.round(day.일별보관비);
                day.일별반품회수재입고비 = Math.round(day.일별반품회수재입고비);
                day.일별반출비 = Math.round(day.일별반출비);
                day.일별반출배송비 = Math.round(day.일별반출배송비);
                day.일별바코드비 = Math.round(day.일별바코드비);
                day.일별재고손실보상 = Math.round(day.일별재고손실보상);
                day.일별밀크런비용 = Math.round(day.일별밀크런비용);
                day.일별광고비 = Math.round(day.일별광고비);
                day.일별판매비용합계 = Math.round(day.일별판매비용합계);
                day.일별기여이익 = Math.round(day.일별기여이익);
                day.일별매입원가 = Math.round(day.일별매입원가);
                day.일별부가세 = Math.round(day.일별부가세);
                day.일별총수익 = Math.round(day.일별총수익);
                day.일별자연판매량 = Math.round(day.일별자연판매량);
                day.일별광고판매량 = Math.round(day.일별광고판매량);
                day.일별광고총수익 = Math.round(day.일별광고총수익 || 0); // 광고매출
                day.일별광고이익 = Math.round(day.일별광고이익 || 0);
                day.일별광고순수익 = Math.round((day.일별광고이익 || 0) - (day.일별광고비 || 0)); // 광고이익 - 광고비

                return day;
            }).sort((a, b) => b.date.localeCompare(a.date));

            console.log('최종 일별 판매장부 데이터:', this.salesLedgerData.length, '일');

            // 상품별 보기용 데이터 생성
            this.generateProductViewData();

            // 차트(워터폴·일별 추이·상품 진단)는 dailyRows·productRows 에서 computed 로 나온다
            this.calculateSummary();
        },

        // 상품별 보기용 데이터 생성
        generateProductViewData() {
            const productGrouped = {};

            // 모든 일별 데이터에서 상품별로 집계
            this.salesLedgerData.forEach(day => {
                day.products.forEach(product => {
                    const vendorItemId = product.vendorItemId;

                    if (!productGrouped[vendorItemId]) {
                        // 상품명에서 판매상품명과 옵션명 분리
                        const productNameParts = product.상품명 ? product.상품명.split(', ') : ['', ''];
                        const 판매상품명 = productNameParts[0] || '';
                        const 옵션명 = productNameParts[1] || '';

                        const coverage = this.getCoverage(vendorItemId);
                        productGrouped[vendorItemId] = {
                            vendorItemId: vendorItemId,
                            productId: product.productId,
                            상품명: product.상품명,
                            판매상품명: 판매상품명,
                            옵션명: 옵션명,
                            썸네일: this.getProductThumbnail(vendorItemId),
                            coupangStock: coverage.coupangStock,   // 쿠팡재고 수량 (현재 스냅샷)
                            purchasePrice: coverage.purchasePrice, // 최근 매입가 (개당)
                            skuGrade: product.skuGrade,
                            upBundlingSize: product.upBundlingSize || 1,
                            saleQuantity: 0,
                            saleAmount: 0,
                            coupangDiscount: 0,
                            instantCoupon: 0,
                            downloadCoupon: 0,
                            netSales: 0,
                            commissionTotal: 0,
                            shippingTotal: 0,
                            warehouseTotal: 0,
                            storageFee: 0,
                            creturnFee: 0,
                            vreturnFee: 0,
                            vreturnShippingFee: 0,
                            barcodeFee: 0,
                            inventoryCompensation: 0,
                            milkrunCostTotal: 0,
                            costPendingDays: 0,
                            adTotal: 0,
                            sellingCosts: 0,
                            contribution: 0,
                            cogs: 0,
                            vat: 0,
                            grossProfit: 0,
                            naturalSales: 0,
                            saleQuantityFromAd: 0,
                            returnQuantity: 0,
                            adProfit: 0,      // 광고이익 (광고판매량 × 개당이익[광고제외]) — 광고순수익 계산용
                            adTotalProfit: 0, // 광고매출
                            adNetProfit: 0, // 광고순수익
                            pricedRows: 0, // 매입가가 있는 일자 행 수 — 0 이면 cogs·GP 를 null 로 둔다
                            dailyData: [] // 일별 상세 데이터
                        };
                    }

                    // 합계 계산
                    productGrouped[vendorItemId].saleQuantity += product.saleQuantity || 0;
                    productGrouped[vendorItemId].saleAmount += product.saleAmount || 0;
                    productGrouped[vendorItemId].coupangDiscount += product.coupangDiscount || 0;
                    productGrouped[vendorItemId].instantCoupon += product.instantCoupon || 0;
                    productGrouped[vendorItemId].downloadCoupon += product.downloadCoupon || 0;
                    productGrouped[vendorItemId].netSales += product.netSales || 0;
                    productGrouped[vendorItemId].commissionTotal += product.commissionTotal || 0;
                    productGrouped[vendorItemId].shippingTotal += product.shippingTotal || 0;
                    productGrouped[vendorItemId].warehouseTotal += product.warehouseTotal || 0;
                    productGrouped[vendorItemId].storageFee += product.storageFee || 0;
                    productGrouped[vendorItemId].creturnFee += product.creturnFee || 0;
                    productGrouped[vendorItemId].vreturnFee += product.vreturnFee || 0;
                    productGrouped[vendorItemId].vreturnShippingFee += product.vreturnShippingFee || 0;
                    productGrouped[vendorItemId].barcodeFee += product.barcodeFee || 0;
                    productGrouped[vendorItemId].inventoryCompensation += product.inventoryCompensation || 0;
                    productGrouped[vendorItemId].milkrunCostTotal += product.milkrunCostTotal || 0;
                    if (product.costPending) productGrouped[vendorItemId].costPendingDays += 1;
                    productGrouped[vendorItemId].adTotal += product.adTotal || 0;
                    productGrouped[vendorItemId].sellingCosts += product.sellingCosts || 0;
                    productGrouped[vendorItemId].contribution += product.contribution || 0;
                    productGrouped[vendorItemId].cogs += product.cogs || 0;
                    productGrouped[vendorItemId].vat += product.vat || 0;
                    productGrouped[vendorItemId].grossProfit += product.grossProfit || 0;
                    // 매입가 없는 행은 cogs·GP 가 null 이라 합산에서 빠진다(서버 buildLedgerSummary 와 같다).
                    // 전 행이 없으면 아래에서 null 로 되돌린다 — 0 으로 두면 '이익 = 기여이익'으로 부풀어 보인다
                    if (product.cogs !== null && product.cogs !== undefined) productGrouped[vendorItemId].pricedRows += 1;
                    productGrouped[vendorItemId].naturalSales += product.naturalSales || 0;
                    productGrouped[vendorItemId].saleQuantityFromAd += product.saleQuantityFromAd || 0;
                    productGrouped[vendorItemId].returnQuantity = (productGrouped[vendorItemId].returnQuantity || 0) + (product.returnQuantity || 0);

                    // 광고매출(진짜 매출) = 광고판매량 × 개당매출액 / 광고이익 = 광고판매량 × 개당이익(광고제외)
                    // product.profitPerUnit 은 이미 광고비를 제외한 개당이익
                    const unitPrice = product.saleQuantity > 0 ? (product.saleAmount || 0) / product.saleQuantity : 0;
                    productGrouped[vendorItemId].adTotalProfit += (product.saleQuantityFromAd || 0) * unitPrice;
                    productGrouped[vendorItemId].adProfit += (product.saleQuantityFromAd || 0) * (product.profitPerUnit || 0);

                    // 일별 상세 데이터 추가 (광고매출/광고이익 포함)
                    productGrouped[vendorItemId].dailyData.push({
                        date: day.date,
                        ...product,
                        adTotalProfit: (product.saleQuantityFromAd || 0) * unitPrice,
                        adProfit: (product.saleQuantityFromAd || 0) * (product.profitPerUnit || 0)
                    });
                });
            });

            // 비율 계산
            Object.values(productGrouped).forEach(product => {
                // 기본값 설정
                product.profitPerUnit = null;
                product.grossMargin = null;
                product.roi = null;
                product.roas = null;
                product.adROI = null;

                // 매입가가 한 행도 없으면 매입원가·부가세·총이익을 모른다
                if (product.pricedRows === 0) {
                    product.cogs = null;
                    product.vat = null;
                    product.grossProfit = null;
                }

                if (product.saleQuantity > 0) {
                    // 광고매출 = 광고판매량 × 개당매출액(단가) / 광고이익 = 광고판매량 × 개당이익(광고제외)
                    const unitPrice = product.saleAmount / product.saleQuantity;
                    product.adTotalProfit = product.saleQuantityFromAd * unitPrice;
                    if (product.grossProfit !== null) {
                        // 개당이익(광고제외) = (총이익 + 광고비 − 재고손실보상) ÷ 판매수량 (#112)
                        product.profitPerUnit = ledgerProfitPerUnit(product.grossProfit, product.adTotal, product.inventoryCompensation, product.saleQuantity);
                        product.adProfit = product.saleQuantityFromAd * product.profitPerUnit;
                    }
                }
                if (product.netSales > 0 && product.grossProfit !== null) {
                    product.grossMargin = (product.grossProfit / product.netSales) * 100;
                }
                if (product.cogs > 0) {
                    // 일자 행(calculateProductMetricsV3)과 같게 공급가(부가세 제외) 기준
                    product.roi = (product.grossProfit / (product.cogs * 10 / 11)) * 100;
                }
                if (product.saleQuantityFromAd > 0 && product.adTotal > 0 && product.saleQuantity > 0) {
                    const avgSalePrice = product.saleAmount / product.saleQuantity;
                    product.roas = (product.saleQuantityFromAd * avgSalePrice) / product.adTotal;
                    if (product.profitPerUnit !== null) {
                        product.adROI = (product.profitPerUnit * product.saleQuantityFromAd) / product.adTotal;
                    }
                }

                // 모든 수치 반올림 처리
                product.saleQuantity = Math.round(product.saleQuantity);
                product.saleAmount = Math.round(product.saleAmount);
                product.coupangDiscount = Math.round(product.coupangDiscount);
                product.instantCoupon = Math.round(product.instantCoupon);
                product.downloadCoupon = Math.round(product.downloadCoupon);
                product.netSales = Math.round(product.netSales);
                product.commissionTotal = Math.round(product.commissionTotal);
                product.shippingTotal = Math.round(product.shippingTotal);
                product.warehouseTotal = Math.round(product.warehouseTotal);
                product.storageFee = Math.round(product.storageFee);
                product.creturnFee = Math.round(product.creturnFee);
                product.vreturnFee = Math.round(product.vreturnFee);
                product.vreturnShippingFee = Math.round(product.vreturnShippingFee);
                product.barcodeFee = Math.round(product.barcodeFee);
                product.inventoryCompensation = Math.round(product.inventoryCompensation);
                product.milkrunCostTotal = Math.round(product.milkrunCostTotal);
                product.adTotal = Math.round(product.adTotal);
                product.sellingCosts = Math.round(product.sellingCosts);
                product.contribution = Math.round(product.contribution);
                if (product.cogs !== null) product.cogs = Math.round(product.cogs);
                if (product.vat !== null) product.vat = Math.round(product.vat);
                if (product.grossProfit !== null) product.grossProfit = Math.round(product.grossProfit);
                product.naturalSales = Math.round(product.naturalSales);
                product.saleQuantityFromAd = Math.round(product.saleQuantityFromAd);
                product.adTotalProfit = Math.round(product.adTotalProfit); // 광고매출
                product.adProfit = Math.round(product.adProfit || 0);
                product.adNetProfit = Math.round((product.adProfit || 0) - (product.adTotal || 0)); // 광고이익 - 광고비

                // 비율은 소수점 1자리로 반올림
                if (product.profitPerUnit !== null) {
                    product.profitPerUnit = Math.round(product.profitPerUnit);
                }
                if (product.grossMargin !== null) {
                    product.grossMargin = Math.round(product.grossMargin * 10) / 10;
                }
                if (product.roi !== null) {
                    product.roi = Math.round(product.roi * 10) / 10;
                }
                if (product.roas !== null) {
                    product.roas = Math.round(product.roas * 1000) / 1000; // ROAS는 소수점 3자리
                }
                if (product.adROI !== null) {
                    product.adROI = Math.round(product.adROI * 100) / 100; // 소수점 2자리
                }
            });

            // 매출 내림차순으로 정렬
            this.productViewData = Object.values(productGrouped).sort((a, b) => b.saleAmount - a.saleAmount);

            console.log('상품별 보기 데이터:', this.productViewData.length, '개');
        },

        // 금액을 짧게 (차트 라벨·진단 리스트용) — 셈은 utils/ledgerCharts 한 곳
        formatMoneyShort(v) {
            return formatMoneyShort(v);
        },

        // 판매장부 데이터 처리 V2 (실제 정산현황 데이터 사용)
        processSalesLedgerDataV2(data) {
            console.log('=== processSalesLedgerDataV2 시작 ===');
            console.log('입력 데이터:', data);

            const { settlementData, adData } = data;
            const growthDB = this.$store.state.growthdb; // store에서 가져오기

            console.log('정산현황 데이터:', settlementData.length, '개');
            console.log('광고 데이터:', adData.length, '개');
            console.log('growthDB:', growthDB.length, '개');

            // growthDB의 vendorItemId만 추출 (기준이 되는 상품들)
            const validVendorItemIds = new Set(growthDB.map(product => product.vendorItemId));
            console.log('유효한 vendorItemId:', validVendorItemIds.size, '개');

            // vendorItemId별로 데이터 그룹화 (growthDB 기준)
            const groupedData = {};

            // 1. growthDB 기준으로 초기 그룹 생성
            growthDB.forEach(product => {
                groupedData[product.vendorItemId] = {
                    vendorItemId: product.vendorItemId,
                    settlementData: [],
                    adData: [],
                    productInfo: product
                };
            });

            // 2. 정산현황 데이터 매핑 (growthDB에 있는 vendorItemId만)
            let settlementMappedCount = 0;
            settlementData.forEach(item => {
                if (validVendorItemIds.has(item.vendorItemId)) {
                    groupedData[item.vendorItemId].settlementData.push(item);
                    settlementMappedCount++;
                }
            });
            console.log('정산현황 데이터 매핑:', settlementMappedCount, '개');

            // 3. 광고 데이터 매핑 (growthDB에 있는 vendorItemId만)
            let adMappedCount = 0;
            adData.forEach(item => {
                if (validVendorItemIds.has(item.vendorItemId)) {
                    groupedData[item.vendorItemId].adData.push(item);
                    adMappedCount++;
                }
            });
            console.log('광고 데이터 매핑:', adMappedCount, '개');

            // 4. 판매장부 데이터 생성 (실제 정산현황 기반 계산)
            this.salesLedgerData = Object.values(groupedData).map(group => {
                return this.calculateProductMetricsV2(group);
            }).filter(item => item !== null);

            console.log('최종 판매장부 데이터:', this.salesLedgerData.length, '개');
            this.calculateSummary();
        },

        // 판매장부 데이터 처리 (기존 방식)
        processSalesLedgerData(data) {
            const { dailySalesData, adData } = data;
            const growthDB = this.$store.state.growthdb; // store에서 가져오기
            console.log('판매장부 데이터:', data);
            console.log('growthDB from store:', growthDB.length);

            // vendorItemId별로 데이터 그룹화
            const groupedData = {};

            // 일별 판매 데이터 그룹화
            dailySalesData.forEach(item => {
                if (!groupedData[item.vendorItemId]) {
                    groupedData[item.vendorItemId] = {
                        vendorItemId: item.vendorItemId,
                        dailySales: [],
                        adData: [],
                        productInfo: null
                    };
                }
                groupedData[item.vendorItemId].dailySales.push(item);
            });

            // 광고 데이터 그룹화
            adData.forEach(item => {
                if (!groupedData[item.vendorItemId]) {
                    groupedData[item.vendorItemId] = {
                        vendorItemId: item.vendorItemId,
                        dailySales: [],
                        adData: [],
                        productInfo: null
                    };
                }
                groupedData[item.vendorItemId].adData.push(item);
            });

            // 상품 정보 매핑
            let mappedCount = 0;
            growthDB.forEach(product => {
                if (groupedData[product.vendorItemId]) {
                    groupedData[product.vendorItemId].productInfo = product;
                    mappedCount++;
                }

                // 번들상품의 판매 데이터를 메인 상품에 합산
                if (product.upBundlingProducts && product.upBundlingProducts.length > 0) {
                    product.upBundlingProducts.forEach(bundleProduct => {
                        if (groupedData[bundleProduct.vendorItemId]) {
                            const bundleSize = bundleProduct.upBundlingSize || 1;
                            const bundleData = groupedData[bundleProduct.vendorItemId];

                            // 메인 상품 데이터가 없으면 생성
                            if (!groupedData[product.vendorItemId]) {
                                groupedData[product.vendorItemId] = {
                                    vendorItemId: product.vendorItemId,
                                    dailySales: [],
                                    adData: [],
                                    productInfo: product
                                };
                            }

                            // 번들상품의 일별 판매 데이터를 메인 상품에 합산 (번들 사이즈로 나누어서)
                            bundleData.dailySales.forEach(bundleSale => {
                                const mainSale = groupedData[product.vendorItemId].dailySales.find(s => s.date === bundleSale.date);
                                if (mainSale) {
                                    // 기존 날짜에 합산
                                    mainSale.sales.unitsSold = (mainSale.sales.unitsSold || 0) + ((bundleSale.sales?.unitsSold || 0) / bundleSize);
                                    mainSale.sales.gmv = (mainSale.sales.gmv || 0) + ((bundleSale.sales?.gmv || 0) / bundleSize);
                                } else {
                                    // 새로운 날짜 추가
                                    const newSale = {
                                        ...bundleSale,
                                        sales: {
                                            unitsSold: (bundleSale.sales?.unitsSold || 0) / bundleSize,
                                            gmv: (bundleSale.sales?.gmv || 0) / bundleSize
                                        }
                                    };
                                    groupedData[product.vendorItemId].dailySales.push(newSale);
                                }
                            });

                            // 번들상품의 광고 데이터를 메인 상품에 합산 (번들 사이즈로 나누어서)
                            bundleData.adData.forEach(bundleAd => {
                                const mainAd = groupedData[product.vendorItemId].adData.find(a => a.date === bundleAd.date);
                                if (mainAd) {
                                    // 기존 날짜에 합산
                                    bundleAd.adDetails?.forEach(bundleDetail => {
                                        const mainDetail = mainAd.adDetails?.find(d => d.adType === bundleDetail.adType);
                                        if (mainDetail) {
                                            mainDetail.cost = (mainDetail.cost || 0) + ((bundleDetail.cost || 0) / bundleSize);
                                            mainDetail.clicks = (mainDetail.clicks || 0) + ((bundleDetail.clicks || 0) / bundleSize);
                                            mainDetail.impressions = (mainDetail.impressions || 0) + ((bundleDetail.impressions || 0) / bundleSize);
                                        }
                                    });
                                } else {
                                    // 새로운 날짜 추가
                                    const newAd = {
                                        ...bundleAd,
                                        adDetails: bundleAd.adDetails?.map(detail => ({
                                            ...detail,
                                            cost: (detail.cost || 0) / bundleSize,
                                            clicks: (detail.clicks || 0) / bundleSize,
                                            impressions: (detail.impressions || 0) / bundleSize
                                        }))
                                    };
                                    groupedData[product.vendorItemId].adData.push(newAd);
                                }
                            });

                            // 번들상품 데이터는 제거 (중복 방지)
                            delete groupedData[bundleProduct.vendorItemId];
                        }
                    });
                }
            });
            console.log('상품 정보 매핑 및 번들상품 합산 완료:', mappedCount, '개 상품');

            // 판매장부 데이터 생성
            this.salesLedgerData = Object.values(groupedData).map(group => {
                return this.calculateProductMetrics(group);
            }).filter(item => item !== null);

            this.calculateSummary();
        },

        // 상품별 18개 지표 계산
        calculateProductMetrics(group) {
            const { vendorItemId, dailySales, adData, productInfo } = group;

            if (!productInfo) return null; // 상품 정보가 없으면 제외

            // 디버깅: 실제 데이터 구조 확인
            if (adData.length > 0) {
                console.log(`[${vendorItemId}] 광고 데이터 구조:`, JSON.stringify(adData[0], null, 2));
            }

            // 기본 상품 정보 - 실제 growthDB 구조에 맞게 매핑
            const result = {
                vendorItemId: vendorItemId,
                productId: productInfo.productId || '',
                썸네일: productInfo.썸네일 || "http://image1.coupangcdn.com/image/" + (productInfo.listingDetails?.mainImagePath || productInfo.imgUrl || ''),
                판매상품명: productInfo.판매상품명 || productInfo.listingDetails?.vendorInventoryName || productInfo.displayProductName || '',
                옵션명: productInfo.옵션명 || productInfo.listingDetails?.vendorInventoryItemName || productInfo.itemName || '',
                판매가: productInfo.salePrice || 0
            };

            // 일별 판매 데이터 집계
            let 총판매량 = 0;
            let 총매출액 = 0;

            dailySales.forEach(day => {
                총판매량 += day.sales?.unitsSold || 0;
                총매출액 += day.sales?.gmv || 0;
            });

            // 광고 데이터 집계 - 기여도 분석 방식
            let 직접광고비 = 0;  // 해당 상품에 직접 지출된 광고비
            let 기여광고비 = 0;  // 다른 상품 광고로 인해 배분받은 광고비
            let 직접광고판매량 = 0;
            let 기여매출 = 0;  // 해당 상품 광고가 다른 상품에 기여한 매출

            // 1. 해당 상품에 직접 지출된 광고비 계산 (실제 저장된 데이터 구조 활용)
            adData.forEach(ad => {
                if (ad.vendorItemId === vendorItemId) {
                    ad.adDetails?.forEach(adDetail => {
                        // adDetail.adType이 '검색 영역', '비검색 영역', '리타겟팅' 중 하나
                        직접광고비 += adDetail.cost || 0;

                        // 해당 상품의 직접 전환만 계산 (detail 배열에서)
                        if (adDetail.detail && Array.isArray(adDetail.detail)) {
                            const 직접전환 = adDetail.detail.find(conv => conv.vendorItemId == vendorItemId);
                            if (직접전환) {
                                직접광고판매량 += 직접전환.units || 0;
                            }
                        }
                    });
                }
            });

            // 2. 다른 상품의 광고로 인한 기여 효과 계산 (실제 저장된 데이터 구조 활용)
            adData.forEach(ad => {
                if (ad.vendorItemId !== vendorItemId) {
                    ad.adDetails?.forEach(adDetail => {
                        // adDetail.detail 배열에서 현재 vendorItemId의 전환 데이터 찾기
                        if (adDetail.detail && Array.isArray(adDetail.detail)) {
                            const 전환데이터 = adDetail.detail.find(conv => conv.vendorItemId == vendorItemId);
                            if (전환데이터) {
                                // 광고비를 전환 비율에 따라 배분
                                const 총전환매출 = adDetail.detail.reduce((sum, conv) => sum + (conv.sales || 0), 0);
                                const 기여비율 = 총전환매출 > 0 ? (전환데이터.sales || 0) / 총전환매출 : 0;

                                기여광고비 += (adDetail.cost || 0) * 기여비율;
                            }
                        }
                    });
                }
            });

            // 3. 해당 상품 광고가 다른 상품에 기여한 매출 계산
            adData.forEach(ad => {
                if (ad.vendorItemId === vendorItemId) {
                    ad.adDetails?.forEach(adDetail => {
                        // adDetail.detail 배열에서 다른 상품의 전환 매출 합계
                        if (adDetail.detail && Array.isArray(adDetail.detail)) {
                            adDetail.detail.forEach(conv => {
                                if (conv.vendorItemId != vendorItemId) {
                                    기여매출 += conv.sales || 0;
                                }
                            });
                        }
                    });
                }
            });

            // 총 광고 관련 지표는 참고용으로만 사용                                                      // 번들제품, 등급품 판매량 추가, 반품비, 반품재입고비, 쿠폰분리(즉시할인쿠폰, 다운로드쿠폰), 

            // 계산된 지표들 - 실제 growthDB 데이터 구조 활용
            const 매입가 = productInfo.매입가 || 0;
            const 총매입액 = 총판매량 * 매입가;

            // 실제 growthDB 구조에서 수수료와 쿠폰 정보 활용
            const 판매가 = productInfo.salePrice || result.판매가;
            const 수수료정보 = productInfo.수수료 || {};
            const 수수료 = productInfo.commission || 0; // 실제 수수료 금액
            const 쿠폰 = productInfo.coupon || 0; // 실제 쿠폰 금액

            // 실제 판매에 따른 수수료와 쿠폰 계산
            const 판매수수료 = Math.round((수수료 / 판매가) * 총매출액);
            const 즉시할인쿠폰 = Math.round((쿠폰 / 판매가) * 총매출액);

            // 물류비 계산 - 실제 growthDB의 수수료 구조 활용
            const 입출고비 = 총판매량 * (수수료정보.입출고비 || 1000); // 실제 입출고비 또는 기본값
            const 배송비 = 총판매량 * (수수료정보.배송비 || 1925); // 실제 배송비 또는 기본값

            // 반품 관련 비용 - 실제 반품 데이터 기반 계산
            let 반품수량 = 0;
            // returnProducts가 있다면 실제 반품 데이터 활용
            if (productInfo.returnProducts && productInfo.returnProducts.length > 0) {
                // 실제 반품 상품이 있는 경우 해당 데이터 활용
                반품수량 = Math.round(총판매량 * 0.03); // 실제 반품이 있는 상품은 3% 가정
            } else {
                // 반품 상품이 없는 경우 낮은 반품율 적용
                반품수량 = Math.round(총판매량 * 0.01); // 1% 반품율 가정
            }
            const 반품비 = 반품수량 * (수수료정보.배송비 || 1925); // 반품당 배송비
            const 반품재입고비 = 반품수량 * (수수료정보.입출고비 || 1000); // 반품당 재입고비

            // 다운로드쿠폰은 별도 데이터가 없으므로 가정
            const 다운로드쿠폰 = Math.round(총매출액 * 0.02); // 2% 가정

            const 일반판매량 = 총판매량 - 직접광고판매량;

            // 총 수수료 계산 (쿠폰 제외)
            const 총수수료 = 판매수수료 + 입출고비 + 배송비 + 반품비 + 반품재입고비;

            // 총 쿠폰 계산
            const 총쿠폰 = 즉시할인쿠폰 + 다운로드쿠폰;

            // 광고비에 부가세 적용 (광고비는 부가세 미포함이므로 1.1 곱함)
            const 부가세포함광고비 = 직접광고비 * 1.1;

            // 수익 계산: 매출 - 수수료 - 광고비*1.1 - 매입 - (매출-수수료-광고비-매입)/11 (부가세)
            const 부가세전수익 = 총매출액 - 총수수료 - 부가세포함광고비 - 총매입액;
            const 부가세 = 부가세전수익 / 11;
            const 직접수익 = 부가세전수익 - 부가세;

            // ROAS 계산 (부가세 포함 광고비 기준)
            const 직접ROAS = 부가세포함광고비 > 0 ? Math.round((총매출액 / 부가세포함광고비) * 100) : 0;
            const 통합ROAS = 부가세포함광고비 > 0 ? Math.round(((총매출액 + 기여매출) / 부가세포함광고비) * 100) : 0; // 참고용

            const ROI = 총매입액 > 0 ? Math.round((직접수익 / (총매입액 * 10 / 11)) * 100) : 0;

            return {
                ...result,
                총판매량, 총매출액, 총매입액,
                판매수수료, 입출고비, 배송비, 반품비, 반품재입고비,
                총수수료, // 수수료 합계
                즉시할인쿠폰, 다운로드쿠폰, 총쿠폰, // 쿠폰 관련
                일반판매량, 광고판매량: 직접광고판매량,
                직접광고비: Math.round(직접광고비), // 부가세 미포함 광고비
                부가세포함광고비: Math.round(부가세포함광고비), // 부가세 포함 광고비
                부가세: Math.round(부가세), // 부가세
                기여광고비: Math.round(기여광고비), // 참고용
                기여매출: Math.round(기여매출), // 참고용
                직접수익: Math.round(직접수익), // 부가세 차감 후 실제 수익
                직접ROAS: 직접ROAS, // 직접 ROAS
                통합ROAS: 통합ROAS, // 참고용 통합 ROAS
                ROI
            };
        },

        // 상품별 18개 지표 계산 V2 (실제 정산현황 데이터 사용)
        calculateProductMetricsV2(group) {
            console.log('=== calculateProductMetricsV2 시작 ===');
            console.log('그룹 데이터:', group);

            const { vendorItemId, settlementData, adData, productInfo } = group;

            if (!productInfo) {
                console.log(`[${vendorItemId}] 상품 정보 없음 - 제외`);
                return null;
            }

            // 정산현황 데이터가 없으면 제외
            if (!settlementData || settlementData.length === 0) {
                console.log(`[${vendorItemId}] 정산현황 데이터 없음 - 제외`);
                return null;
            }

            console.log(`[${vendorItemId}] 정산현황 데이터:`, settlementData.length, '개');
            console.log(`[${vendorItemId}] 광고 데이터:`, adData.length, '개');
            console.log(`[${vendorItemId}] 상품 정보:`, productInfo.판매상품명);

            // 기본 상품 정보
            const result = {
                vendorItemId: vendorItemId,
                productId: productInfo.productId || '',
                썸네일: productInfo.썸네일 || "http://image1.coupangcdn.com/image/" + (productInfo.listingDetails?.mainImagePath || productInfo.imgUrl || ''),
                판매상품명: productInfo.판매상품명 || productInfo.listingDetails?.vendorInventoryName || productInfo.displayProductName || '',
                옵션명: productInfo.옵션명 || productInfo.listingDetails?.vendorInventoryItemName || productInfo.itemName || '',
                판매가: productInfo.salePrice || 0
            };

            // 정산현황 데이터 집계 (실제 쿠팡 수수료 사용)
            let 총판매량 = 0;
            let 총매출액 = 0;
            let 총판매수수료 = 0;
            let 총입출고비 = 0;
            let 총배송비 = 0;
            let 총쿠폰 = 0;

            // 매출인식일 기준으로 조회 기간 내 데이터만 필터링
            const 기간내정산데이터 = settlementData.filter(item => {
                if (!item.revenueDate) return false;
                const revenueDate = item.revenueDate;
                return revenueDate >= this.startDate && revenueDate <= this.endDate;
            });

            console.log(`[${vendorItemId}] 전체 정산데이터: ${settlementData.length}개, 기간내 데이터: ${기간내정산데이터.length}개`);

            기간내정산데이터.forEach(item => {
                console.log(`[${vendorItemId}] 정산 항목 (매출인식일: ${item.revenueDate}):`, {
                    orderId: item.orderId,
                    saleAmount: item.saleAmount,
                    commission: item.commission,
                    warehouseFee: item.warehouseFee,
                    shippingFee: item.shippingFee,
                    revenueDate: item.revenueDate
                });

                총판매량 += item.saleQuantity || 0;
                총매출액 += item.saleAmount || 0;
                총판매수수료 += item.commission || 0;
                총입출고비 += item.warehouseFee || 0;
                총배송비 += item.shippingFee || 0;

                // 각종 쿠폰 합계
                총쿠폰 += (item.instantCoupon || 0) + (item.downloadCoupon || 0);
            });

            // 총 쿠팡 수수료 (실제 데이터)
            const 총수수료 = 총판매수수료 + 총입출고비 + 총배송비;

            console.log(`[${vendorItemId}] 집계 결과:`, {
                총판매량, 총매출액, 총판매수수료, 총입출고비, 총배송비, 총수수료, 총쿠폰
            });

            // 매입액 계산 (growthDB의 매입가 사용)
            const 매입가 = productInfo.매입가 || productInfo.purchasePrice || 0;
            const 총매입액 = 매입가 * 총판매량;

            console.log(`[${vendorItemId}] 매입가:`, 매입가, '총매입액:', 총매입액);

            // 일별 광고 데이터 집계 (새로운 DailyAdData 구조)
            let 직접광고비 = 0;
            let 기여광고비 = 0;
            let 직접광고판매량 = 0;
            let 기여매출 = 0;

            // 해당 vendorItemId의 조회 기간 내 일별 광고데이터만 필터링
            const 해당상품광고데이터 = adData.filter(ad => {
                return ad.vendorItemId === vendorItemId &&
                       ad.date >= this.startDate &&
                       ad.date <= this.endDate;
            });

            console.log(`[${vendorItemId}] 해당상품 광고데이터 (기간 ${this.startDate}~${this.endDate}):`, 해당상품광고데이터.length, '개');

            해당상품광고데이터.forEach(ad => {
                // 직접 광고비 집계
                직접광고비 += ad.cost || 0;

                // 직접 전환 집계 (detail 배열에서 해당 vendorItemId 찾기)
                if (ad.detail && Array.isArray(ad.detail)) {
                    const 직접전환 = ad.detail.find(conv => conv.vendorItemId == vendorItemId);
                    if (직접전환) {
                        직접광고판매량 += 직접전환.units || 0;
                    }

                    // 기여 매출 집계 (다른 vendorItemId로의 전환)
                    ad.detail.forEach(conv => {
                        if (conv.vendorItemId != vendorItemId) {
                            기여매출 += conv.sales || 0;
                        }
                    });
                }
            });

            // 다른 상품의 광고가 이 상품에 기여한 광고비 계산
            adData.forEach(ad => {
                if (ad.vendorItemId !== vendorItemId && ad.detail && Array.isArray(ad.detail)) {
                    const 기여전환 = ad.detail.find(conv => conv.vendorItemId == vendorItemId);
                    if (기여전환 && 기여전환.sales > 0) {
                        // 기여도에 따른 광고비 배분 (매출 비율로 계산)
                        const 총전환매출 = ad.detail.reduce((sum, conv) => sum + (conv.sales || 0), 0);
                        if (총전환매출 > 0) {
                            기여광고비 += (ad.cost || 0) * (기여전환.sales / 총전환매출);
                        }
                    }
                }
            });

            console.log(`[${vendorItemId}] 광고 집계:`, {
                직접광고비, 기여광고비, 직접광고판매량, 기여매출
            });

            // 부가세 계산 (광고비의 10%)
            const 광고비부가세 = 직접광고비 * 0.1;
            const 부가세포함광고비 = 직접광고비 + 광고비부가세;

            // VAT 계산 (매출*10/11 - 각종VAT)
            // settlementData에서 VAT 관련 필드들 합계
            let 총VAT = 0;
            기간내정산데이터.forEach(item => {
                총VAT += (item.commissionVAT || 0) + (item.shippingFeeVAT || 0) + (item.warehouseFeeVAT || 0);
            });
            const 매출VAT = 총매출액 * 10 / 11;
            const VAT차액 = 매출VAT - 총VAT;

            // 수익 계산 (새로운 공식): 매출 - 각종쿠폰 - 수수료 - 입출고비 - 배송비 - 광고비 - 매입가 - (매출*10/11 - 각종VAT)
            const 직접수익 = 총매출액 - 총쿠폰 - 총판매수수료 - 총입출고비 - 총배송비 - 부가세포함광고비 - 총매입액 - VAT차액;

            // ROAS 및 ROI 계산
            const 직접ROAS = 직접광고비 > 0 ? Math.round((총매출액 / 직접광고비) * 100) / 100 : 0;
            const ROI = 총매입액 > 0 ? Math.round((직접수익 / (총매입액 * 10 / 11)) * 100) : 0;

            // 일반판매량 = 총판매량 - 직접광고판매량
            const 일반판매량 = Math.max(0, 총판매량 - 직접광고판매량);

            const finalResult = {
                ...result,
                총판매량, 총매출액, 총매입액,
                총판매수수료, 총입출고비, 총배송비, 총수수료, 총쿠폰,
                일반판매량, 광고판매량: 직접광고판매량,
                직접광고비: Math.round(직접광고비),
                부가세포함광고비: Math.round(부가세포함광고비),
                광고비부가세: Math.round(광고비부가세),
                기여광고비: Math.round(기여광고비),
                기여매출: Math.round(기여매출),
                총VAT: Math.round(총VAT),
                매출VAT: Math.round(매출VAT),
                VAT차액: Math.round(VAT차액),
                직접수익: Math.round(직접수익),
                직접ROAS: 직접ROAS,
                통합ROAS: 직접ROAS, // V2에서는 직접ROAS와 동일
                ROI
            };

            console.log(`[${vendorItemId}] 최종 결과:`, finalResult);
            return finalResult;
        },

        /**
         * lot FIFO 로 **날짜별** 원가 단가를 낸다 — `{ 'YYYY-MM-DD': 단가 }` (정본 §5 Phase 3-13).
         *
         * ★ 재고를 쓰지 않는다. 입고 목록(lot)과 실제 판매량만으로 계산한다.
         *   재고를 앵커로 쓰면 (1) 셀러가 언제 수집했느냐에 따라 원가가 흔들리고
         *   (2) 월별로 좁혀 보면 '가장 최근에 팔린 몫' 을 그 달 것으로 집어 틀린 값이 나온다.
         *
         * settlementData 는 **조회 기간으로 자르기 전 전체**를 넘긴다.
         * 2월만 보더라도 1월 판매를 알아야 lot 이 얼마나 깎였는지 알 수 있다.
         *
         * ⚠ 2026-09-11 까지 호출부가 이 약속을 안 지키고 **그 날 하루치**를 넘기고 있었다.
         *   그러면 날마다 lot 큐를 처음부터 다시 까서 사실상 첫 lot 단가만 붙었다.
         *   지금은 processSalesLedgerDataV3 가 fifoSales(상품별 전체)를 만들어 넘긴다.
         *   ⇒ **여기에 하루치를 넘기지 마라.** 서버 buildLedgerSummary 와 값이 갈린다.
         *
         * ⚠ 2026-09-14 까지는 기간 단가를 **하나**만 내서 모든 날짜에 똑같이 곱했다.
         *   lot 이 중간에 바뀌는 상품은 전 날짜에 기간 평균이 붙었다(실측: 484→584 상품이
         *   7월 판매도 9월 판매도 똑같이 493). 날짜별로 내면서 조회기간 인자도 없앴다 —
         *   날짜별 단가는 어느 기간으로 조회하든 같은 값이라 자를 이유가 없다.
         *
         * 수량이 붙은 lot 이 하나도 없으면 null → 부르는 쪽이 날짜별 대표값으로 돌아간다.
         * 옛 항목({date, 매입가})만 있는 상품은 영원히 이 경로를 타지 않는다.
         */
        getFifoUnitByDate(productInfo, settlementData) {
            try {
                if (!productInfo || !Array.isArray(productInfo.매입정보)) return null;

                // 날짜별 순수판매량 — 서버 집계(q − rn − rq)와 같은 식으로 맞춘다.
                // 반품이 더 많은 날(음수)은 빼둔다. 단가는 가중평균이라 거의 흔들리지 않고,
                // 되돌림까지 큐에 반영하려면 소진 상태를 저장해야 해서 얻는 것보다 잃는 게 크다
                const byDate = {};
                (settlementData || []).forEach(it => {
                    if (!it.revenueDate) return;
                    const q = it.saleQuantity || 0;
                    const rq = (it.returnQuantity || 0) > 0 ? it.returnQuantity : 0;
                    const net = (q >= 0 ? q : -Math.abs(q)) - rq;
                    byDate[it.revenueDate] = (byDate[it.revenueDate] || 0) + net;
                });
                const 판매내역 = Object.keys(byDate).map(d => ({ date: d, 수량: byDate[d] }));

                // 매입가와 밀크런은 같은 lot 을 따라간다(#112) — { 날짜: { 매입가, 밀크런, 입고일 } }
                return fifoLotByDate(productInfo.매입정보, 판매내역, productInfo.밀크런정보);
            } catch (e) {
                console.error('lot FIFO 계산 오류:', e);
                return null;
            }
        },

        // 매입가를 날짜별로 계산하는 함수
        //
        // ⚠ **가격이 적힌 행만** 본다(lotCost.매입가ByDate, 서버 priceByDate 와 같은 함수).
        //   입고내역이 깔아주는 뼈대 행은 날짜·수량만 있고 가격이 비어 있어서, 그게 판매일
        //   직전에 끼면 그 상품 원가가 조용히 0 이 된다.
        getPurchasePriceByDate(productInfo, revenueDate) {
            const p = 매입가ByDate(productInfo.매입정보, revenueDate);
            if (p > 0) return p;

            // 매입정보에 가격이 하나도 없으면 기존 폴백 그대로
            return productInfo.매입가 || productInfo.purchasePrice || 0;
        },

        // 비용 미확정(#112) — 배송완료 전 주문은 배송비·입출고비 행이 아직 없어 그날 이익이 부풀어 보인다.
        //   입출고비는 출고된 개수마다 붙으므로(쿠팡 요율 개당) 최근 5일 안인데 순판매 대비 입출고비가 70% 미만이면
        //   아직 비용이 다 안 잡힌 날로 본다(실측 rubycoco: 배송 끝난 날 0.83~1.43 로 흔들리고, 미배송 날은 0.38~0.44). 요율을 모르면 배송비·입출고비가 둘 다 0 인 판매일만.
        isCostPending(productInfo, vendorItemId, date, 순수판매량, warehouseTotal, shippingTotal) {
            if (!date || !(순수판매량 > 0)) return false;
            const kst = new Date(Date.now() + 9 * 3600000);
            const 경계 = new Date(kst.getTime() - 5 * 86400000).toISOString().slice(0, 10);
            if (date < 경계) return false;
            const own = String(productInfo.vendorItemId) === String(vendorItemId) ? productInfo
                : ((productInfo.returnProducts || []).find(r => String(r.vendorItemId) === String(vendorItemId))
                    || (productInfo.upBundlingProducts || []).find(b => String(b.vendorItemId) === String(vendorItemId)));
            const rate = own && own.수수료 ? Number(own.수수료.입출고비) || 0 : 0;
            if (rate > 0) return warehouseTotal < 순수판매량 * rate * 1.1 * 0.7;
            return !warehouseTotal && !shippingTotal;
        },

        // 상품별 새로운 지표 계산 (요구사항에 맞게 수정)
        calculateProductMetricsV3(group) {
            // console.log('=== calculateProductMetricsV3 시작 ===');
            // console.log('그룹 데이터:', group);

            const { vendorItemId, settlementData, productInfo } = group;
            // 그 날의 FIFO 단가. 부르는 쪽(processSalesLedgerDataV3)이 상품당 한 번 계산해 넘긴다.
            // 안 넘어오면 null → 아래에서 날짜별 대표 매입가로 떨어진다(다른 호출부 대비)
            const fifo단가 = group.fifo단가 != null ? group.fifo단가 : null;
            const fifo밀크런 = group.fifo밀크런 != null ? group.fifo밀크런 : null;

            if (!productInfo) {
                console.log(`[${vendorItemId}] 상품 정보 없음 - 제외`);
                return null;
            }

            // 정산현황 데이터가 없으면 제외
            if (!settlementData || settlementData.length === 0) {
                console.log(`[${vendorItemId}] 정산현황 데이터 없음 - 제외`);
                return null;
            }

            // console.log(`[${vendorItemId}] 정산현황 데이터:`, settlementData.length, '개');

            // 매출인식일 기준으로 조회 기간 내 데이터만 필터링
            const 기간내정산데이터 = settlementData.filter(item => {
                if (!item.revenueDate) return false;
                const revenueDate = item.revenueDate;
                return revenueDate >= this.startDate && revenueDate <= this.endDate;
            });

            if (기간내정산데이터.length === 0) {
                console.log(`[${vendorItemId}] 기간 내 정산 데이터 없음 - 제외`);
                return null;
            }

            // 기본 상품 정보 (settlementData에서 가져오기)
            const firstSettlement = 기간내정산데이터[0];

            // skuGrade 찾기 (productInfo에서 찾기)
            let skuGrade = '';

            // 1. vendorItemId가 메인 상품의 vendorItemId와 같은 경우에만 메인 상품의 skuGrade 사용
            if (productInfo.vendorItemId == vendorItemId) {
                skuGrade = productInfo.skuGrade || productInfo.listingDetails?.skuGrade || '';
            }

            // 2. 메인 상품이 아니거나 skuGrade가 없으면 returnProducts에서 찾기
            if (!skuGrade && productInfo.returnProducts) {
                const returnProduct = productInfo.returnProducts.find(rp => rp.vendorItemId == vendorItemId);
                if (returnProduct) {
                    skuGrade = returnProduct.skuGrade || '';
                }
            }

            // 3. returnProducts에도 없으면 upBundlingProducts에서 찾기
            if (!skuGrade && productInfo.upBundlingProducts) {
                const bundleProduct = productInfo.upBundlingProducts.find(bundle => bundle.vendorItemId == vendorItemId);
                if (bundleProduct) {
                    skuGrade = bundleProduct.skuGrade || '';
                }
            }

            // 4. 그래도 없으면 기본값 'NEW'
            if (!skuGrade) {
                skuGrade = 'NEW';
            }

            // 정산현황 데이터 집계
            let saleQuantity = 0;
            let saleAmount = 0;
            let coupangDiscount = 0;
            let instantCoupon = 0;
            let downloadCoupon = 0;
            let commission = 0;
            let commissionVAT = 0;
            let shippingFee = 0;
            let shippingFeeVAT = 0;
            let warehouseFee = 0;
            let warehouseFeeVAT = 0;
            let adCost = 0;
            let adCostVAT = 0;
            let saleQuantityFromAd = 0;
            let returnQuantity = 0; // 반품수량
            // 추가 비용 필드
            let storageFee = 0;           // 보관비
            let creturnFee = 0;           // 반품회수재입고비
            let vreturnFee = 0;           // 반출비
            let vreturnShippingFee = 0;   // 반출배송비
            let barcodeFee = 0;           // 바코드비
            let inventoryCompensation = 0; // 재고손실보상

            // console.log(`[${vendorItemId}] 전체 정산데이터: ${settlementData.length}개, 기간내 데이터: ${기간내정산데이터.length}개`);


            기간내정산데이터.forEach(item => {
                const itemSaleQuantity = item.saleQuantity || 0;

                // 기존 데이터 호환성: saleQuantity < 0인 경우 반품으로 처리
                if (itemSaleQuantity < 0) {
                    // 음수 판매량은 반품으로 처리 (saleQuantity에는 더하지 않음)
                    returnQuantity += Math.abs(itemSaleQuantity);
                } else {
                    // 정상 판매 데이터 집계 (양수만)
                    saleQuantity += itemSaleQuantity;
                    saleAmount += item.saleAmount || 0;
                    coupangDiscount += item.coupangDiscount || 0;
                    instantCoupon += item.instantCoupon || 0;
                    downloadCoupon += item.downloadCoupon || 0;
                    commission += item.commission || 0;
                    commissionVAT += item.commissionVAT || 0;
                    shippingFee += item.shippingFee || 0;
                    shippingFeeVAT += item.shippingFeeVAT || 0;
                    warehouseFee += item.warehouseFee || 0;
                    warehouseFeeVAT += item.warehouseFeeVAT || 0;
                    adCost += item.adCost || 0;
                    adCostVAT += item.adCostVAT || 0;
                    saleQuantityFromAd += item.saleQuantityFromAd || 0;
                    // 추가 비용 필드 집계
                    storageFee += item.storageFee || 0;
                    creturnFee += item.creturnFee || 0;
                    vreturnFee += item.vreturnFee || 0;
                    vreturnShippingFee += item.vreturnShippingFee || 0;
                    barcodeFee += item.barcodeFee || 0;
                    inventoryCompensation += item.inventoryCompensation || 0;
                    // item.밀크런비용(입고일 행에 얹힌 입고 총액)은 더하지 않는다(#112) — 아래에서 판매 개수 × lot 원/개로 잡는다
                }

                // 반품 정보 집계 (서버에서 이미 집계된 값)
                if (item.returnQuantity && item.returnQuantity > 0) {
                    returnQuantity += item.returnQuantity;
                }
            });

            // 새로운 계산 로직
            const sellerDiscount = instantCoupon + downloadCoupon;
            const netSales = saleAmount - sellerDiscount;
            const commissionTotal = commission + commissionVAT;
            const shippingTotal = shippingFee + shippingFeeVAT;
            const warehouseTotal = warehouseFee + warehouseFeeVAT;
            const adTotal = adCost + adCostVAT;

            // 정상판매량 (saleQuantity는 이미 정상 판매만 포함)
            const 정상판매량 = saleQuantity;
            // 순수 판매량 = 정상판매량 - 반품수량
            const 순수판매량 = 정상판매량 - returnQuantity;

            // 밀크런비용 = 순수판매량 × 그 판매가 속한 입고 lot 의 «밀크런 총액 ÷ 입고 수량»(#112 사장님 원칙).
            //   예전엔 입고 총액을 입고일 하루 행에 통째로 얹어, 입고일엔 큰 적자·판매일엔 0 이 됐다.
            //   매입가 FIFO 와 같은 lot 을 따라간다(fifoLotByDate). lot 이 없으면 판매일에 들어와 있던 최근 입고값.
            //   묶음은 매입가처럼 묶음수를 곱한다.
            let 밀크런단가 = fifo밀크런 !== null ? fifo밀크런 : 밀크런ByDate(productInfo.밀크런정보, firstSettlement.revenueDate);
            if (productInfo.upBundlingProducts) {
                const bundleForMilk = productInfo.upBundlingProducts.find(bundle => bundle.vendorItemId == vendorItemId);
                if (bundleForMilk) 밀크런단가 = 밀크런단가 * (bundleForMilk.upBundlingSize || 1);
            }
            const milkrunCostTotal = 밀크런단가 * (saleQuantity - returnQuantity);

            // 판매비용합계 = 수수료 + 배송비 + 입출고비 + 보관비 + 반품회수재입고비 + 반출비 + 반출배송비 + 바코드비 + 밀크런비용 + 광고비 - 재고손실보상
            const sellingCosts = commissionTotal + shippingTotal + warehouseTotal + storageFee + creturnFee + vreturnFee + vreturnShippingFee + barcodeFee + milkrunCostTotal + adTotal - inventoryCompensation;
            const contribution = netSales - sellingCosts;

            // 매입원가 계산 — 언제나 '판매일 기준' 매입가를 쓴다.
            // 매입가가 6월 1일부터 바뀌었다면 6월 1일 이후 판매분에만 그 가격이 적용되어야 한다.
            //
            // ⚠️ 옵션(returnProducts)·묶음(upBundlingProducts) 항목에도 매입가 필드가 있지만,
            //    그건 store(store/index.js:664)가 화면 표시용으로 넣어둔 '최신' 매입가라
            //    판매일과 무관하다. 그걸 쓰면 과거 판매분에 현재 매입가가 붙으므로 쓰지 않는다.
            //    옵션·묶음 모두 본체의 매입정보를 공유하고, 묶음만 묶음수를 곱한다.
            //
            // lot 이 있으면 FIFO 로 계산한다(정본 §4 "원가는 lot 단위다").
            // 같은 SKU라도 환율·경로에 따라 원가가 달라서 대표단가 하나로 뭉개면 이익이 틀어진다.
            // lot 이 없으면 null 이 넘어오고, 지금까지 쓰던 날짜별 대표값으로 간다.
            let purchasePrice = fifo단가 !== null
                ? fifo단가
                : this.getPurchasePriceByDate(productInfo, firstSettlement.revenueDate);

            if (purchasePrice > 0 && productInfo.upBundlingProducts) {
                const bundleProduct = productInfo.upBundlingProducts.find(bundle => bundle.vendorItemId == vendorItemId);
                if (bundleProduct) {
                    purchasePrice = purchasePrice * (bundleProduct.upBundlingSize || 1);
                }
            }

            // 매입원가 계산: 순수 판매량 기준 (실제로 판매된 수량만)
            //
            // 매입가를 모르면 원가·부가세·총이익을 계산하지 않는다(null). 원가를 0으로 두고
            // 계산하면 이익이 실제보다 크게 잡히기 때문에, 모르는 건 세지 않는 쪽을 택한다.
            // 이렇게 빠진 금액은 요약장부에 '매입가 미입력' 줄로 따로 보여준다
            const cogs = purchasePrice > 0 ? purchasePrice * 순수판매량 : null;

            // 부가세 계산: (기여이익-매입원가)*1/11
            const vat = (cogs !== null && contribution !== null) ? Math.round((contribution - cogs) * 1 / 11) : null;

            // 총이익(GP) 계산: 기여이익 - 매입원가 - 부가세
            const grossProfit = (cogs !== null && vat !== null) ? contribution - cogs - vat : null;

            // 자연판매수량 계산 (정상판매량 - 광고판매량)
            const naturalSales = 정상판매량 - saleQuantityFromAd;

            // 개당 이익 계산 (순수판매량 기준, 광고비 제외 = 총이익에 광고비를 다시 더해 광고 영향 제거)
            // 재고손실보상은 총이익에만 넣고 개당이익에선 뺀다(#112 사장님 결정 — adDomain.ledgerProfitPerUnit)
            const profitPerUnit = ledgerProfitPerUnit(grossProfit, adTotal, inventoryCompensation, 순수판매량);

            // 마진율 계산
            const grossMargin = grossProfit !== null && netSales > 0 ? (grossProfit / netSales) * 100 : null;

            // ROAS 계산
            const roas = saleQuantityFromAd > 0 && adTotal > 0 ?
                (saleQuantityFromAd * (firstSettlement.salePrice || 0)) / adTotal : null;

            // 광고ROI 계산
            const adROI = profitPerUnit !== null && saleQuantityFromAd > 0 && adTotal > 0 ?
                (profitPerUnit * saleQuantityFromAd) / adTotal : null;

            // ROI 계산
            const roi = grossProfit !== null && cogs !== null && cogs > 0 ?
                (grossProfit / (cogs * 10 / 11)) * 100 : null;

            // 상품명과 옵션명 가져오기
            let productName = '';
            let optionName = '';

            // 1. productInfo에서 먼저 확인
            if (productInfo.listingDetails) {
                productName = productInfo.listingDetails.vendorInventoryName || '';
                optionName = productInfo.listingDetails.vendorInventoryItemName || '';
            }

            // 2. returnProducts에서 확인
            if (!productName && productInfo.returnProducts) {
                const returnProduct = productInfo.returnProducts.find(rp => rp.vendorItemId == vendorItemId);
                if (returnProduct && returnProduct.listingDetails) {
                    productName = returnProduct.listingDetails.vendorInventoryName || '';
                    optionName = returnProduct.listingDetails.vendorInventoryItemName || '';
                }
            }

            // 3. upBundlingProducts에서 확인
            if (!productName && productInfo.upBundlingProducts) {
                const bundleProduct = productInfo.upBundlingProducts.find(bundle => bundle.vendorItemId == vendorItemId);
                if (bundleProduct && bundleProduct.listingDetails) {
                    productName = bundleProduct.listingDetails.vendorInventoryName || '';
                    optionName = bundleProduct.listingDetails.vendorInventoryItemName || '';
                }
            }

            // 4. 정산 데이터에서 확인 (fallback)
            if (!productName && firstSettlement) {
                productName = firstSettlement.productName || '';
                optionName = firstSettlement.optionName || '';
            }

            // 번들 묶음수 (옵션명 x2 배지 표시용)
            let upBundlingSize = 1;
            if (productInfo.upBundlingProducts) {
                const bundleInfoForSize = productInfo.upBundlingProducts.find(b => b.vendorItemId == vendorItemId);
                if (bundleInfoForSize && bundleInfoForSize.upBundlingSize > 1) {
                    upBundlingSize = bundleInfoForSize.upBundlingSize;
                }
            }

            const finalResult = {
                vendorItemId: vendorItemId,
                productId: productInfo.productId || '',
                상품명: `${productName}, ${optionName}`,
                skuGrade: skuGrade,
                upBundlingSize: upBundlingSize,
                saleQuantity: 순수판매량,  // 순수 판매량 = 정상판매량 - 반품수량
                saleAmount: saleAmount,
                coupangDiscount: coupangDiscount,
                instantCoupon: instantCoupon,
                downloadCoupon: downloadCoupon,
                netSales: netSales,
                commissionTotal: commissionTotal,
                shippingTotal: shippingTotal,
                warehouseTotal: warehouseTotal,
                adTotal: adTotal,
                sellingCosts: sellingCosts,
                contribution: contribution,
                cogs: cogs,
                vat: vat,
                grossProfit: grossProfit,
                profitPerUnit: profitPerUnit,
                grossMargin: grossMargin,
                naturalSales: naturalSales,
                saleQuantityFromAd: saleQuantityFromAd,
                returnQuantity: returnQuantity,
                roas: roas,
                adROI: adROI,
                roi: roi,
                // 추가 비용 필드
                storageFee: storageFee,
                creturnFee: creturnFee,
                vreturnFee: vreturnFee,
                vreturnShippingFee: vreturnShippingFee,
                barcodeFee: barcodeFee,
                milkrunCostTotal: milkrunCostTotal,
                inventoryCompensation: inventoryCompensation,
                costPending: this.isCostPending(productInfo, vendorItemId, firstSettlement.revenueDate, 순수판매량, warehouseTotal, shippingTotal)
            };

            // console.log(`[${vendorItemId}] 최종 결과:`, finalResult);
            return finalResult;
        },

        // 요약 데이터 계산 (새로운 구조에 맞게 수정)
        // 총이익은 기타비용이 얹힌 dailyRows 기준으로 본다 — 표와 카드가 어긋나면 안 된다
        calculateSummary() {
            // 일별 데이터에서 전체 합계 계산
            const totalRevenue = this.salesLedgerData.reduce((sum, day) => sum + (day.일별총매출액 || 0), 0);
            const totalNetSales = this.salesLedgerData.reduce((sum, day) => sum + (day.일별순매출액 || 0), 0);
            const totalProfit = this.dailyRows.reduce((sum, day) => sum + (day.일별총수익 || 0), 0);
            // 부가세도 총이익과 같은 dailyRows 기준. 기타비용의 부가세 공제가 반영된 값이라
            // salesLedgerData 원본을 쓰면 카드와 표가 어긋난다
            const totalVat = this.dailyRows.reduce((sum, day) => sum + (day.일별부가세 || 0), 0);

            // 상품별 상세 데이터에서 계산
            let totalAdCost = 0;
            let totalPurchase = 0;
            let totalFees = 0;
            let totalSellingCosts = 0;
            let totalAdRevenue = 0; // 총광고매출
            let totalCompensation = 0; // 재고손실보상 — 총이익에 포함, 개당이익엔 제외(#112) · 요약에 별도 줄

            this.salesLedgerData.forEach(day => {
                day.products.forEach(product => {
                    totalAdCost += product.adTotal || 0;
                    totalPurchase += product.cogs || 0;
                    totalFees += product.commissionTotal + product.shippingTotal + product.warehouseTotal;
                    totalSellingCosts += product.sellingCosts || 0;
                    totalCompensation += product.inventoryCompensation || 0;

                    // 광고매출 = 광고판매량 * 개당매출액
                    const adSales = (product.saleQuantityFromAd || 0) * (product.saleAmount / Math.max(product.saleQuantity, 1));
                    totalAdRevenue += adSales;
                });
            });

            // 총 마진율 = 총수익 / 총매출 * 100
            const totalMarginRate = totalRevenue > 0 ? Math.round((totalProfit / totalRevenue) * 100) : 0;

            // 총 ROAS = 총광고매출 / 총광고비 * 100
            const totalROAS = totalAdCost > 0 ? (totalAdRevenue / totalAdCost) * 100 : 0;

            // 총 ROI = 총수익 / 총매입 * 100
            const totalROI = totalPurchase > 0 ? Math.round((totalProfit / (totalPurchase * 10 / 11)) * 100) : 0;

            // 재고금액 = 조회 상품들의 전체가용재고 금액 합계 (현재 스냅샷, vendorItemId 중복 제외)
            const seenVendorItems = new Set();
            let totalStockAmount = 0;
            this.salesLedgerData.forEach(day => {
                day.products.forEach(product => {
                    const vid = String(product.vendorItemId);
                    if (product.vendorItemId != null && !seenVendorItems.has(vid)) {
                        seenVendorItems.add(vid);
                        totalStockAmount += this.getCoverage(product.vendorItemId).totalStockAmount || 0;
                    }
                });
            });

            this.summary = {
                totalRevenue,
                totalNetSales,
                totalProfit,
                totalVat,
                totalAdCost,
                totalPurchase,
                totalFees,
                totalSellingCosts,
                totalCompensation,
                totalMarginRate,
                totalROAS,
                totalROI,
                totalStockAmount
            };

            // 계산 검증 로그
            console.log('=== 판매장부 계산 검증 (새로운 구조) ===');
            console.log('총매출액:', totalRevenue.toLocaleString());
            console.log('총순매출액:', totalNetSales.toLocaleString());
            console.log('총매입액:', totalPurchase.toLocaleString());
            console.log('총판매비용:', totalSellingCosts.toLocaleString());
            console.log('총광고비:', totalAdCost.toLocaleString());
            console.log('총수익:', totalProfit.toLocaleString());
            console.log('총ROI:', totalROI + '%');

            // 쿠폰 및 할인 확인
            let totalCoupons = 0;
            this.salesLedgerData.forEach(day => {
                day.products.forEach(product => {
                    totalCoupons += (product.instantCoupon || 0) + (product.downloadCoupon || 0);
                });
            });
            console.log('총쿠폰할인:', totalCoupons.toLocaleString());

            const 계산검증 = totalPurchase + totalSellingCosts + totalProfit;
            console.log('계산검증 (매입+판매비용+수익):', 계산검증.toLocaleString());
            console.log('총순매출액과 비교:', totalNetSales.toLocaleString());
            console.log('차이:', (totalNetSales - 계산검증).toLocaleString());

            // 수익 공식 검증
            console.log('--- 수익 공식 검증 ---');
            console.log('수익 = 순매출 - 판매비용 - 매입원가');
            const 공식검증수익 = totalNetSales - totalSellingCosts - totalPurchase;
            console.log('공식으로 계산한 수익:', 공식검증수익.toLocaleString());
            console.log('실제 수익과 차이:', (totalProfit - 공식검증수익).toLocaleString());
            console.log('총ROAS:', totalROAS.toFixed(1) + '%');
            console.log('=====================================');

            // 요약 파이차트 생성
            this.generateSummaryChart(totalProfit, totalAdRevenue);
        },

        // 요약 파이차트 생성 (전체수익 대비 광고수익)
        generateSummaryChart(totalProfit, totalAdRevenue) {
            console.log('=== 요약 파이차트 생성 ===');
            console.log('totalProfit:', totalProfit);
            console.log('totalAdRevenue:', totalAdRevenue);
            console.log('totalAdCost:', this.summary.totalAdCost);

            this.showSummaryChart = false;

            if (totalProfit <= 0) {
                console.log('totalProfit이 0 이하여서 차트 생성 중단');
                return;
            }

            // 광고수익 계산 (광고매출에서 광고비를 뺀 순수익)
            const adProfit = Math.max(0, totalAdRevenue - this.summary.totalAdCost);
            const naturalProfit = Math.max(0, totalProfit - adProfit);

            console.log('adProfit:', adProfit);
            console.log('naturalProfit:', naturalProfit);

            // 비율 계산
            const adProfitRatio = totalProfit > 0 ? (adProfit / totalProfit) * 100 : 0;
            const naturalProfitRatio = totalProfit > 0 ? (naturalProfit / totalProfit) * 100 : 100;

            console.log('adProfitRatio:', adProfitRatio);
            console.log('naturalProfitRatio:', naturalProfitRatio);

            // NaN 체크
            if (isNaN(adProfitRatio) || isNaN(naturalProfitRatio)) {
                console.log('비율 계산에서 NaN 발생, 차트 생성 중단');
                return;
            }

            // 테스트용 고정 데이터로 시작
            this.summaryChartOptions = {
                chart: {
                    type: 'pie',
                    height: 150,
                    width: 150
                },
                series: [60, 40], // 고정값으로 테스트
                labels: ['자연수익', '광고매출'],
                colors: ['#28a745', '#ffc107'],
                legend: {
                    show: true,
                    position: 'bottom',
                    fontSize: '10px'
                },
                dataLabels: {
                    enabled: true,
                    style: {
                        fontSize: '10px'
                    }
                }
            };

            console.log('고정 데이터로 차트 옵션 설정 완료:', this.summaryChartOptions);

            console.log('파이차트 옵션 생성 완료');
            console.log('series:', [naturalProfitRatio, adProfitRatio]);

            setTimeout(() => {
                this.showSummaryChart = true;
                console.log('showSummaryChart = true 설정 완료');
                console.log('this.showSummaryChart:', this.showSummaryChart);
            }, 100);
        },

        // ===== 판매장부 리스크 필터 =====
        // 판정식은 서버 ledgerRiskFlags(server.js)와 글자 그대로 같아야 한다 — 한쪽만 고치면 대시보드 개수와 화면 필터가 갈린다.
        //   back(x) = x·10/11 — 비용을 되돌리면 부가세((기여−원가)/11)도 같이 늘어 총이익은 x·10/11 늘어난다
        //   기본이익 B = GP + back(oneoff)   oneoff = 반출비 + 반출배송비 + 밀크런비, GP 는 기타비용 반영 전
        //
        // 상품×일 행(dailyData)에서 서버와 같은 모양 { S, R, GP, oneoff, ad, creturn, storage, adSales,
        //   adRevenue, cogs } 을 기간 전체 / 앞 절반 / 뒤 절반으로 합산한다.
        //   - 매입가 없는 행이 하루라도 있으면 그 구간 cogs = null (GP 는 매입가 있는 행만 더한다)
        //   - 광고매출 = 행마다 광고판매량 × 판매액 / max(순수판매량, 1)
        //   - 앞 절반 = 시작일 ~ 시작일+floor(n/2)−1, 뒤 절반 = 그 다음날 ~ 종료일
        ledgerRiskAggregate(item) {
            const n = this.queriedDayCount;
            const mid = new Date(new Date(this.queriedStartDate + 'T00:00:00Z').getTime() + (Math.floor(n / 2) - 1) * 86400000)
                .toISOString().slice(0, 10);
            const blank = () => ({
                S: 0, R: 0, GP: 0, oneoff: 0, ad: 0, creturn: 0, storage: 0,
                adSales: 0, adRevenue: 0, cogs: 0, cogsMissing: false
            });
            const add = (a, d) => {
                const 순수 = d.saleQuantity || 0;
                a.S += 순수;
                a.R += d.returnQuantity || 0;
                a.oneoff += (d.vreturnFee || 0) + (d.vreturnShippingFee || 0) + (d.milkrunCostTotal || 0);
                a.ad += d.adTotal || 0;
                a.creturn += d.creturnFee || 0;
                a.storage += d.storageFee || 0;
                a.adSales += d.saleQuantityFromAd || 0;
                a.adRevenue += (d.saleQuantityFromAd || 0) * ((d.saleAmount || 0) / Math.max(순수, 1));
                if (d.cogs !== null && d.cogs !== undefined) {
                    a.cogs += d.cogs;
                    a.GP += d.grossProfit || 0;
                } else {
                    a.cogsMissing = true;
                }
            };
            const full = blank();
            const first = blank();
            const second = blank();
            (item.dailyData || []).forEach(d => {
                if (d.date < this.queriedStartDate || d.date > this.queriedEndDate) return;
                add(full, d);
                add(d.date <= mid ? first : second, d);
            });
            [full, first, second].forEach(a => {
                if (a.cogsMissing) a.cogs = null;
            });
            return { ...full, days: n, first, second };
        },

        // 한 상품의 리스크 키 배열. 버튼·카운트·필터가 모두 이것만 부른다
        ledgerRiskFlags(item) {
            const agg = this.ledgerRiskAggregate(item);
            const back = x => x * 10 / 11;
            const f = [];
            const S = agg.S, R = agg.R, G = S + R;
            const B = agg.GP + back(agg.oneoff);
            // 빨강 — 적자 원인 폭포 (한 상품에 하나만)
            if (S >= 1 && agg.cogs != null && B < 0) {
                // 광고비만 빼면 흑자인 상품은 원인 분류에서 빼고 아래 광고 손익 판정(lossByAd)이 잡는다
                if (B + back(agg.ad) >= 0) { /* lossByAd 로 넘긴다 */ }
                else if (B + back(agg.ad + agg.creturn) >= 0) f.push('lossByReturn');
                else if (B + back(agg.ad + agg.creturn + agg.storage) >= 0) f.push('lossByStorage');
                else f.push('lossStructural');
            }
            // 빨강 — 별도
            if (S <= 0 && agg.storage > 0) f.push('storageOnly');
            if (agg.cogs == null) f.push('noPurchasePrice');
            // 광고 손익 — 빨강 광고로 적자 / 노랑 반품 반영 시 광고적자 (노랑 ⊇ 빨강)
            if (S >= 1 && agg.cogs != null && agg.ad > 0) {
                const r = G > 0 ? R / G : 0;
                const u = (B + back(agg.ad)) / S;
                if (agg.adSales * u - back(agg.ad) < 0) f.push('lossByAd');
                if (agg.adSales * (1 - r) * u - back(agg.ad) < 0) f.push('adLossAfterReturn');
            }
            // 노랑 — ROI / ROAS 하락 (앞뒤 절반 비교, 14일 이상)
            if (agg.days >= 14 && agg.first && agg.second) {
                const h1 = agg.first, h2 = agg.second;
                if (h1.S >= 1 && h2.S >= 1 && h1.cogs > 0 && h2.cogs > 0) {
                    const roi1 = (h1.GP + back(h1.oneoff)) / (h1.cogs * 10 / 11);
                    const roi2 = (h2.GP + back(h2.oneoff)) / (h2.cogs * 10 / 11);
                    if (roi2 < roi1 - Math.abs(roi1) * 0.1) f.push('roiDecline');
                }
                if (h1.ad > 0 && h2.ad > 0) {
                    const roas1 = h1.adRevenue / h1.ad;
                    const roas2 = h2.adRevenue / h2.ad;
                    if (roas2 < roas1 * 0.9) f.push('roasDecline');
                }
            }
            return f;
        },

        // 리스크 버튼 — 누르면 상품별 보기로 바꾸고 해당 상품만, 다시 누르면 해제
        toggleLedgerRisk(key) {
            if (this.ledgerRiskFilter === key) {
                this.ledgerRiskFilter = null;
                return;
            }
            this.ledgerRiskFilter = key;
            if (!this.isProductView) {
                this.isProductView = true;
                this.toggleView();
            }
        },

        // 보기 모드 전환
        toggleView() {
            console.log('보기 모드 전환:', this.isProductView ? '상품별' : '일별');

            // 테이블 재렌더링을 위해 약간의 지연 후 이벤트 재설정
            this.$nextTick(() => {
                setTimeout(() => {
                    this.setupTableClickEvents();
                }, 100);
            });
        },

        // 날짜 클릭 시 해당 날짜의 상품별 상세 모달 표시
        showDateDetail(dateItem) {
            console.log('날짜 클릭:', dateItem);
            this.modalTitle = `${dateItem.date} 상품별 상세`;

            // 상품별 헤더 사용 (고정 컬럼은 computed에서 처리)
            this.modalHeaders = this.productHeaders;

            // 상품 데이터에 썸네일과 상품명 정보 추가
            this.modalData = dateItem.products.map((product, index) => {
                const productNameParts = product.상품명 ? product.상품명.split(', ') : ['', ''];
                const 판매상품명 = productNameParts[0] || '';
                const 옵션명 = productNameParts[1] || '';

                // 개당이익(광고제외) = (총이익 + 광고비 − 재고손실보상) ÷ 판매수량, 광고매출 단가
                const profitPerUnit = product.saleQuantity > 0 ? ledgerProfitPerUnit(product.grossProfit || 0, product.adTotal, product.inventoryCompensation, product.saleQuantity) : 0;
                const unitPrice = product.saleQuantity > 0 ? (product.saleAmount || 0) / product.saleQuantity : 0;

                const coverage = this.getCoverage(product.vendorItemId);
                return {
                    ...product,
                    date: dateItem.date,  // 메모 스코프 판별용 (date_product)
                    uniqueKey: `${dateItem.date}_${product.vendorItemId || 'unknown'}_${index}`, // 고유 키 추가
                    썸네일: this.getProductThumbnail(product.vendorItemId),
                    coupangStock: coverage.coupangStock,
                    purchasePrice: coverage.purchasePrice,
                    판매상품명: 판매상품명,
                    옵션명: 옵션명,
                    profitPerUnit: Math.round(profitPerUnit),
                    vat: Math.round(product.vat || 0),
                    grossProfit: Math.round(product.grossProfit || 0),
                    cogs: Math.round(product.cogs || 0),
                    // ROAS 는 여기서 다시 계산한다. V3 가 넣어준 값은 정산에 저장되지 않는 필드를
                    // 곱해 만든 것이라 광고를 집행해도 늘 0 이었다. 상품별 표·엑셀과 같은 식
                    // (판매액 ÷ 판매수량) 으로 맞춘다. 값이 없으면 null 이라 화면에 '-' 로 나온다.
                    roas: (product.saleQuantityFromAd > 0 && product.adTotal > 0)
                        ? (product.saleQuantityFromAd * unitPrice) / product.adTotal : null,
                    adTotalProfit: Math.round((product.saleQuantityFromAd || 0) * unitPrice), // 광고매출
                    adProfit: Math.round((product.saleQuantityFromAd || 0) * profitPerUnit),  // 광고이익
                    adNetProfit: Math.round(((product.saleQuantityFromAd || 0) * profitPerUnit) - (product.adTotal || 0)) // 광고이익 - 광고비
                };
            }).sort((a, b) => b.saleAmount - a.saleAmount); // 매출 내림차순

            // 상품별 파레토 차트 생성
            this.generateModalProductChart(this.modalData);

            this.showModal = true;
        },

        // 상품 클릭 시 해당 상품의 일별 상세 모달 표시
        showProductDetail(productItem) {
            console.log('상품 클릭:', productItem);
            this.modalTitle = `${productItem.상품명} 일별 상세`;

            // 일별 차트 모드 초기화 (금액 모드로 시작)
            this.dailyChartMode = 'amount';

            // 상단 재고 패널 데이터 (현재 스냅샷) + 그래프 펼침 상태 초기화
            this.modalCoverage = this.getCoverage(productItem.vendorItemId);
            this.modalChartCollapsed = false;

            // 일별 헤더에서 날짜 컬럼을 첫 번째로 하고 상품 정보 컬럼들 제거, 고정 컬럼은 날짜로 설정
            this.modalHeaders = [
                { text: '날짜', value: 'date', align: 'center', width: 120 },
                ...this.productHeaders.filter(h =>
                    !['썸네일', '상품명', 'productId', 'vendorItemId', 'skuGrade', 'coupangStock', 'purchasePrice'].includes(h.value)
                )
            ];

            // 일별 데이터에 반올림 처리
            this.modalData = productItem.dailyData.map((dailyItem, index) => {
                // 개당이익(광고제외) = (총이익 + 광고비 − 재고손실보상) ÷ 판매수량, 광고매출 단가
                const profitPerUnit = dailyItem.saleQuantity > 0 ? ledgerProfitPerUnit(dailyItem.grossProfit || 0, dailyItem.adTotal, dailyItem.inventoryCompensation, dailyItem.saleQuantity) : 0;
                const unitPrice = dailyItem.saleQuantity > 0 ? (dailyItem.saleAmount || 0) / dailyItem.saleQuantity : 0;

                return {
                    ...dailyItem,
                    vendorItemId: productItem.vendorItemId,  // 메모 스코프 판별용
                    uniqueKey: `${dailyItem.date}_${productItem.vendorItemId}_${index}`, // 고유 키 추가
                    profitPerUnit: Math.round(profitPerUnit),
                    vat: Math.round(dailyItem.vat || 0),
                    grossProfit: Math.round(dailyItem.grossProfit || 0),
                    cogs: Math.round(dailyItem.cogs || 0),
                    // ROAS 는 여기서 다시 계산한다. V3 가 넣어준 값은 정산에 저장되지 않는 필드를
                    // 곱해 만든 것이라 광고를 집행해도 늘 0 이었다. 상품별 표·엑셀과 같은 식
                    // (판매액 ÷ 판매수량) 으로 맞춘다. 값이 없으면 null 이라 화면에 '-' 로 나온다.
                    roas: (dailyItem.saleQuantityFromAd > 0 && dailyItem.adTotal > 0)
                        ? (dailyItem.saleQuantityFromAd * unitPrice) / dailyItem.adTotal : null,
                    adTotalProfit: Math.round((dailyItem.saleQuantityFromAd || 0) * unitPrice), // 광고매출
                    adProfit: Math.round((dailyItem.saleQuantityFromAd || 0) * profitPerUnit),  // 광고이익
                    adNetProfit: Math.round(((dailyItem.saleQuantityFromAd || 0) * profitPerUnit) - (dailyItem.adTotal || 0)) // 광고이익 - 광고비
                };
            }).sort((a, b) => b.date.localeCompare(a.date)); // 날짜 내림차순

            // 일자별 지표 차트 생성
            this.generateModalDailyChart(this.modalData);

            this.showModal = true;
        },

        // 일별 차트 모드 변경 시 차트 업데이트
        updateDailyChart() {
            this.generateModalDailyChart(this.modalData);
        },

        // 모달 상품별 파레토 차트 생성
        generateModalProductChart(products) {
            this.showModalChart = false;

            if (!products || products.length === 0) {
                return;
            }

            // TOP 50 상품으로 제한
            const top50Products = products.slice(0, 50);

            const categories = top50Products.map(product => {
                const shortName = product.판매상품명.length > 8
                    ? product.판매상품명.substring(0, 8) + '...'
                    : product.판매상품명;
                return shortName;
            });

            // 매출 데이터
            const saleData = top50Products.map(product => Math.round(product.saleAmount));

            // 파레토 누적 비율 계산
            const total = saleData.reduce((sum, val) => sum + val, 0);
            let cumulative = 0;
            const cumulativePercentage = saleData.map(value => {
                cumulative += value;
                return (cumulative / total) * 100;
            });

            this.modalChartOptions = {
                chart: {
                    type: 'line',
                    height: 400,
                    width: '100%',
                    toolbar: { show: false }
                },
                plotOptions: {
                    bar: {
                        horizontal: false,
                        columnWidth: '70%',
                        endingShape: 'rounded'
                    }
                },
                dataLabels: { enabled: false },
                stroke: {
                    show: true,
                    width: [0, 2],
                    colors: ['transparent', '#ff6b6b']
                },
                xaxis: {
                    categories: categories,
                    labels: {
                        rotate: -45,
                        style: { fontSize: '8px' },
                        maxHeight: 80
                    }
                },
                yaxis: [
                    {
                        title: { text: '매출액 (원)' },
                        labels: {
                            formatter: function (val) {
                                return val ? val.toLocaleString() : '0';
                            }
                        }
                    },
                    {
                        opposite: true,
                        title: { text: '누적 비율 (%)' },
                        min: 0,
                        max: 100,
                        labels: {
                            formatter: function (val) {
                                return val.toFixed(0) + '%';
                            }
                        }
                    }
                ],
                fill: { opacity: [1, 1] },
                tooltip: {
                    shared: true,
                    intersect: false,
                    y: [
                        {
                            formatter: function (val) {
                                return val ? val.toLocaleString() + '원' : '0원';
                            }
                        },
                        {
                            formatter: function (val) {
                                return val.toFixed(1) + '%';
                            }
                        }
                    ]
                },
                series: [
                    {
                        name: '매출액',
                        type: 'column',
                        data: saleData,
                        color: '#007bff'
                    },
                    {
                        name: '누적 비율',
                        type: 'line',
                        yAxisIndex: 1,
                        data: cumulativePercentage,
                        color: '#ff6b6b'
                    }
                ],
                legend: {
                    position: 'top',
                    horizontalAlign: 'center'
                }
            };

            setTimeout(() => {
                this.showModalChart = true;
            }, 100);
        },

        // 모달 일자별 지표 차트 생성
        generateModalDailyChart(dailyData) {
            this.showModalChart = false;
            this.showModalPieChart = false;

            if (!dailyData || dailyData.length === 0) {
                return;
            }

            // 날짜 오름차순으로 정렬
            const sortedData = [...dailyData].sort((a, b) => a.date.localeCompare(b.date));

            const categories = sortedData.map(item => item.date);

            // 금액 모드와 수량 모드에 따라 다른 데이터 사용
            if (this.dailyChartMode === 'amount') {
                // 금액 모드: 순매출, 순이익, 광고매출, 광고이익
                const netSalesData = sortedData.map(item => Math.round(item.netSales || 0));
                const grossProfitData = sortedData.map(item => Math.round(item.grossProfit || 0));
                const adSalesData = sortedData.map(item => Math.round((item.saleQuantityFromAd || 0) * (item.saleAmount / Math.max(item.saleQuantity, 1))));
                const adProfitData = sortedData.map(item => Math.round(item.adProfit || 0));

                this.modalChartOptions = {
                    chart: {
                        type: 'line',
                        height: 400,
                        width: '100%',
                        toolbar: { show: false }
                    },
                    dataLabels: { enabled: false },
                    stroke: {
                        curve: 'smooth',
                        width: 2
                    },
                    xaxis: {
                        categories: categories,
                        labels: {
                            rotate: -45,
                            style: { fontSize: '9px' }
                        }
                    },
                    yaxis: {
                        title: { text: '금액 (원)' },
                        labels: {
                            formatter: function (val) {
                                return val ? val.toLocaleString() : '0';
                            }
                        }
                    },
                    tooltip: {
                        shared: true,
                        intersect: false,
                        y: {
                            formatter: function (val) {
                                return val ? val.toLocaleString() + '원' : '0원';
                            }
                        }
                    },
                    series: [
                        {
                            name: '순매출',
                            data: netSalesData,
                            color: '#007bff'
                        },
                        {
                            name: '순이익',
                            data: grossProfitData,
                            color: '#28a745'
                        },
                        {
                            name: '광고매출',
                            data: adSalesData,
                            color: '#ffc107'
                        },
                        {
                            name: '광고이익',
                            data: adProfitData,
                            color: '#dc3545'
                        }
                    ],
                    legend: {
                        position: 'top',
                        horizontalAlign: 'center'
                    }
                };


            } else {
                // 수량 모드: 전체판매량, 자연판매량, 광고판매량, 반품수량
                const totalQuantityData = sortedData.map(item => item.saleQuantity || 0);
                const naturalSalesData = sortedData.map(item => item.naturalSales || 0);
                const adSalesQuantityData = sortedData.map(item => item.saleQuantityFromAd || 0);
                const returnQuantityData = sortedData.map(item => item.returnQuantity || 0);

                this.modalChartOptions = {
                    chart: {
                        type: 'line',
                        height: 400,
                        width: '100%',
                        toolbar: { show: false }
                    },
                    dataLabels: { enabled: false },
                    stroke: {
                        curve: 'smooth',
                        width: 2
                    },
                    xaxis: {
                        categories: categories,
                        labels: {
                            rotate: -45,
                            style: { fontSize: '9px' }
                        }
                    },
                    yaxis: {
                        title: { text: '수량 (개)' },
                        labels: {
                            formatter: function (val) {
                                return val ? val.toLocaleString() : '0';
                            }
                        }
                    },
                    tooltip: {
                        shared: true,
                        intersect: false,
                        y: {
                            formatter: function (val) {
                                return val ? val.toLocaleString() + '개' : '0개';
                            }
                        }
                    },
                    series: [
                        {
                            name: '전체판매량',
                            data: totalQuantityData,
                            color: '#007bff'
                        },
                        {
                            name: '자연판매량',
                            data: naturalSalesData,
                            color: '#28a745'
                        },
                        {
                            name: '광고판매량',
                            data: adSalesQuantityData,
                            color: '#ffc107'
                        },
                        {
                            name: '반품수량',
                            data: returnQuantityData,
                            color: '#dc3545'
                        }
                    ],
                    legend: {
                        position: 'top',
                        horizontalAlign: 'center'
                    }
                };


            }

            setTimeout(() => {
                this.showModalChart = true;
            }, 100);
        },

        // growthDB에서 썸네일 가져오기
        // 재고 스냅샷 로드 (growthScm이 저장한 InventoryCoverage)
        buildInventoryCoverageMap(coverage) {
            const map = {};
            (coverage || []).forEach(c => {
                if (c.vendorItemId != null) map[String(c.vendorItemId)] = c;
            });
            this.inventoryCoverageMap = map;
            console.log('재고 스냅샷 로드:', Object.keys(map).length, '건');
        },
        // loadLedgerExtras 가 이미 받아뒀으면 그걸 쓰고, 아직이면(예: 실패) 그때만 다시 받는다
        async loadInventoryCoverage() {
            if (this.ledgerExtrasPromise) {
                try { await this.ledgerExtrasPromise; } catch (e) { console.error('[판매장부] 부가 데이터 대기 실패', e); }
            }
            if (this.inventoryCoverage.length) {
                this.buildInventoryCoverageMap(this.inventoryCoverage);
                return;
            }
            try {
                const result = await axios.post('/api/getInventoryCoverage');
                const coverage = (result.data && Array.isArray(result.data.coverage)) ? result.data.coverage : [];
                this.inventoryCoverage = coverage;
                this.buildInventoryCoverageMap(coverage);
            } catch (e) {
                console.error('재고 스냅샷 로드 실패:', e);
                this.inventoryCoverageMap = {};
            }
        },

        // vendorItemId로 재고 스냅샷 조회 (없으면 0으로 채운 기본값)
        getCoverage(vendorItemId) {
            const c = this.inventoryCoverageMap[String(vendorItemId)];
            let purchasePrice = c && c.purchasePrice != null ? c.purchasePrice : 0;

            // 번들·반품재판매 옵션은 재고 스냅샷에 자기 행이 없어서 매입가가 0 으로 떨어진다
            // (화면엔 '-', 엑셀엔 0). 손익 계산(calculateProductMetricsV3)은 이미
            // 본체 매입가 × 묶음수량을 쓰고 있으므로, 표시값도 같은 규칙으로 맞춘다.
            //
            // ⚠ 재고(coupangStock/totalStock)는 물려받지 않는다. 번들은 독립 재고가 없어
            //   부모 재고를 그대로 보여주면 있지도 않은 수량이 찍힌다.
            if (!purchasePrice) {
                const bundle = this.bundleParentOf[String(vendorItemId)];
                if (bundle) {
                    const pc = this.inventoryCoverageMap[bundle.parentVid];
                    if (pc && pc.purchasePrice) purchasePrice = pc.purchasePrice * bundle.size;
                }
            }

            return {
                coupangStock: c && c.coupangStock != null ? c.coupangStock : 0,
                coupangStockDays: c && c.coupangStockDays != null ? c.coupangStockDays : 0,
                coupangStockAmount: c && c.coupangStockAmount != null ? c.coupangStockAmount : 0,
                totalStock: c && c.totalStock != null ? c.totalStock : 0,
                totalStockDays: c && c.stockDays != null ? c.stockDays : 0,
                totalStockAmount: c && c.totalStockAmount != null ? c.totalStockAmount : 0,
                purchasePrice: purchasePrice
            };
        },

        getProductThumbnail(vendorItemId) {
            const growthDB = this.$store.state.growthdb;

            // 기본 vendorItemId로 찾기
            let productInfo = growthDB.find(p => p.vendorItemId == vendorItemId);

            // returnProducts에서 찾기
            if (!productInfo) {
                productInfo = growthDB.find(p =>
                    p.returnProducts && p.returnProducts.some(rp => rp.vendorItemId == vendorItemId)
                );
            }

            // upBundlingProducts에서 찾기 (번들 상품의 경우 기본 상품 이미지 사용)
            if (!productInfo) {
                productInfo = growthDB.find(p =>
                    p.upBundlingProducts && p.upBundlingProducts.some(bundle => bundle.vendorItemId == vendorItemId)
                );
            }

            if (productInfo && productInfo.썸네일) {
                return productInfo.썸네일;
            } else if (productInfo && productInfo.listingDetails && productInfo.listingDetails.mainImagePath) {
                return "http://image1.coupangcdn.com/image/" + productInfo.listingDetails.mainImagePath;
            }

            return '/img/no-image.png'; // 기본 이미지
        },

        // 모달 스타일 강제 적용 (growthScm 방식 참고)
        forceModalStyle() {
            this.$nextTick(() => {
                setTimeout(() => {
                    const modal = document.querySelector('.sales-ledger-modal .modal-dialog');
                    const modalContent = document.querySelector('.sales-ledger-modal .modal-content');
                    const modalBody = document.querySelector('.sales-ledger-modal .modal-body');

                    if (modal) {
                        modal.style.maxWidth = '98vw';
                        modal.style.width = '98vw';
                        modal.style.height = '96vh';
                        modal.style.maxHeight = '96vh';
                        modal.style.margin = '1vh auto';
                    }

                    if (modalContent) {
                        modalContent.style.height = '100%';
                        modalContent.style.display = 'flex';
                        modalContent.style.flexDirection = 'column';
                        modalContent.style.border = 'none';
                        modalContent.style.borderRadius = '0';
                    }

                    if (modalBody) {
                        modalBody.style.flex = '1';
                        modalBody.style.overflowY = 'auto';
                        modalBody.style.padding = '0.5rem';
                        modalBody.style.maxHeight = 'none';
                    }

                    console.log('판매장부 모달 스타일 강제 적용 완료');
                }, 100);
            });
        },

        // 테이블 행 클릭 이벤트 처리 (mounted에서 이벤트 리스너 추가)
        setupTableClickEvents() {
            this.$nextTick(() => {
                // 일별 테이블 클릭 이벤트
                const dailyTable = document.querySelector('#data_table_daily .v-data-table tbody');
                if (dailyTable) {
                    dailyTable.addEventListener('click', (e) => {
                        const row = e.target.closest('tr');
                        if (row && row.dataset.index !== undefined) {
                            const index = parseInt(row.dataset.index);
                            const item = this.salesLedgerData[index];
                            if (item) {
                                this.showDateDetail(item);
                            }
                        }
                    });
                }

                // 상품별 테이블 클릭 이벤트
                const productTable = document.querySelector('#data_table_product .v-data-table tbody');
                if (productTable) {
                    productTable.addEventListener('click', (e) => {
                        const row = e.target.closest('tr');
                        if (row && row.dataset.index !== undefined) {
                            const index = parseInt(row.dataset.index);
                            const item = this.productViewData[index];
                            if (item) {
                                this.showProductDetail(item);
                            }
                        }
                    });
                }
            });
        },

        // 정산현황수집 (조회기간 기준)
        // isRecollect=false : 일반 수집 (DB에 이미 있는 날짜는 건너뜀, 최근 recollectDays일은 항상 재수집)
        // isRecollect=true  : 선택 기간 강제 재수집 (오류 데이터 보정용 — 덮어쓰기)
        async collectSettlementData(isRecollect) {
            console.log('정산현황수집 시작:', isRecollect ? '선택 기간 재수집' : '정산현황수집');

            // 조회기간 유효성 검사 (일반/재수집 공통)
            if (!this.validateDateRange()) {
                return;
            }

            const dateRange = {
                startDate: this.startDate,
                endDate: this.endDate
            };

            if (isRecollect) {
                // 선택 기간 강제 재수집 — 기존 데이터 덮어쓰기
                if (!confirm(`${this.startDate} ~ ${this.endDate} 기간의 정산데이터를 강제로 재수집하시겠습니까?\n기존 데이터가 덮어씌워집니다.`)) {
                    return;
                }

                console.log('선택 기간 강제 재수집 시작:', dateRange);

                this.beginOwnCollection();
                window.postMessage({
                    greeting: "manualCollect",
                    itemKey: "rocketGrowthSalesLedger",
                    companyid: this.$store.state.user.companyid,
                    suppliercode: this.$store.state.user.suppliercode,
                    options: { dateRange: dateRange, forceFullRange: true }
                }, "*");

                this.showspinner = true;
                this.showlist = false;
                this.loadingtext = '선택 기간 재수집 중...';
            } else {
                // 일반 수집 — background에서 DB 조회 후 실제 수집 범위 재계산 (이미 있는 날짜는 스킵)
                console.log('조회기간 기준 수집 범위:', dateRange);

                this.beginOwnCollection();
                window.postMessage({
                    greeting: "manualCollect",
                    itemKey: "rocketGrowthSalesLedger",
                    companyid: this.$store.state.user.companyid,
                    suppliercode: this.$store.state.user.suppliercode,
                    options: { dateRange: dateRange, recollectDays: this.recollectDays }
                }, "*");

                this.showspinner = true;
                this.showlist = false;
                this.loadingtext = '정산현황 수집 중...';
            }
        },

        // 조회기간 유효성 검사 (31일 초과 시 알림)
        validateDateRange() {
            console.log('=== 조회기간 유효성 검사 ===');
            console.log('조회 시작일:', this.startDate);
            console.log('조회 종료일:', this.endDate);

            const startDate = new Date(this.startDate + 'T00:00:00');
            const endDate = new Date(this.endDate + 'T00:00:00');

            // 날짜 차이 계산 (일 단위)
            const timeDiff = endDate.getTime() - startDate.getTime();
            const daysDiff = Math.ceil(timeDiff / (1000 * 3600 * 24)) + 1; // +1은 시작일 포함

            console.log('조회 기간 일수:', daysDiff);

            // 31일을 넘어가는 경우 알림
            if (daysDiff > 31) {
                alert(`조회 기간이 ${daysDiff}일로 31일을 초과합니다.\n정산현황 수집은 최대 31일까지만 가능합니다.\n조회 기간을 31일 이내로 변경해주세요.`);
                console.log('조회 기간 초과로 수집 중단');
                return false;
            }

            console.log('조회 기간 유효성 검사 통과');
            console.log('========================');
            return true;
        },

        // 정산현황 수집 날짜 범위 계산 (기존 함수 - 재수집용으로 유지)
        async calculateSettlementDateRange() {
            try {
                // 기존 정산 데이터 확인 (일단 판매장부 데이터로 확인)
                const result = await axios.post('/api/getLatestSettlementData', {
                    companyid: this.$store.state.user.companyid
                });

                if (result.data.res === 'success' && result.data.latestDate) {
                    // 마지막 수집 날짜부터 어제까지
                    const latestDate = new Date(result.data.latestDate + 'T00:00:00');
                    const todayStr = this.getTodayString();
                    const yesterdayStr = this.generateDatesFromUpdateDate(todayStr)[29]; // 마지막 날짜가 어제
                    const yesterday = new Date(yesterdayStr + 'T00:00:00');

                    // 마지막 수집 날짜 다음날부터 시작
                    const startDate = new Date(latestDate);
                    startDate.setDate(startDate.getDate() + 1);

                    console.log('=== 정산현황 날짜 범위 계산 디버깅 ===');
                    console.log('latestDate (마지막 수집):', this.formatDateForCollection(latestDate));
                    console.log('startDate (시작일):', this.formatDateForCollection(startDate));
                    console.log('yesterday (어제):', this.formatDateForCollection(yesterday));

                    // 수집할 날짜가 있는지 확인
                    if (startDate <= yesterday) {
                        const dateRange = {
                            startDate: this.formatDateForCollection(startDate),
                            endDate: this.formatDateForCollection(yesterday)
                        };
                        console.log('부분 수집 범위:', dateRange);
                        return dateRange;
                    } else {
                        // 수집할 날짜가 없으면 어제 하루만
                        const dateRange = {
                            startDate: this.formatDateForCollection(yesterday),
                            endDate: this.formatDateForCollection(yesterday)
                        };
                        console.log('어제 하루만 수집:', dateRange);
                        return dateRange;
                    }
                } else {
                    // 기존 데이터가 없으면 전체 30일
                    const fullRange = this.getFullDateRange();
                    console.log('기존 데이터 없음, 전체 30일 수집:', fullRange);
                    return fullRange;
                }
            } catch (error) {
                console.error('정산현황 날짜 범위 계산 오류:', error);
                // 오류 시 전체 30일
                return this.getFullDateRange();
            }
        },

        // 전체 30일 날짜 범위 반환 (growthScm과 동일)
        getFullDateRange() {
            const todayStr = this.getTodayString();
            const dates = this.generateDatesFromUpdateDate(todayStr);

            console.log('=== 전체 30일 범위 계산 ===');
            console.log('어제 기준 30일 범위:', `${dates[0]} ~ ${dates[dates.length - 1]}`);

            return {
                startDate: dates[0],
                endDate: dates[dates.length - 1]
            };
        },

        // growthScm과 동일한 날짜 계산 함수들
        getTodayString() {
            const today = new Date();
            const kstOffset = 9 * 60; // 한국은 UTC+9
            const kstDate = new Date(today.getTime() + kstOffset * 60 * 1000);
            return kstDate.toISOString().split('T')[0];
        },

        // growthScm과 동일한 날짜 생성 함수
        generateDatesFromUpdateDate(updateDate) {
            if (!updateDate) return [];
            const baseDate = new Date(updateDate);
            // 어제를 기준으로 설정 (오늘 데이터 제외)
            baseDate.setDate(baseDate.getDate() - 1); // 하루 빼서 어제로 설정
            const dates = [];
            // 어제부터 30일 전까지 생성 (오름차순 정렬)
            for (let i = 29; i >= 0; i--) {
                const d = new Date(baseDate);
                d.setDate(baseDate.getDate() - i);
                dates.push(d.toISOString().split('T')[0]);
            }

            console.log(`generateDatesFromUpdateDate: updateDate=${updateDate}, 어제=${baseDate.toISOString().split('T')[0]}, 생성된 날짜 범위=${dates[0]} ~ ${dates[dates.length-1]}`);
            return dates;
        },

        // 날짜 포맷팅 함수 (growthScm과 동일)
        formatDateForCollection(date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            return `${year}-${month}-${day}`;
        },

        // 숫자 포맷팅
        formatNumber(num) {
            if (!num && num !== 0) return '0';
            return Number(num).toLocaleString();
        },

        // 판매가 = 순매출(쿠폰 제외) ÷ 판매량
        calcSalePrice(item) {
            const qty = item.saleQuantity || 0;
            return qty > 0 ? Math.round((item.netSales || 0) / qty) : 0;
        },

        // 엑셀 다운로드 - 일별 테이블
        downloadDailyTableExcel() {
            try {
                console.log('일별 테이블 엑셀 다운로드 시작');

                if (!this.salesLedgerData || this.salesLedgerData.length === 0) {
                    alert('다운로드할 데이터가 없습니다.');
                    return;
                }

                // 엑셀용 데이터 변환
                const excelData = this.salesLedgerData.map(item => {
                    const row = {};
                    this.dailyHeaders.forEach(header => {
                        const key = header.value;
                        let value = item[key];

                        // 숫자 데이터는 원본 값으로 변환
                        if (typeof value === 'number') {
                            row[header.text] = value;
                        } else if (typeof value === 'string' && !isNaN(value.replace(/,/g, ''))) {
                            row[header.text] = parseFloat(value.replace(/,/g, '')) || 0;
                        } else {
                            row[header.text] = value || '';
                        }
                    });
                    return row;
                });

                // 워크북 생성
                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(excelData);

                // 워크시트를 워크북에 추가
                XLSX.utils.book_append_sheet(wb, ws, '일별 판매장부');

                // 파일명 생성
                const filename = `판매장부_일별_${this.startDate}_${this.endDate}.xlsx`;

                // 파일 다운로드
                XLSX.writeFile(wb, filename);

                console.log('일별 테이블 엑셀 다운로드 완료:', filename);
            } catch (error) {
                console.error('일별 테이블 엑셀 다운로드 오류:', error);
                alert('엑셀 다운로드 중 오류가 발생했습니다.');
            }
        },

        // 엑셀 다운로드 - 상품별 테이블
        downloadProductTableExcel() {
            try {
                console.log('상품별 테이블 엑셀 다운로드 시작');

                if (!this.productViewData || this.productViewData.length === 0) {
                    alert('다운로드할 데이터가 없습니다.');
                    return;
                }

                // 엑셀용 데이터 변환
                const excelData = this.productViewData.map(item => {
                    const row = {};
                    this.productHeaders.forEach(header => {
                        const key = header.value;
                        let value = item[key];

                        // 특별 처리가 필요한 컬럼들
                        if (key === '썸네일') {
                            row[header.text] = item.썸네일 || '';
                        } else if (key === '판매가') {
                            row[header.text] = this.calcSalePrice(item);
                        } else if (key === '상품명') {
                            row['상품명'] = item.판매상품명 || '';
                            row['옵션명'] = item.옵션명 || '';
                        } else if (typeof value === 'number') {
                            row[header.text] = value;
                        } else if (typeof value === 'string' && !isNaN(value.replace(/,/g, ''))) {
                            row[header.text] = parseFloat(value.replace(/,/g, '')) || 0;
                        } else {
                            row[header.text] = value || '';
                        }
                    });
                    return row;
                });

                // 워크북 생성
                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(excelData);

                // 워크시트를 워크북에 추가
                XLSX.utils.book_append_sheet(wb, ws, '상품별 판매장부');

                // 파일명 생성
                const filename = `판매장부_상품별_${this.startDate}_${this.endDate}.xlsx`;

                // 파일 다운로드
                XLSX.writeFile(wb, filename);

                console.log('상품별 테이블 엑셀 다운로드 완료:', filename);
            } catch (error) {
                console.error('상품별 테이블 엑셀 다운로드 오류:', error);
                alert('엑셀 다운로드 중 오류가 발생했습니다.');
            }
        },

        // 비용관리 모달 열기 — date 가 있으면 그 달·그 날로(일별 «비용 입력» · 판 #452) · 빈 값 = 요약장부(옛 동작)
        // 같은 날짜로 다시 열어도 맞추려고 한 번 비웠다가 준다(LedgerCostManager 는 값이 바뀔 때만 맞춘다)
        openCostsFor(date) {
            this.costInitialDate = '';
            this.$nextTick(() => {
                this.costInitialDate = date || '';
                this.showCostManager = true;
            });
        },

        // 엑셀 다운로드 - 전체 데이터 (날짜별 × 상품별, 한 테이블)
        downloadFullTableExcel() {
            try {
                console.log('전체 데이터 엑셀 다운로드 시작');

                if (!this.salesLedgerData || this.salesLedgerData.length === 0) {
                    alert('다운로드할 데이터가 없습니다.');
                    return;
                }

                // 날짜 × 상품 단위로 평탄화 (날짜별 상품 상세와 동일한 행 구성 + 행단위 비율 계산)
                const flatRows = [];
                this.salesLedgerData.forEach(day => {
                    (day.products || []).forEach(product => {
                        const productNameParts = product.상품명 ? product.상품명.split(', ') : ['', ''];
                        const 판매상품명 = productNameParts[0] || '';
                        const 옵션명 = productNameParts[1] || '';

                        // 개당이익(광고제외) = (총이익 + 광고비 − 재고손실보상) ÷ 판매수량, 광고매출 단가
                        const profitPerUnit = product.saleQuantity > 0 ? ledgerProfitPerUnit(product.grossProfit || 0, product.adTotal, product.inventoryCompensation, product.saleQuantity) : 0;
                        const unitPrice = product.saleQuantity > 0 ? (product.saleAmount || 0) / product.saleQuantity : 0;

                        // 행단위 비율 (상품별 집계와 동일 공식)
                        const grossMargin = product.netSales > 0 ? (product.grossProfit / product.netSales) * 100 : null;
                        const roi = product.cogs > 0 ? (product.grossProfit / product.cogs) * 100 : null;
                        let roas = null;
                        let adROI = null;
                        if (product.saleQuantityFromAd > 0 && product.adTotal > 0 && product.saleQuantity > 0) {
                            roas = (product.saleQuantityFromAd * unitPrice) / product.adTotal;
                            adROI = (profitPerUnit * product.saleQuantityFromAd) / product.adTotal;
                        }

                        const coverage = this.getCoverage(product.vendorItemId);
                        flatRows.push({
                            ...product,
                            date: day.date,
                            판매상품명: 판매상품명,
                            옵션명: 옵션명,
                            판매가: this.calcSalePrice(product),
                            coupangStock: coverage.coupangStock,
                            purchasePrice: coverage.purchasePrice,
                            profitPerUnit: Math.round(profitPerUnit),
                            vat: Math.round(product.vat || 0),
                            grossProfit: Math.round(product.grossProfit || 0),
                            cogs: Math.round(product.cogs || 0),
                            grossMargin: grossMargin !== null ? Math.round(grossMargin * 10) / 10 : '',
                            roi: roi !== null ? Math.round(roi * 10) / 10 : '',
                            roas: roas !== null ? Math.round(roas * 100) / 100 : '',
                            adROI: adROI !== null ? Math.round(adROI * 100) / 100 : '',
                            adTotalProfit: Math.round((product.saleQuantityFromAd || 0) * unitPrice), // 광고매출
                            adNetProfit: Math.round(((product.saleQuantityFromAd || 0) * profitPerUnit) - (product.adTotal || 0)) // 광고순수익
                        });
                    });
                });

                if (flatRows.length === 0) {
                    alert('다운로드할 데이터가 없습니다.');
                    return;
                }

                // 날짜 내림차순 → 같은 날짜 내 매출 내림차순
                flatRows.sort((a, b) => b.date.localeCompare(a.date) || (b.saleAmount - a.saleAmount));

                // 컬럼 = 날짜 + 상품별 헤더 (썸네일/메모 제외)
                const columns = this.productHeaders.filter(h => !['썸네일', 'memo'].includes(h.value));

                const excelData = flatRows.map(item => {
                    const row = { 날짜: item.date };
                    columns.forEach(header => {
                        const key = header.value;
                        const value = item[key];

                        if (key === '판매가') {
                            row[header.text] = item.판매가;
                        } else if (key === '상품명') {
                            row['상품명'] = item.판매상품명 || '';
                            row['옵션명'] = item.옵션명 || '';
                        } else if (typeof value === 'number') {
                            row[header.text] = value;
                        } else if (typeof value === 'string' && value !== '' && !isNaN(value.replace(/,/g, ''))) {
                            row[header.text] = parseFloat(value.replace(/,/g, '')) || 0;
                        } else {
                            row[header.text] = value || '';
                        }
                    });
                    return row;
                });

                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(excelData);
                XLSX.utils.book_append_sheet(wb, ws, '전체 판매장부');

                const filename = `판매장부_전체_${this.startDate}_${this.endDate}.xlsx`;
                XLSX.writeFile(wb, filename);

                console.log('전체 데이터 엑셀 다운로드 완료:', filename, flatRows.length, '행');
            } catch (error) {
                console.error('전체 데이터 엑셀 다운로드 오류:', error);
                alert('엑셀 다운로드 중 오류가 발생했습니다.');
            }
        },

        // 엑셀 다운로드 - 모달 테이블
        downloadModalTableExcel() {
            try {
                console.log('모달 테이블 엑셀 다운로드 시작');

                if (!this.modalData || this.modalData.length === 0) {
                    alert('다운로드할 데이터가 없습니다.');
                    return;
                }

                // 엑셀용 데이터 변환
                const excelData = this.modalData.map(item => {
                    const row = {};
                    this.modalHeaders.forEach(header => {
                        const key = header.value;
                        let value = item[key];

                        // 특별 처리가 필요한 컬럼들
                        if (key === '썸네일') {
                            row[header.text] = item.썸네일 || '';
                        } else if (key === '판매가') {
                            row[header.text] = this.calcSalePrice(item);
                        } else if (key === '상품명') {
                            row['상품명'] = item.판매상품명 || '';
                            row['옵션명'] = item.옵션명 || '';
                        } else if (typeof value === 'number') {
                            row[header.text] = value;
                        } else if (typeof value === 'string' && !isNaN(value.replace(/,/g, ''))) {
                            row[header.text] = parseFloat(value.replace(/,/g, '')) || 0;
                        } else {
                            row[header.text] = value || '';
                        }
                    });
                    return row;
                });

                // 워크북 생성
                const wb = XLSX.utils.book_new();
                const ws = XLSX.utils.json_to_sheet(excelData);

                // 파일명 생성 (모달 타입에 따라 구분)
                let filename;
                let sheetName;

                if (this.isProductView) {
                    // 상품별 상세 모달
                    const productName = this.modalData[0]?.판매상품명 || '상품상세';
                    const safeProductName = productName.replace(/[<>:"/\\|?*]/g, '_').substring(0, 50);
                    filename = `판매장부_상품별상세_${safeProductName}_${this.startDate}_${this.endDate}.xlsx`;
                    sheetName = '상품별 상세';
                } else {
                    // 일별 상세 모달
                    const selectedDate = this.modalTitle.includes('상세') ? this.modalTitle.split(' ')[0] : this.startDate;
                    filename = `판매장부_일별상세_${selectedDate}.xlsx`;
                    sheetName = '일별 상세';
                }

                // 워크시트를 워크북에 추가
                XLSX.utils.book_append_sheet(wb, ws, sheetName);

                // 파일 다운로드
                XLSX.writeFile(wb, filename);

                console.log('모달 테이블 엑셀 다운로드 완료:', filename);
            } catch (error) {
                console.error('모달 테이블 엑셀 다운로드 오류:', error);
                alert('엑셀 다운로드 중 오류가 발생했습니다.');
            }
        },

        // [삭제됨] saveSettlementData - Extension에서 직접 서버 저장으로 통합됨 (background.js saveSettlementData 사용)

        // 광고 데이터 저장 (일별 광고데이터 API 사용) - 일별로 청크를 나누어 저장
        async saveAdData(adData) {
            try {
                console.log('광고 데이터 저장 시작:', Object.keys(adData).length, '개 vendorItemId');

                // 새로운 adData 구조를 일별 광고데이터 형태로 변환
                const dailyAdData = this.convertToDailyAdData(adData);
                console.log('일별 광고데이터 변환 완료:', dailyAdData.length);

                // 일별로 그룹화
                const dailyGroups = this.groupAdDataByDate(dailyAdData);
                const dates = Object.keys(dailyGroups);
                const totalDates = dates.length;

                for (let i = 0; i < totalDates; i++) {
                    const date = dates[i];
                    const dateData = dailyGroups[date];

                    this.loadingtext = `광고 데이터 저장 중... (${date}: ${i + 1}/${totalDates})`;

                    const response = await axios.post('/api/saveDailyAdData', dateData);

                    if (response.data.success) {
                        console.log(`광고 데이터 ${date} 저장 완료:`, response.data.message);
                    } else {
                        console.error(`광고 데이터 ${date} 저장 실패:`, response.data.message);
                        throw new Error(response.data.message);
                    }
                }

                console.log('광고 데이터 전체 저장 완료');
            } catch (error) {
                console.error('일별 광고데이터 저장 오류:', error);
                throw error;
            }
        },

        // 새로운 adData 구조를 일별 광고데이터로 변환
        convertToDailyAdData(adData) {
            const dailyAdData = [];

            // adData는 vendorItemId를 키로 하는 객체 구조
            Object.keys(adData).forEach(vendorItemId => {
                const vendorData = adData[vendorItemId];

                // 각 날짜별 데이터 처리
                Object.keys(vendorData).forEach(date => {
                    const dateData = vendorData[date];

                    // 각 광고 영역별 데이터 처리
                    Object.keys(dateData).forEach(adType => {
                        const adTypeData = dateData[adType];

                        // 광고 영역명 정규화 (필요시)
                        let normalizedAdType = adType;
                        if (adType.includes('리타게팅')) {
                            normalizedAdType = '리타게팅';
                        }

                        dailyAdData.push({
                            vendorItemId: vendorItemId,
                            date: date,
                            adType: normalizedAdType,
                            cost: adTypeData.cost || 0,
                            clicks: adTypeData.clicks || 0,
                            impressions: adTypeData.impressions || 0,
                            cpc: adTypeData.cpc || 0,
                            detail: adTypeData.detail || [] // vendorItemId별 전환 데이터
                        });
                    });
                });
            });

            return dailyAdData;
        },

        // 광고 데이터를 일별로 그룹화
        groupAdDataByDate(dailyAdData) {
            const groups = {};

            dailyAdData.forEach(item => {
                if (!groups[item.date]) {
                    groups[item.date] = [];
                }
                groups[item.date].push(item);
            });

            return groups;
        },

        // 상품등급 뱃지 variant 반환
        getSkuGradeBadgeVariant(skuGrade) {
            if (skuGrade === 'NEW') {
                return 'success';
            } else if (skuGrade === 'PACKAGE_DAMAGED' || skuGrade === 'RETURN_BEST' || skuGrade === 'RETURN_GOOD' || skuGrade === 'RETURN_NORMAL') {
                return 'secondary';
            } else {
                return 'info'; // 번들 상품용
            }
        },

        // 상품등급 라벨 반환
        getSkuGradeLabel(skuGrade) {
            if (skuGrade === 'NEW') {
                return '새제품';
            } else if (skuGrade === 'PACKAGE_DAMAGED') {
                return '반품-미개봉';
            } else if (skuGrade === 'RETURN_BEST') {
                return '반품-최상';
            } else if (skuGrade === 'RETURN_GOOD') {
                return '반품-상';
            } else if (skuGrade === 'RETURN_NORMAL') {
                return '반품-중';
            } else if (skuGrade && skuGrade.startsWith('번들')) {
                return skuGrade; // 번들x2 등
            } else {
                return skuGrade || 'NEW';
            }
        }
    }
}
</script>

<style scoped>
.card {
    border-radius: 10px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.1);
}

/* ===== 상품 진단 TOP 10 ===== */
.diag-sort .btn {
    font-size: 11.5px;
    padding: 2px 8px;
}

/* 카드 높이는 옆 칸(워터폴)이 정한다. 리스트는 헤더를 뺀 나머지를 채우고,
   행은 그 공간을 나눠 갖되 max-height 로 묶어 상품이 10개보다 적을 때
   한 행이 세로로 늘어지지 않게 한다.
   min-height:0 이 없으면 flex 아이템이 내용 높이 밑으로 안 줄어들어 카드가 삐져나간다 */
.diag-list {
    display: flex;
    flex-direction: column;
    min-height: 0;
}

.diag-row {
    display: grid;
    grid-template-columns: 18px minmax(0, 1.5fr) minmax(0, 1fr) minmax(0, 1fr) 78px;
    gap: 10px;
    align-items: center;
    padding: 4px 2px;
    border-bottom: 1px solid #f1f1f1;
    font-size: 13px;
    flex: 1 1 0;
    min-height: 0;
    max-height: 52px;
}

/* 폰(md 아래)은 col-md-6 이 쌓여 옆 칸이 카드 높이를 안 정한다 — 바탕 0 이면 행이 눌려 겹치고 카드 밖으로 나감(390 사진 10-30) */
@media (max-width: 767.98px) {
    .diag-row {
        flex: 0 0 auto;
        max-height: none;
    }
    /* 폰은 칸이 좁아 «3,512만 · 84일» 이 «3,512만 · 8…» 로 잘린다 — 행이 제 높이라 줄바꿈해도 들쭉날쭉 문제 0
       이 칸이 밑의 기본 .diag-row__val(nowrap) 보다 앞이라 선택자를 한 단계 무겁게 */
    .diag-row .diag-row__val {
        white-space: normal;
        word-break: keep-all;
    }
}

.diag-row--head {
    flex: 0 0 auto;
    font-size: 11px;
    color: #898781;
    letter-spacing: 0.04em;
    border-bottom: 1px solid #dee2e6;
    padding-bottom: 5px;
}

.diag-row__rank {
    color: #898781;
    font-size: 11.5px;
    font-variant-numeric: tabular-nums;
}

.diag-row__name {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}

.diag-row__metric {
    display: flex;
    flex-direction: column;
    gap: 3px;
    min-width: 0;
}

.diag-row__bar {
    height: 7px;
    border-radius: 3px;
    background: #eceae3;
    overflow: hidden;
}

.diag-row__bar > i {
    display: block;
    height: 100%;
    border-radius: 3px;
}

/* 반칸(col-md-6)이라 "1,028만 · 96일"이 두 줄로 접히면 행 높이가 들쭉날쭉해진다 */
.diag-row__val {
    font-size: 11.5px;
    color: #52514e;
    font-variant-numeric: tabular-nums;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.diag-row__roi {
    text-align: right;
    font-weight: 700;
    font-size: 12.5px;
    line-height: 1.25;
    padding: 2px 6px;
    border-radius: 5px;
    font-variant-numeric: tabular-nums;
}

.diag-row__roi em {
    display: block;
    font-style: normal;
    font-weight: 500;
    font-size: 10px;
    opacity: 0.78;
}

/* 파랑↔빨강 발산형. 신호등(빨강/초록)은 적록색맹에서 두 색이 거의 같게 보인다 */
.diag-row__roi.is-good { color: #2a78d6; background: rgba(42, 120, 214, 0.08); }
.diag-row__roi.is-mid  { color: #52514e; background: transparent; }
.diag-row__roi.is-bad  { color: #d03b3b; background: rgba(208, 59, 59, 0.08); }

/* 좁은 화면(390)에선 칸 넓이가 모자라 막대·값이 잘린다 — 순위 칸을 줄이고 간격을 좁힌다 */
@media (max-width: 575.98px) {
    .diag-row {
        grid-template-columns: 14px minmax(0, 1.2fr) minmax(0, 1fr) minmax(0, 1fr) 64px;
        gap: 6px;
        font-size: 12px;
    }
}

/* 매입가 미입력 경고 — 두 탭 모두에서 눈에 띄게 */
.missing-price-alert {
    background: #fff8ec;
    border: 1px solid #f0d9b5;
    border-left: 4px solid #e67e22;
    border-radius: 6px;
    padding: 12px 16px;
    margin-bottom: 14px;
}
.missing-price-alert > i {
    font-size: 20px;
    color: #e67e22;
}
.missing-price-alert__title {
    font-weight: 700;
    color: #b35309;
    font-size: 14px;
}
.missing-price-alert__desc {
    font-size: 12px;
    color: #7f8c9a;
    margin-top: 2px;
}

/* 원가·이익 잠금 모달 */
.lock-modal__desc {
    font-size: 13px;
    color: #5b6b7c;
    line-height: 1.6;
    margin-bottom: 10px;
}
.lock-modal__error {
    font-size: 12px;
    color: #d9534f;
    margin-top: 6px;
}
.lock-modal__hint {
    font-size: 11px;
    color: #9aa7b4;
    margin-top: 8px;
    line-height: 1.5;
}

/* 제목 줄에 얹은 탭 — 카드 안이라 아래 테두리는 지운다 */
.sales-ledger-tabs {
    border-bottom: none;
    margin-bottom: 0;
    flex-wrap: nowrap;
}
.sales-ledger-tabs .nav-link {
    padding: 6px 16px;
    color: #7f8c9a;
    font-weight: 600;
    border: 1px solid transparent;
    border-radius: 6px;
}
.sales-ledger-tabs .nav-link:hover {
    background: #f2f5f8;
}
.sales-ledger-tabs .nav-link.active {
    background: #eaf2fb;
    color: #2b6cb0;
    border-color: #d3e3f5;
}
.sales-ledger-tabs .nav-link.disabled {
    opacity: 0.45;
    cursor: default;
    background: transparent;
}

.table th {
    background-color: #f8f9fa;
    border-top: none;
    font-weight: 600;
    font-size: 0.9rem;
}

.table td {
    vertical-align: middle;
    font-size: 0.9rem;
}

/* 상품명 멀티라인 표시 */
.product-name-multiline {
    line-height: 1.2;
    word-break: break-word;
    overflow-wrap: break-word;
}

/* 옵션명 래핑 */
.option-name-wrap {
    line-height: 1.2;
    word-break: break-word;
    overflow-wrap: break-word;
}

/* 번들 묶음수 배지 */
.bundle-badge {
    flex-shrink: 0;
    font-size: 11px;
    font-weight: 700;
    color: #fff;
    background-color: #f76707;
    padding: 1px 6px;
    border-radius: 4px;
    line-height: 1.4;
}

/* 아바타 이미지 */
.avatar-40 {
    width: 40px;
    height: 40px;
    object-fit: cover;
}

.avatar-30 {
    width: 30px;
    height: 30px;
    object-fit: cover;
}

.nested-table {
    background-color: #f8f9fa;
    border-radius: 8px;
}

.nested-table .table {
    margin-bottom: 0;
    font-size: 0.9em;
}

/* 모달 차트 컨테이너 스타일 */
.modal-chart-container {
    width: 100% !important;
    padding-left: 0 !important;
    padding-right: 0 !important;
}

.modal-chart-container .apexcharts-canvas {
    width: 100% !important;
}

.modal-chart-container .apexcharts-inner {
    width: 100% !important;
}

.modal-chart-container svg {
    width: 100% !important;
}

/* 판매장부 모달 크기 고정 (growthScm 방식 참고) */
::v-deep .sales-ledger-modal .modal-dialog {
    max-width: 98vw !important;
    width: 98vw !important;
    height: 96vh !important;
    max-height: 96vh !important;
    margin: 1vh auto !important;
}

::v-deep .sales-ledger-modal .modal-content {
    height: 100% !important;
    display: flex !important;
    flex-direction: column !important;
    border: none !important;
    border-radius: 0 !important;
}

::v-deep .sales-ledger-modal .modal-header {
    padding: 0.5rem 1rem !important;
    border-bottom: 1px solid #dee2e6 !important;
    flex-shrink: 0 !important;
}

::v-deep .sales-ledger-modal .modal-body {
    flex: 1 !important;
    overflow-y: auto !important;
    padding: 0.5rem !important;
    max-height: none !important;
}

::v-deep .sales-ledger-modal .modal-footer {
    padding: 0.5rem 1rem !important;
    border-top: 1px solid #dee2e6 !important;
    flex-shrink: 0 !important;
}

/* 수익(+) 컬럼 배경색 - 연한 녹색 */
.bg-revenue {
    background-color: rgba(40, 167, 69, 0.08) !important;
}

/* 비용(-) 컬럼 배경색 - 연한 빨간색 */
.bg-cost {
    background-color: rgba(220, 53, 69, 0.08) !important;
}
</style>
